import guitar, { makeRule, block } from 'guitar';
import get from 'lodash/get';
import styled, { css, keyframes, useTheme, ThemeProvider } from 'styled-components';
import { Link as Link$1, useLocation } from 'react-router-dom';
import * as React from 'react';
import React__default, { createContext, useRef, useCallback, useState, useLayoutEffect, useContext, useMemo, useEffect, useInsertionEffect } from 'react';
import { createPortal } from 'react-dom';

const shadedColors = ['primary', 'secondary', 'grey', 'success', 'info', 'alert', 'error'];
const simpleColors = ['white', 'black', 'clear', 'star', 'facebookBlue', 'background'];
function isColorIndicator(str) {
    if (str.startsWith('!')) {
        str = str.substring(1);
    }
    const [name, opacity] = str.split('@', 2);
    if (opacity) {
        const isCorrect = opacity.toLowerCase() === 'medium' || opacity.toLowerCase() === 'low' || !isNaN(Number(opacity));
        if (!isCorrect) {
            return false;
        }
    }
    for (let i = 0; i <= simpleColors.length; i++) {
        if (name === simpleColors[i]) {
            return true;
        }
    }
    for (let i = 0; i <= shadedColors.length; i++) {
        if (name === shadedColors[i]) {
            return true;
        }
        if (name === `${shadedColors[i]}.light`) {
            return true;
        }
        if (name === `${shadedColors[i]}.lighter`) {
            return true;
        }
        if (name === `${shadedColors[i]}.dark`) {
            return true;
        }
        if (name === `${shadedColors[i]}.darker`) {
            return true;
        }
    }
    return false;
}
function isShadedColorName(name) {
    return shadedColors.includes(name);
}
class Color {
    constructor(kind, color, shades) {
        this.kind = kind;
        this.value = color;
        this.shades = shades;
    }
    static define(name, value, shades) {
        if (isShadedColorName(name)) {
            if (!shades) {
                throw new Error(`Must define shades for shaded color: ${name}`);
            }
            const shadeMap = new Map();
            Object.entries(shades).forEach(([k, v]) => {
                shadeMap.set(k, v);
            });
            return new Color('shaded', value, shadeMap);
        }
        else {
            return new Color('simple', value);
        }
    }
    mapShades(fn) {
        if (this.shades) {
            return new Map(Array.from(this.shades.entries()).map(([k, v]) => [k, fn(v)]));
        }
    }
    toString(s) {
        if (s && this.shades) {
            return this.shades.get(s);
        }
        return this.value;
    }
}

const NoConversion = color => color;
function lookupOnParent(parent, key, options) {
    if (!parent) {
        return;
    }
    let value = get(parent, key);
    const colorConverter = options.skipColorConverter ? NoConversion : get(parent, 'colorConverter', NoConversion);
    if (!value) {
        const nextParent = get(parent, 'parent');
        if (nextParent) {
            value = lookupOnParent(nextParent, key, options);
        }
    }
    if (value instanceof Color) {
        return colorConverter(value);
    }
    else {
        return value;
    }
}
// Look up a property from a theme
function themeLookup(theme, key, options = {}) {
    return lookupOnParent(theme.RoadieUI, key, options);
}

const checkTheme = (str) => {
    return (props) => {
        return get(props.theme, str) || themeLookup(props.theme, str) || str;
    };
};

const TABLET_MAX = 900;
const MOBILE_MAX = 600;
const sizes = {
    desktop: { min: TABLET_MAX + 1, max: Number.MAX_SAFE_INTEGER },
    tablet: { min: MOBILE_MAX + 1, max: TABLET_MAX },
    mobile: { min: 0, max: MOBILE_MAX },
};
const em = (n) => `${n / 16}em`;
const rule = makeRule({
    lookupParser: checkTheme,
    media: {
        desktop: `(min-width: ${em(sizes.desktop.min)})`,
        tablet: `(min-width: ${em(sizes.tablet.min)}) AND (max-width: ${em(sizes.tablet.max)})`,
        mobile: `(max-width: ${em(sizes.mobile.max)})`,
    },
});

function _extends$Z() {
  _extends$Z = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends$Z.apply(this, arguments);
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  _setPrototypeOf(subClass, superClass);
}

function _getPrototypeOf(o) {
  _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) {
    return o.__proto__ || Object.getPrototypeOf(o);
  };
  return _getPrototypeOf(o);
}

function _isNativeFunction(fn) {
  return Function.toString.call(fn).indexOf("[native code]") !== -1;
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _wrapNativeSuper(Class) {
  var _cache = typeof Map === "function" ? new Map() : undefined;

  _wrapNativeSuper = function _wrapNativeSuper(Class) {
    if (Class === null || !_isNativeFunction(Class)) return Class;

    if (typeof Class !== "function") {
      throw new TypeError("Super expression must either be null or a function");
    }

    if (typeof _cache !== "undefined") {
      if (_cache.has(Class)) return _cache.get(Class);

      _cache.set(Class, Wrapper);
    }

    function Wrapper() {
      return _construct(Class, arguments, _getPrototypeOf(this).constructor);
    }

    Wrapper.prototype = Object.create(Class.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    });
    return _setPrototypeOf(Wrapper, Class);
  };

  return _wrapNativeSuper(Class);
}

/**
 * Create an error file out of errors.md for development and a simple web link to the full errors
 * in production mode.
 * @private
 */


var PolishedError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(PolishedError, _Error);

  function PolishedError(code) {
    var _this;

    {
      _this = _Error.call(this, "An error occurred. See https://github.com/styled-components/polished/blob/main/src/internalHelpers/errors.md#" + code + " for more information.") || this;
    }

    return _assertThisInitialized(_this);
  }

  return PolishedError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function colorToInt(color) {
  return Math.round(color * 255);
}

function convertToInt(red, green, blue) {
  return colorToInt(red) + "," + colorToInt(green) + "," + colorToInt(blue);
}

function hslToRgb(hue, saturation, lightness, convert) {
  if (convert === void 0) {
    convert = convertToInt;
  }

  if (saturation === 0) {
    // achromatic
    return convert(lightness, lightness, lightness);
  } // formulae from https://en.wikipedia.org/wiki/HSL_and_HSV


  var huePrime = (hue % 360 + 360) % 360 / 60;
  var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  var secondComponent = chroma * (1 - Math.abs(huePrime % 2 - 1));
  var red = 0;
  var green = 0;
  var blue = 0;

  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = secondComponent;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = secondComponent;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = secondComponent;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = secondComponent;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = secondComponent;
    blue = chroma;
  } else if (huePrime >= 5 && huePrime < 6) {
    red = chroma;
    blue = secondComponent;
  }

  var lightnessModification = lightness - chroma / 2;
  var finalRed = red + lightnessModification;
  var finalGreen = green + lightnessModification;
  var finalBlue = blue + lightnessModification;
  return convert(finalRed, finalGreen, finalBlue);
}

var namedColorMap = {
  aliceblue: 'f0f8ff',
  antiquewhite: 'faebd7',
  aqua: '00ffff',
  aquamarine: '7fffd4',
  azure: 'f0ffff',
  beige: 'f5f5dc',
  bisque: 'ffe4c4',
  black: '000',
  blanchedalmond: 'ffebcd',
  blue: '0000ff',
  blueviolet: '8a2be2',
  brown: 'a52a2a',
  burlywood: 'deb887',
  cadetblue: '5f9ea0',
  chartreuse: '7fff00',
  chocolate: 'd2691e',
  coral: 'ff7f50',
  cornflowerblue: '6495ed',
  cornsilk: 'fff8dc',
  crimson: 'dc143c',
  cyan: '00ffff',
  darkblue: '00008b',
  darkcyan: '008b8b',
  darkgoldenrod: 'b8860b',
  darkgray: 'a9a9a9',
  darkgreen: '006400',
  darkgrey: 'a9a9a9',
  darkkhaki: 'bdb76b',
  darkmagenta: '8b008b',
  darkolivegreen: '556b2f',
  darkorange: 'ff8c00',
  darkorchid: '9932cc',
  darkred: '8b0000',
  darksalmon: 'e9967a',
  darkseagreen: '8fbc8f',
  darkslateblue: '483d8b',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  darkturquoise: '00ced1',
  darkviolet: '9400d3',
  deeppink: 'ff1493',
  deepskyblue: '00bfff',
  dimgray: '696969',
  dimgrey: '696969',
  dodgerblue: '1e90ff',
  firebrick: 'b22222',
  floralwhite: 'fffaf0',
  forestgreen: '228b22',
  fuchsia: 'ff00ff',
  gainsboro: 'dcdcdc',
  ghostwhite: 'f8f8ff',
  gold: 'ffd700',
  goldenrod: 'daa520',
  gray: '808080',
  green: '008000',
  greenyellow: 'adff2f',
  grey: '808080',
  honeydew: 'f0fff0',
  hotpink: 'ff69b4',
  indianred: 'cd5c5c',
  indigo: '4b0082',
  ivory: 'fffff0',
  khaki: 'f0e68c',
  lavender: 'e6e6fa',
  lavenderblush: 'fff0f5',
  lawngreen: '7cfc00',
  lemonchiffon: 'fffacd',
  lightblue: 'add8e6',
  lightcoral: 'f08080',
  lightcyan: 'e0ffff',
  lightgoldenrodyellow: 'fafad2',
  lightgray: 'd3d3d3',
  lightgreen: '90ee90',
  lightgrey: 'd3d3d3',
  lightpink: 'ffb6c1',
  lightsalmon: 'ffa07a',
  lightseagreen: '20b2aa',
  lightskyblue: '87cefa',
  lightslategray: '789',
  lightslategrey: '789',
  lightsteelblue: 'b0c4de',
  lightyellow: 'ffffe0',
  lime: '0f0',
  limegreen: '32cd32',
  linen: 'faf0e6',
  magenta: 'f0f',
  maroon: '800000',
  mediumaquamarine: '66cdaa',
  mediumblue: '0000cd',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  mediumseagreen: '3cb371',
  mediumslateblue: '7b68ee',
  mediumspringgreen: '00fa9a',
  mediumturquoise: '48d1cc',
  mediumvioletred: 'c71585',
  midnightblue: '191970',
  mintcream: 'f5fffa',
  mistyrose: 'ffe4e1',
  moccasin: 'ffe4b5',
  navajowhite: 'ffdead',
  navy: '000080',
  oldlace: 'fdf5e6',
  olive: '808000',
  olivedrab: '6b8e23',
  orange: 'ffa500',
  orangered: 'ff4500',
  orchid: 'da70d6',
  palegoldenrod: 'eee8aa',
  palegreen: '98fb98',
  paleturquoise: 'afeeee',
  palevioletred: 'db7093',
  papayawhip: 'ffefd5',
  peachpuff: 'ffdab9',
  peru: 'cd853f',
  pink: 'ffc0cb',
  plum: 'dda0dd',
  powderblue: 'b0e0e6',
  purple: '800080',
  rebeccapurple: '639',
  red: 'f00',
  rosybrown: 'bc8f8f',
  royalblue: '4169e1',
  saddlebrown: '8b4513',
  salmon: 'fa8072',
  sandybrown: 'f4a460',
  seagreen: '2e8b57',
  seashell: 'fff5ee',
  sienna: 'a0522d',
  silver: 'c0c0c0',
  skyblue: '87ceeb',
  slateblue: '6a5acd',
  slategray: '708090',
  slategrey: '708090',
  snow: 'fffafa',
  springgreen: '00ff7f',
  steelblue: '4682b4',
  tan: 'd2b48c',
  teal: '008080',
  thistle: 'd8bfd8',
  tomato: 'ff6347',
  turquoise: '40e0d0',
  violet: 'ee82ee',
  wheat: 'f5deb3',
  white: 'fff',
  whitesmoke: 'f5f5f5',
  yellow: 'ff0',
  yellowgreen: '9acd32'
};
/**
 * Checks if a string is a CSS named color and returns its equivalent hex value, otherwise returns the original color.
 * @private
 */

function nameToHex(color) {
  if (typeof color !== 'string') return color;
  var normalizedColorName = color.toLowerCase();
  return namedColorMap[normalizedColorName] ? "#" + namedColorMap[normalizedColorName] : color;
}

var hexRegex = /^#[a-fA-F0-9]{6}$/;
var hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
var reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
var reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
var rgbRegex = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i;
var rgbaRegex = /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
var hslRegex = /^hsl\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
var hslaRegex = /^hsla\(\s*(\d{0,3}[.]?[0-9]+)\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*(\d{1,3}[.]?[0-9]?)%\s*,\s*([-+]?[0-9]*[.]?[0-9]+)\s*\)$/i;
/**
 * Returns an RgbColor or RgbaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a RgbColor or RgbaColor object back to a string.
 *
 * @example
 * // Assigns `{ red: 255, green: 0, blue: 0 }` to color1
 * const color1 = parseToRgb('rgb(255, 0, 0)');
 * // Assigns `{ red: 92, green: 102, blue: 112, alpha: 0.75 }` to color2
 * const color2 = parseToRgb('hsla(210, 10%, 40%, 0.75)');
 */

function parseToRgb(color) {
  if (typeof color !== 'string') {
    throw new PolishedError(3);
  }

  var normalizedColor = nameToHex(color);

  if (normalizedColor.match(hexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16)
    };
  }

  if (normalizedColor.match(hexRgbaRegex)) {
    var alpha = parseFloat((parseInt("" + normalizedColor[7] + normalizedColor[8], 16) / 255).toFixed(2));
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16),
      alpha: alpha
    };
  }

  if (normalizedColor.match(reducedHexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16)
    };
  }

  if (normalizedColor.match(reducedRgbaHexRegex)) {
    var _alpha = parseFloat((parseInt("" + normalizedColor[4] + normalizedColor[4], 16) / 255).toFixed(2));

    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16),
      alpha: _alpha
    };
  }

  var rgbMatched = rgbRegex.exec(normalizedColor);

  if (rgbMatched) {
    return {
      red: parseInt("" + rgbMatched[1], 10),
      green: parseInt("" + rgbMatched[2], 10),
      blue: parseInt("" + rgbMatched[3], 10)
    };
  }

  var rgbaMatched = rgbaRegex.exec(normalizedColor.substring(0, 50));

  if (rgbaMatched) {
    return {
      red: parseInt("" + rgbaMatched[1], 10),
      green: parseInt("" + rgbaMatched[2], 10),
      blue: parseInt("" + rgbaMatched[3], 10),
      alpha: parseFloat("" + rgbaMatched[4])
    };
  }

  var hslMatched = hslRegex.exec(normalizedColor);

  if (hslMatched) {
    var hue = parseInt("" + hslMatched[1], 10);
    var saturation = parseInt("" + hslMatched[2], 10) / 100;
    var lightness = parseInt("" + hslMatched[3], 10) / 100;
    var rgbColorString = "rgb(" + hslToRgb(hue, saturation, lightness) + ")";
    var hslRgbMatched = rgbRegex.exec(rgbColorString);

    if (!hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, rgbColorString);
    }

    return {
      red: parseInt("" + hslRgbMatched[1], 10),
      green: parseInt("" + hslRgbMatched[2], 10),
      blue: parseInt("" + hslRgbMatched[3], 10)
    };
  }

  var hslaMatched = hslaRegex.exec(normalizedColor.substring(0, 50));

  if (hslaMatched) {
    var _hue = parseInt("" + hslaMatched[1], 10);

    var _saturation = parseInt("" + hslaMatched[2], 10) / 100;

    var _lightness = parseInt("" + hslaMatched[3], 10) / 100;

    var _rgbColorString = "rgb(" + hslToRgb(_hue, _saturation, _lightness) + ")";

    var _hslRgbMatched = rgbRegex.exec(_rgbColorString);

    if (!_hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, _rgbColorString);
    }

    return {
      red: parseInt("" + _hslRgbMatched[1], 10),
      green: parseInt("" + _hslRgbMatched[2], 10),
      blue: parseInt("" + _hslRgbMatched[3], 10),
      alpha: parseFloat("" + hslaMatched[4])
    };
  }

  throw new PolishedError(5);
}

function rgbToHsl(color) {
  // make sure rgb are contained in a set of [0, 255]
  var red = color.red / 255;
  var green = color.green / 255;
  var blue = color.blue / 255;
  var max = Math.max(red, green, blue);
  var min = Math.min(red, green, blue);
  var lightness = (max + min) / 2;

  if (max === min) {
    // achromatic
    if (color.alpha !== undefined) {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness,
        alpha: color.alpha
      };
    } else {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness
      };
    }
  }

  var hue;
  var delta = max - min;
  var saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);

  switch (max) {
    case red:
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
      break;

    case green:
      hue = (blue - red) / delta + 2;
      break;

    default:
      // blue case
      hue = (red - green) / delta + 4;
      break;
  }

  hue *= 60;

  if (color.alpha !== undefined) {
    return {
      hue: hue,
      saturation: saturation,
      lightness: lightness,
      alpha: color.alpha
    };
  }

  return {
    hue: hue,
    saturation: saturation,
    lightness: lightness
  };
}

/**
 * Returns an HslColor or HslaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a HslColor or HslaColor object back to a string.
 *
 * @example
 * // Assigns `{ hue: 0, saturation: 1, lightness: 0.5 }` to color1
 * const color1 = parseToHsl('rgb(255, 0, 0)');
 * // Assigns `{ hue: 128, saturation: 1, lightness: 0.5, alpha: 0.75 }` to color2
 * const color2 = parseToHsl('hsla(128, 100%, 50%, 0.75)');
 */
function parseToHsl(color) {
  // Note: At a later stage we can optimize this function as right now a hsl
  // color would be parsed converted to rgb values and converted back to hsl.
  return rgbToHsl(parseToRgb(color));
}

/**
 * Reduces hex values if possible e.g. #ff8866 to #f86
 * @private
 */
var reduceHexValue = function reduceHexValue(value) {
  if (value.length === 7 && value[1] === value[2] && value[3] === value[4] && value[5] === value[6]) {
    return "#" + value[1] + value[3] + value[5];
  }

  return value;
};

var reduceHexValue$1 = reduceHexValue;

function numberToHex(value) {
  var hex = value.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function colorToHex(color) {
  return numberToHex(Math.round(color * 255));
}

function convertToHex(red, green, blue) {
  return reduceHexValue$1("#" + colorToHex(red) + colorToHex(green) + colorToHex(blue));
}

function hslToHex(hue, saturation, lightness) {
  return hslToRgb(hue, saturation, lightness, convertToHex);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsl(359, 0.75, 0.4),
 *   background: hsl({ hue: 360, saturation: 0.75, lightness: 0.4 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsl(359, 0.75, 0.4)};
 *   background: ${hsl({ hue: 360, saturation: 0.75, lightness: 0.4 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#b3191c";
 *   background: "#b3191c";
 * }
 */
function hsl(value, saturation, lightness) {
  if (typeof value === 'number' && typeof saturation === 'number' && typeof lightness === 'number') {
    return hslToHex(value, saturation, lightness);
  } else if (typeof value === 'object' && saturation === undefined && lightness === undefined) {
    return hslToHex(value.hue, value.saturation, value.lightness);
  }

  throw new PolishedError(1);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsla(359, 0.75, 0.4, 0.7),
 *   background: hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 }),
 *   background: hsla(359, 0.75, 0.4, 1),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsla(359, 0.75, 0.4, 0.7)};
 *   background: ${hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 })};
 *   background: ${hsla(359, 0.75, 0.4, 1)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(179,25,28,0.7)";
 *   background: "rgba(179,25,28,0.7)";
 *   background: "#b3191c";
 * }
 */
function hsla(value, saturation, lightness, alpha) {
  if (typeof value === 'number' && typeof saturation === 'number' && typeof lightness === 'number' && typeof alpha === 'number') {
    return alpha >= 1 ? hslToHex(value, saturation, lightness) : "rgba(" + hslToRgb(value, saturation, lightness) + "," + alpha + ")";
  } else if (typeof value === 'object' && saturation === undefined && lightness === undefined && alpha === undefined) {
    return value.alpha >= 1 ? hslToHex(value.hue, value.saturation, value.lightness) : "rgba(" + hslToRgb(value.hue, value.saturation, value.lightness) + "," + value.alpha + ")";
  }

  throw new PolishedError(2);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgb(255, 205, 100),
 *   background: rgb({ red: 255, green: 205, blue: 100 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgb(255, 205, 100)};
 *   background: ${rgb({ red: 255, green: 205, blue: 100 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#ffcd64";
 *   background: "#ffcd64";
 * }
 */
function rgb(value, green, blue) {
  if (typeof value === 'number' && typeof green === 'number' && typeof blue === 'number') {
    return reduceHexValue$1("#" + numberToHex(value) + numberToHex(green) + numberToHex(blue));
  } else if (typeof value === 'object' && green === undefined && blue === undefined) {
    return reduceHexValue$1("#" + numberToHex(value.red) + numberToHex(value.green) + numberToHex(value.blue));
  }

  throw new PolishedError(6);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * Can also be used to fade a color by passing a hex value or named CSS color along with an alpha value.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgba(255, 205, 100, 0.7),
 *   background: rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 }),
 *   background: rgba(255, 205, 100, 1),
 *   background: rgba('#ffffff', 0.4),
 *   background: rgba('black', 0.7),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgba(255, 205, 100, 0.7)};
 *   background: ${rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 })};
 *   background: ${rgba(255, 205, 100, 1)};
 *   background: ${rgba('#ffffff', 0.4)};
 *   background: ${rgba('black', 0.7)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,205,100,0.7)";
 *   background: "rgba(255,205,100,0.7)";
 *   background: "#ffcd64";
 *   background: "rgba(255,255,255,0.4)";
 *   background: "rgba(0,0,0,0.7)";
 * }
 */
function rgba(firstValue, secondValue, thirdValue, fourthValue) {
  if (typeof firstValue === 'string' && typeof secondValue === 'number') {
    var rgbValue = parseToRgb(firstValue);
    return "rgba(" + rgbValue.red + "," + rgbValue.green + "," + rgbValue.blue + "," + secondValue + ")";
  } else if (typeof firstValue === 'number' && typeof secondValue === 'number' && typeof thirdValue === 'number' && typeof fourthValue === 'number') {
    return fourthValue >= 1 ? rgb(firstValue, secondValue, thirdValue) : "rgba(" + firstValue + "," + secondValue + "," + thirdValue + "," + fourthValue + ")";
  } else if (typeof firstValue === 'object' && secondValue === undefined && thirdValue === undefined && fourthValue === undefined) {
    return firstValue.alpha >= 1 ? rgb(firstValue.red, firstValue.green, firstValue.blue) : "rgba(" + firstValue.red + "," + firstValue.green + "," + firstValue.blue + "," + firstValue.alpha + ")";
  }

  throw new PolishedError(7);
}

var isRgb = function isRgb(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};

var isRgba = function isRgba(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && typeof color.alpha === 'number';
};

var isHsl = function isHsl(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};

var isHsla = function isHsla(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && typeof color.alpha === 'number';
};
/**
 * Converts a RgbColor, RgbaColor, HslColor or HslaColor object to a color string.
 * This util is useful in case you only know on runtime which color object is
 * used. Otherwise we recommend to rely on `rgb`, `rgba`, `hsl` or `hsla`.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: toColorString({ red: 255, green: 205, blue: 100 }),
 *   background: toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 }),
 *   background: toColorString({ hue: 240, saturation: 1, lightness: 0.5 }),
 *   background: toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${toColorString({ red: 255, green: 205, blue: 100 })};
 *   background: ${toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 })};
 *   background: ${toColorString({ hue: 240, saturation: 1, lightness: 0.5 })};
 *   background: ${toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 })};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#ffcd64";
 *   background: "rgba(255,205,100,0.72)";
 *   background: "#00f";
 *   background: "rgba(179,25,25,0.72)";
 * }
 */


function toColorString(color) {
  if (typeof color !== 'object') throw new PolishedError(8);
  if (isRgba(color)) return rgba(color);
  if (isRgb(color)) return rgb(color);
  if (isHsla(color)) return hsla(color);
  if (isHsl(color)) return hsl(color);
  throw new PolishedError(8);
}

// Type definitions taken from https://github.com/gcanti/flow-static-land/blob/master/src/Fun.js
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-redeclare
function curried(f, length, acc) {
  return function fn() {
    // eslint-disable-next-line prefer-rest-params
    var combined = acc.concat(Array.prototype.slice.call(arguments));
    return combined.length >= length ? f.apply(this, combined) : curried(f, length, combined);
  };
} // eslint-disable-next-line no-redeclare


function curry(f) {
  // eslint-disable-line no-redeclare
  return curried(f, f.length, []);
}

function guard(lowerBoundary, upperBoundary, value) {
  return Math.max(lowerBoundary, Math.min(upperBoundary, value));
}

/**
 * Returns a number (float) representing the luminance of a color.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff',
 *   background: getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)',
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff'};
 *   background: ${getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)'};
 *
 * // CSS in JS Output
 *
 * div {
 *   background: "#CCCD64";
 *   background: "rgba(58, 133, 255, 1)";
 * }
 */

function getLuminance(color) {
  if (color === 'transparent') return 0;
  var rgbColor = parseToRgb(color);

  var _Object$keys$map = Object.keys(rgbColor).map(function (key) {
    var channel = rgbColor[key] / 255;
    return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
  }),
      r = _Object$keys$map[0],
      g = _Object$keys$map[1],
      b = _Object$keys$map[2];

  return parseFloat((0.2126 * r + 0.7152 * g + 0.0722 * b).toFixed(3));
}

/**
 * Sets the lightness of a color to the provided value. The lightness range can be
 * from 0 and 1.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: setLightness(0.2, '#CCCD64'),
 *   background: setLightness('0.75', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${setLightness(0.2, '#CCCD64')};
 *   background: ${setLightness('0.75', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#4d4d19";
 *   background: "rgba(223,224,159,0.7)";
 * }
 */

function setLightness(lightness, color) {
  if (color === 'transparent') return color;
  return toColorString(_extends$Z({}, parseToHsl(color), {
    lightness: parseFloat(lightness)
  }));
} // prettier-ignore


var curriedSetLightness = /*#__PURE__*/curry
/* ::<number | string, string, string> */
(setLightness);
var curriedSetLightness$1 = curriedSetLightness;

/**
 * Decreases the opacity of a color. Its range for the amount is between 0 to 1.
 *
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: transparentize(0.1, '#fff');
 *   background: transparentize(0.2, 'hsl(0, 0%, 100%)'),
 *   background: transparentize('0.5', 'rgba(255, 0, 0, 0.8)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${transparentize(0.1, '#fff')};
 *   background: ${transparentize(0.2, 'hsl(0, 0%, 100%)')},
 *   background: ${transparentize('0.5', 'rgba(255, 0, 0, 0.8)')},
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,255,255,0.9)";
 *   background: "rgba(255,255,255,0.8)";
 *   background: "rgba(255,0,0,0.3)";
 * }
 */

function transparentize(amount, color) {
  if (color === 'transparent') return color;
  var parsedColor = parseToRgb(color);
  var alpha = typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;

  var colorWithAlpha = _extends$Z({}, parsedColor, {
    alpha: guard(0, 1, +(alpha * 100 - parseFloat(amount) * 100).toFixed(2) / 100)
  });

  return rgba(colorWithAlpha);
} // prettier-ignore


var curriedTransparentize = /*#__PURE__*/curry
/* ::<number | string, string, string> */
(transparentize);
var curriedTransparentize$1 = curriedTransparentize;

const invertBrightness = colorValue => {
    const lightness = getLuminance(`${colorValue}`);
    const newColor = curriedSetLightness$1(1 - lightness, `${colorValue}`);
    return newColor;
};
const darkMode = color => {
    // Intentionally NOT inverting base on shaded colors
    const base = color.toString();
    return new Color(color.kind, color.kind === 'shaded' ? base : invertBrightness(base), color.mapShades(invertBrightness));
};
function getAlphaConverter(alpha) {
    const alphaConverter = colorValue => {
        if (alpha < 1) {
            const transparency = 1 - alpha;
            colorValue = curriedTransparentize$1(transparency, `${colorValue}`);
        }
        return colorValue;
    };
    return color => new Color(color.kind, alphaConverter(color.toString()), color.mapShades(alphaConverter));
}

function isCustomColorLookup(str) {
    return str.startsWith('lookup:');
}
function getCustomColorLookupPath(str) {
    return str.substring(7);
}
const emphasis = {
    normal: 1, medium: 0.6, low: 0.3
};
const color = str => {
    return (props) => {
        if (isCustomColorLookup(str)) {
            const path = getCustomColorLookupPath(str);
            const found = get(props.theme, path);
            if (found && isColorIndicator(found)) {
                return color(found)(props);
            }
            return found;
        }
        if (str === 'inherit' || str === 'currentColor') {
            return str;
        }
        const indicator = str;
        let colorVal = indicator;
        let alpha = 1;
        let skipColorConverter = false;
        if (colorVal.includes('@')) {
            const [colorName, colorAlpha] = colorVal.split('@', 2);
            colorVal = colorName;
            alpha = (colorAlpha in emphasis ? emphasis[colorAlpha] : Number(colorAlpha)) || 1;
        }
        if (colorVal.startsWith('!')) {
            skipColorConverter = true;
            colorVal = colorVal.substring(1); // remove !
        }
        const [colorName, colorShade] = colorVal.split('.');
        let colorValue;
        const colorInstance = getAlphaConverter(alpha)((themeLookup(props.theme, `colors.${colorName}`, { skipColorConverter })));
        if (isShadedColorName(colorName)) {
            colorValue = colorInstance.toString(colorShade);
        }
        else {
            colorValue = colorInstance.toString();
        }
        return colorValue;
    };
};

const padding = block(rule('padding'), rule('paddingTop'), rule('paddingBottom'), rule('paddingLeft'), rule('paddingRight'));
const border = block(rule('borderWidth'), rule('borderStyle'), rule('borderColor', undefined, { parser: color }), rule('borderTopWidth'), rule('borderTopStyle'), rule('borderTopColor', undefined, { parser: color }), rule('borderBottomWidth'), rule('borderBottomStyle'), rule('borderBottomColor', undefined, { parser: color }), rule('borderLeftWidth'), rule('borderLeftStyle'), rule('borderLeftColor', undefined, { parser: color }), rule('borderRightWidth'), rule('borderRightStyle'), rule('borderRightColor', undefined, { parser: color }), rule('borderRadius'));
const base$5 = block(rule('display'), rule('position'), rule('top'), rule('bottom'), rule('left'), rule('right'), rule('cursor'), rule('textAlign'), rule('width'), rule('maxWidth'), rule('minWidth'), rule('height'), rule('maxHeight'), rule('minHeight'), rule('padding'), rule('margin'), rule('overflow'), rule('borderRadius'), rule('color', undefined, { parser: color }), rule('backgroundColor', undefined, { parser: color }), rule('focusOutlineColor', undefined, {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}));
const all = block(base$5, padding, border);

const input = block(border, rule('width', '100%'), rule('padding', '2.5rem .4rem 1rem'), rule('color', 'black', { parser: color }), rule('backgroundColor', 'clear', { parser: color }), rule('placeholderColor', 'grey', {
    parser: color,
    cssProperty: '::placeholder.color',
}), rule('focusOutlineColor', 'primary', {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}), rule('disabledColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.color',
}));
const select = block(border, rule('width', '100%'), rule('padding', '2.5rem .4rem 1rem'), rule('color', 'black', { parser: color }), rule('backgroundColor', 'clear', { parser: color }), rule('placeholderColor', 'grey', {
    parser: color,
    cssProperty: '::placeholder.color',
}), rule('focusOutlineColor', 'primary', {
    parser: color,
    cssProperty: ':focus-visible.outline-color',
}), rule('disabledColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.color',
}), rule('mobileHeight', '6rem', {
    cssProperty: '@supports (-webkit-overflow-scrolling: touch).height'
}), rule('appearance', 'none'), rule('borderRadius', '0'));

const Input = guitar('input', input);

var BasicFormControls = /*#__PURE__*/Object.freeze({
    __proto__: null,
    Input: Input
});

const Box = {
    Model: guitar('div', all),
    Simple: guitar('div', base$5),
    Padding: guitar('div', padding),
    Border: guitar('div', border),
};

function hue(defaultHue, cb) {
    const f = css `
    ${(props) => {
        const hueName = props.$hue || defaultHue;
        return cb(hueName);
    }}
  `;
    return f;
}

const base$4 = block(rule('fontSize', '1.6rem'), rule('fontWeight', 'initial'), rule('color', 'inherit', { parser: color }), rule('textTransform'), rule('textAlign'), rule('textOverflow', 'ellipsis'), rule('overflow'), rule('whiteSpace'), rule('cursor'), rule('userSelect'), rule('wordBreak'));
const caps = block(rule('textTransform', 'uppercase'));
const h1 = block(base$4, rule('fontSize', '1.04rem'), rule('fontWeight', '300'), rule('letterSpacing', '-.15rem'));
const h2 = block(base$4, rule('fontSize', '6.4rem'), rule('fontWeight', '300'), rule('letterSpacing', '-.05rem'));
const h3 = block(base$4, rule('fontSize', '4.8rem'), rule('fontWeight', '300'));
const h4 = block(base$4, rule('fontSize', '3.2rem'), rule('fontWeight', 'bold'), rule('letterSpacing', '0.025rem'));
const h5 = block(base$4, rule('fontSize', '2.6rem'), rule('fontWeight', '300'));
const h6 = block(base$4, rule('fontSize', '2.2rem'), rule('fontWeight', '500'), rule('letterSpacing', '0.025rem'));
const subtitle1 = block(base$4, rule('fontSize', '1.8rem'), rule('letterSpacing', '0.015rem'));
const subtitle2 = block(base$4, rule('fontSize', '1.8rem'), rule('fontWeight', '500'), rule('letterSpacing', '0.01rem'));
const body1 = block(base$4, rule('fontSize', '1.8rem'));
const body2 = block(base$4, rule('fontSize', '1.5rem'), rule('letterSpacing', '.025rem'));
const button = block(base$4, caps, rule('fontSize', '1.5rem'), rule('fontWeight', '500'), rule('letterSpacing', '.125rem'));
const caption = block(base$4, rule('fontSize', '1.3rem'), rule('fontWeight', '400'), rule('letterSpacing', '.04rem'));
const overline = block(base$4, caps, rule('fontSize', '1.3rem'), rule('fontWeight', '500'), rule('letterSpacing', '.2rem'));

const base$3 = block(button, rule('cursor', 'pointer'), rule('textAlign', 'center'), rule('width', 'fit-content'), rule('height'));
const primary$1 = hue('primary', hue => block(base$3, rule('appearance', 'none'), rule('display', 'block'), rule('borderRadius', 'lookup:buttons.borderRadius'), rule('borderWidth', '1px'), rule('borderStyle', 'solid'), rule('backgroundColor', `!${hue}`, {
    parser: color,
    hover: `!${hue}.darker`,
}), rule('borderColor', hue, {
    parser: color,
    hover: `!${hue}.darker`,
}), rule('color', '!white', {
    parser: color,
    hover: '!white'
}), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
}), rule('disabledBackgroundColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.background-color',
}), rule('disabledBorderColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.border-color',
}), rule('padding', '1rem')));
const secondary$1 = hue('primary', hue => block(primary$1, rule('backgroundColor', 'clear', { parser: color }), rule('color', `!${hue}`, { parser: color }), rule('borderColor', `!${hue}`, { parser: color }), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
}), rule('disabledBorderColor', 'grey.light', {
    parser: color,
    hover: 'grey.light',
    cssProperty: ':disabled.border-color',
})));
const tertiary = hue('primary', hue => block(base$3, rule('padding', '0'), rule('display', 'inline'), rule('backgroundColor', 'clear', { parser: color }), rule('borderStyle', 'none'), rule('color', `!${hue}`, {
    parser: color,
    hover: `!${hue}.darker`,
}), rule('disabledColor', 'grey', {
    parser: color,
    hover: 'grey',
    cssProperty: ':disabled.color',
})));

function buttonThemesOn(element) {
    return {
        Primary: guitar(element, primary$1),
        Secondary: guitar(element, secondary$1),
        Tertiary: guitar(element, tertiary),
    };
}
const Button = buttonThemesOn('button');
const subs = {
    A: buttonThemesOn('a'),
    Div: buttonThemesOn('div'),
    Link: buttonThemesOn(Link$1),
};
Object.assign(Button, subs);

const base$2 = block(rule('padding'), rule('textAlign'), rule('width'), rule('height'), rule('maxWidth'), rule('maxHeight'), rule('minWidth'), rule('minHeight'), rule('aspectRatio'), rule('display', 'block'), rule('overflow'), rule('whiteSpace'), rule('cursor'), rule('borderRadius'), rule('transform'), rule('borderStyle'), rule('borderWidth'), rule('color', undefined, {
    parser: color,
}), rule('borderColor', undefined, { parser: color }), rule('backgroundColor', 'clear', {
    parser: color,
}), rule('focusOutlineColor', undefined, {
    parser: color,
    cssProperty: ':focus.outline-color',
}));
const position = block(base$2, rule('position'), rule('top'), rule('left'), rule('bottom'), rule('right'), rule('zIndex'));
const grid = block(base$2, rule('display', 'grid'), rule('gridTemplateColumns'), rule('gridTemplateRows'), rule('gridAutoColumns'), rule('gridAutoRows'), rule('gridGap'), rule('alignItems'), rule('justifyItems'), rule('alignContent'), rule('justifyContent'));
const gridItem = block(base$2, rule('gridColumnStart'), rule('gridColumnEnd'), rule('gridRowStart'), rule('gridRowEnd'), rule('justifySelf'), rule('alignSelf'), rule('order'));
const gridItemGrid = block(gridItem, grid);
const panel = block(rule('textAlign'), rule('borderStyle', 'solid'), rule('borderWidth', '1px'), rule('borderColor', 'grey.light', { parser: color }), rule('borderRightStyle', undefined, { media: { mobile: 'none' } }), rule('borderLeftStyle', undefined, { media: { mobile: 'none' } }), rule('backgroundColor', 'white', {
    parser: color,
}), rule('padding', 'lookup:panel.padding', {
    media: {
        mobile: 'lookup:panel.mobile.padding',
    }
}), rule('borderRadius', 'lookup:panel.borderRadius', {
    media: {
        mobile: '0',
    }
}));

const Base = guitar('div', base$2);
const Link = guitar(Link$1, base$2);
const Grid = guitar('div', grid);
const GridItem = guitar('div', gridItem);
const GridItemGrid = guitar('div', gridItemGrid);
const Panel = guitar('div', panel);
const Position = guitar('div', position);
const Container = {
    Base,
    Link,
    Grid,
    GridItem,
    GridItemGrid,
    Panel,
    Position
};

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var jsxRuntime = {exports: {}};

var reactJsxRuntime_production_min = {};

/**
 * @license React
 * react-jsx-runtime.production.min.js
 *
 * Copyright (c) Facebook, Inc. and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */
var f=React__default,k=Symbol.for("react.element"),l=Symbol.for("react.fragment"),m=Object.prototype.hasOwnProperty,n=f.__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED.ReactCurrentOwner,p={key:!0,ref:!0,__self:!0,__source:!0};
function q(c,a,g){var b,d={},e=null,h=null;void 0!==g&&(e=""+g);void 0!==a.key&&(e=""+a.key);void 0!==a.ref&&(h=a.ref);for(b in a)m.call(a,b)&&!p.hasOwnProperty(b)&&(d[b]=a[b]);if(c&&c.defaultProps)for(b in a=c.defaultProps,a)void 0===d[b]&&(d[b]=a[b]);return {$$typeof:k,type:c,key:e,ref:h,props:d,_owner:n.current}}reactJsxRuntime_production_min.Fragment=l;reactJsxRuntime_production_min.jsx=q;reactJsxRuntime_production_min.jsxs=q;

{
  jsxRuntime.exports = reactJsxRuntime_production_min;
}

const linearGradient$1 = (gradient) => {
    return (props) => {
        if (Array.isArray(gradient)) {
            const colors = gradient.map((colorName) => `${color(colorName)(props)}`).join(', ');
            return `linear-gradient(${colors})`;
        }
        else {
            const { direction, colors, stops = [] } = gradient;
            const colorStops = colors.map((colorName, index) => `${color(colorName)(props)}${stops[index] ? ` ${stops[index]}%` : ''}`).join(', ');
            if (direction) {
                return `linear-gradient(${gradient.direction}, ${colorStops})`;
            }
            else {
                return `linear-gradient(${colorStops})`;
            }
        }
    };
};

const opacity = block(rule('opacity'));
const linearGradient = block(rule('backgroundSize'), rule('linearGradient', ['primary', 'primary@0'], {
    parser: linearGradient$1,
    cssProperty: 'background-image',
}));

const AnimationDiv = styled.div `
  display: contents;
  > * {
    animation: ${props => props.$animation}
  }
`;
const Animation = props => {
    const { $keyframes, $duration = '1s', $easing = 'ease', $delay = '0s', $iteration = 'infinite', $direction = 'normal', children } = props;
    const animation = keyframes `${$keyframes}`;
    return (jsxRuntime.exports.jsx(AnimationDiv, Object.assign({ "$animation": css `${animation} ${$duration} ${$easing} ${$delay} ${$iteration} ${$direction}` }, { children: children }), void 0));
};
const TransitionDiv = styled.div `
  display: contents;
  > * {
    transition: ${props => props.$transition}
  }
`;
const Transition = props => {
    const { $property, $delay = '0s', $duration = '1s', $easing = 'ease', children } = props;
    return (jsxRuntime.exports.jsx(TransitionDiv, Object.assign({ "$transition": `${$property} ${$duration} ${$easing} ${$delay}` }, { children: children }), void 0));
};
const Effect = {
    Animation,
    LinearGradient: guitar('div', linearGradient),
    Opacity: guitar('div', opacity),
    Transition,
};

const dropShadow$1 = (ds) => {
    const [x, y, blur, c] = ds;
    return (props) => `drop-shadow(${x} ${y} ${blur} ${color(c)(props)})`;
};

const blur$1 = (amount) => {
    return () => `blur(${amount})`;
};

const dropShadow = block(rule('dropShadow', ['2px', '2px', '2px', 'black@.22'], {
    parser: dropShadow$1,
    cssProperty: 'filter',
}));
const blur = block(rule('blur', '3px', {
    parser: blur$1,
    cssProperty: 'filter',
}));
const backdropBlur = block(rule('blur', '3px', {
    parser: blur$1,
    cssProperty: 'backdrop-filter',
}));

const Filter = {
    DropShadow: guitar('div', dropShadow),
    Blur: guitar('div', blur),
    BackdropBlur: guitar('div', backdropBlur),
};

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

var _path$W;

function _extends$Y() { _extends$Y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Y.apply(this, arguments); }

var SvgAlertCircle = function SvgAlertCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Y({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_AlertCircle"
  }, props), _path$W || (_path$W = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M12 15.106a.95.95 0 010 1.9.95.95 0 010-1.9Zm-.638-8.723a.638.638 0 011.276 0v6.865a.638.638 0 01-1.276 0V6.383ZM12 2a1 1 0 010 20 1 1 0 010-20Zm0 1.277a1 1 0 000 17.446 1 1 0 000-17.446Z"
  })));
};

var _path$V;

function _extends$X() { _extends$X = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$X.apply(this, arguments); }

var SvgArrowRight = function SvgArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$X({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ArrowRight"
  }, props), _path$V || (_path$V = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M2 10.66c0-.73.6-1.33 1.33-1.33H16v-3.1c0-.24.14-.3.32-.14l5.47 5.42a.67.67 0 010 .98l-5.47 5.42c-.18.16-.32.1-.32-.14v-3.1H3.33c-.73 0-1.33-.6-1.33-1.34Z"
  })));
};

var _path$U;

function _extends$W() { _extends$W = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$W.apply(this, arguments); }

var SvgArrowToBarLeft = function SvgArrowToBarLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$W({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ArrowToBarLeft"
  }, props), _path$U || (_path$U = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M7.88 11.86h13.49a.61.61 0 010 1.28H7.88l3.94 4.04a.61.61 0 01-.89.91l-5-5.14a.65.65 0 010-.9l5-5.14a.61.61 0 01.89.9ZM2 4.56a.61.61 0 011.25 0v15.87a.61.61 0 01-1.25 0Z"
  })));
};

var _path$T;

function _extends$V() { _extends$V = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$V.apply(this, arguments); }

var SvgArrowToBarRight = function SvgArrowToBarRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$V({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ArrowToBarRight"
  }, props), _path$T || (_path$T = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M19.87 11.86h-13.49a.63.63 0 000 1.28h13.49l-3.94 4.04a.61.61 0 00.89.91l5-5.14a.65.65 0 000-.9l-5-5.14a.61.61 0 00-.89.9ZM2 4.56a.61.61 0 011.25 0v15.87a.61.61 0 01-1.25 0Z"
  })));
};

var _path$S;

function _extends$U() { _extends$U = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$U.apply(this, arguments); }

var SvgBank = function SvgBank(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$U({
    viewBox: "0 0 26 26",
    "data-name": "RoadieUI_icon_Bank"
  }, props), _path$S || (_path$S = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    d: "M13.098 1 3 6.645v2.257h20.195V6.645zm5.048 18.146h3.366v-7.902h-3.366zm-6.731 0h3.365v-7.902h-3.365zm-6.732 0h3.366v-7.902H4.683zM3 25h20.195v-3.512H3z"
  })));
};

var _path$R;

function _extends$T() { _extends$T = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$T.apply(this, arguments); }

var SvgBlankProfile = function SvgBlankProfile(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$T({
    viewBox: "0 0 64 64",
    "data-name": "RoadieUI_icon_BlankProfile"
  }, props), _path$R || (_path$R = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M16.5 22.5a1 1 0 0031 0 1 1 0 00-31 0m15.62 20c10.75 0 20.66 1.46 28.58 3.91a32 32 0 01-57.16 0c7.92-2.45 17.83-3.91 28.58-3.91Z"
  })));
};

var _path$Q;

function _extends$S() { _extends$S = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$S.apply(this, arguments); }

var SvgCalendar = function SvgCalendar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$S({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Calendar"
  }, props), _path$Q || (_path$Q = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M8.13 13.94a.88.88 0 01.01-1.8.88.88 0 01-.01 1.79Zm3.94 0a.88.88 0 010-1.8.88.88 0 01-.01 1.79Zm3.77 0a.88.88 0 01.01-1.8.88.88 0 01-.01 1.79ZM5.34 21c-.36 0-.67-.14-.94-.4a1.3 1.3 0 01-.4-.95V5.7c0-.36.13-.67.4-.95.27-.26.58-.4.93-.4h1.45V3h1.44v1.35h7.56V3h1.44v1.35h1.45c.35 0 .66.14.93.4.27.28.4.59.4.95v13.95c0 .36-.13.68-.4.95-.27.26-.58.4-.93.4Zm0-1.35h13.33V9.98H5.33Zm0-11.02h13.33V5.7H5.33Z"
  })));
};

var _path$P;

function _extends$R() { _extends$R = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$R.apply(this, arguments); }

var SvgCheckmark = function SvgCheckmark(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$R({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Checkmark"
  }, props), _path$P || (_path$P = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "m23.146 5.4-2.792-2.8a.5.5 0 0 0-.708 0L7.854 14.4a.5.5 0 0 1-.708 0l-2.792-2.8a.5.5 0 0 0-.708 0L.854 14.4a.5.5 0 0 0 0 .707L7.146 21.4a.5.5 0 0 0 .708 0L23.146 6.1a.5.5 0 0 0 0-.7Z"
  })));
};

var _path$O;

function _extends$Q() { _extends$Q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$Q.apply(this, arguments); }

var SvgCheckmarkCircle = function SvgCheckmarkCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$Q({
    viewBox: "0 0 14 14",
    "data-name": "RoadieUI_icon_CheckmarkCircle"
  }, props), _path$O || (_path$O = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M7 .333A6.67 6.67 0 0 0 .333 7 6.67 6.67 0 0 0 7 13.667 6.67 6.67 0 0 0 13.667 7 6.67 6.67 0 0 0 7 .333zm-1.333 10L2.333 7l.94-.94 2.394 2.387 5.06-5.06.94.946-6 6z"
  })));
};

var _path$N;

function _extends$P() { _extends$P = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$P.apply(this, arguments); }

var SvgChevronDoubleRight = function SvgChevronDoubleRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$P({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ChevronDoubleRight"
  }, props), _path$N || (_path$N = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M12.683 23.741a1.44 1.44 0 0 1-.36-2l6.583-9.463a.48.48 0 0 0 0-.549l-6.583-9.467A1.44 1.44 0 0 1 14.688.618l6.582 9.463a3.372 3.372 0 0 1 0 3.837l-6.582 9.463a1.44 1.44 0 0 1-2.005.36zM7.96.892l6.585 9.463c.684.99.684 2.3 0 3.29L7.96 23.107a.961.961 0 0 1-.788.411H3.96a.96.96 0 0 1-.788-1.508l6.583-9.463a.963.963 0 0 0 0-1.1L3.172 1.988A.96.96 0 0 1 3.96.48h3.213a.96.96 0 0 1 .788.412z"
  })));
};

var _path$M;

function _extends$O() { _extends$O = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$O.apply(this, arguments); }

var SvgChevronDown = function SvgChevronDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$O({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ChevronDown"
  }, props), _path$M || (_path$M = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M19.692 6 22 8.25 12 18 2 8.25 4.308 6 12 13.5z"
  })));
};

var _path$L;

function _extends$N() { _extends$N = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$N.apply(this, arguments); }

var SvgChevronLeft = function SvgChevronLeft(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$N({
    viewBox: "0 0 15 15",
    "data-name": "RoadieUI_icon_ChevronLeft"
  }, props), _path$L || (_path$L = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M3.614 5.857 9.271.2l1.414 1.414-5.657 5.657 5.657 5.657-1.414 1.414L2.2 7.272l1.414-1.415Z"
  })));
};

var _path$K;

function _extends$M() { _extends$M = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$M.apply(this, arguments); }

var SvgChevronRight = function SvgChevronRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$M({
    viewBox: "0 0 15 15",
    "data-name": "RoadieUI_icon_ChevronRight"
  }, props), _path$K || (_path$K = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M10.728 8.685 5.07 14.342l-1.414-1.414L9.314 7.27 3.657 1.614 5.07.2l7.071 7.071-1.414 1.414Z"
  })));
};

var _path$J;

function _extends$L() { _extends$L = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$L.apply(this, arguments); }

var SvgChevronUp = function SvgChevronUp(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$L({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ChevronUp"
  }, props), _path$J || (_path$J = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M4.308 18 2 15.75 12 6l10 9.75L19.692 18 12 10.5z"
  })));
};

var _path$I;

function _extends$K() { _extends$K = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$K.apply(this, arguments); }

var SvgClock = function SvgClock(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$K({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Clock"
  }, props), _path$I || (_path$I = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M12 2c5.523 0 10 4.477 10 10s-4.477 10-10 10S2 17.523 2 12 6.477 2 12 2Zm0 1.429a8.571 8.571 0 1 0 0 17.142A8.571 8.571 0 0 0 12 3.43Z M12 7.714c.362 0 .66.269.708.618l.006.097V12a.714.714 0 0 1-1.422.097L11.286 12V8.429c0-.395.32-.715.714-.715Z M11.495 11.495a.714.714 0 0 1 .922-.075l.088.075 4.464 4.465a.714.714 0 0 1-.922 1.085l-.088-.075-4.464-4.465a.714.714 0 0 1 0-1.01Z"
  })));
};

var _defs, _circle, _line, _line2;

function _extends$J() { _extends$J = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$J.apply(this, arguments); }

var SvgCloseCircle = function SvgCloseCircle(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$J({
    xmlns: "http://www.w3.org/2000/svg",
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_CloseCircle"
  }, props), _defs || (_defs = /*#__PURE__*/React.createElement("defs", null, /*#__PURE__*/React.createElement("style", null, ".a{fill:none;stroke-linecap:round;stroke-linejoin:round;stroke-width:1.5px;}"))), _circle || (_circle = /*#__PURE__*/React.createElement("circle", {
    className: "a icon-stroke",
    cx: 11.998,
    cy: 12,
    r: 11.25
  })), _line || (_line = /*#__PURE__*/React.createElement("line", {
    className: "a icon-stroke",
    x1: 7.498,
    y1: 16.5,
    x2: 16.497,
    y2: 7.5
  })), _line2 || (_line2 = /*#__PURE__*/React.createElement("line", {
    className: "a icon-stroke",
    x1: 16.498,
    y1: 16.5,
    x2: 7.497,
    y2: 7.5
  })));
};

var _path$H;

function _extends$I() { _extends$I = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$I.apply(this, arguments); }

var SvgCog = function SvgCog(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$I({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Cog"
  }, props), _path$H || (_path$H = /*#__PURE__*/React.createElement("path", {
    d: "M12.06 8.06a1 1 0 000 7.88 1 1 0 000-7.88Zm0 6.75a1 1 0 010-5.62 1 1 0 01-0 5.62Zm6.69-5.17-.34-.85.79-1.46a.95.95 0 00-.17-1.07l-1.35-1.35a.95.95 0 00-1.07-.17l-1.46.8-.84-.35-.51-1.57a.91.91 0 00-.84-.62h-1.92a.78.78 0 00-.78.67l-.51 1.58a2.5 2.5 0 00-.9.34L7.39 4.8a.95.95 0 00-1.07.17L4.97 6.32a.95.95 0 00-.17 1.07l.73 1.4-.34.9-1.57.51a.91.91 0 00-.62.84v1.92c0 .39.28.73.67.84l1.58.5.34.85-.79 1.46a.95.95 0 00.17 1.07l1.35 1.35c.28.28.73.34 1.07.17l1.46-.79.84.34.51 1.63a.9.9 0 00.84.62h1.92a.9.9 0 00.84-.62l.5-1.63.85-.34 1.46.79c.34.17.79.11 1.07-.17l1.35-1.35a.95.95 0 00.17-1.07l-.79-1.46.34-.84 1.63-.51A.91.91 0 0021 12.96v-1.92a.97.97 0 00-.68-.9Zm-.9 3.77-.06.28-.5 1.18-.17.28 1.01 1.86-1.12 1.12-1.86-1.01-.28.17c-.4.22-.79.4-1.18.5l-.28.06-.62 2.02H11.2l-.62-2.02-.28-.06-1.18-.5-.28-.17-1.86 1.01-1.12-1.12 1.01-1.86-.17-.28c-.22-.4-.4-.79-.5-1.18l-.06-.28-2.03-.62V11.2l1.92-.56.11-.28c.11-.45.28-.84.5-1.24l.17-.28-.95-1.86 1.12-1.12 1.8 1.01.29-.17c.39-.22.78-.4 1.23-.5l.28-.12.62-1.96h1.58l.62 1.96.28.12c.4.1.79.28 1.18.5l.28.17L17 5.86l1.12 1.12-1.01 1.86.17.28c.22.4.4.79.5 1.18l.06.28 2.02.62v1.58Z",
    className: "icon-fill"
  })));
};

var _path$G;

function _extends$H() { _extends$H = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$H.apply(this, arguments); }

var SvgClose = function SvgClose(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$H({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Close"
  }, props), _path$G || (_path$G = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "m13.47 11.95 7.29-7.29a1 1 0 00-1.41-1.41l-7.29 7.29-7.29-7.3a1 1 0 00-1.41 1.41l7.29 7.3-7.3 7.29a1 1 0 101.41 1.41l7.3-7.29 7.29 7.29a1 1 0 001.41-1.41Z"
  })));
};

var _path$F;

function _extends$G() { _extends$G = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$G.apply(this, arguments); }

var SvgCoins = function SvgCoins(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$G({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Coins"
  }, props), _path$F || (_path$F = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M14.813 2.438a6.75 6.75 0 1 0 0 13.5 6.75 6.75 0 0 0 0-13.5Zm0 1.124a5.625 5.625 0 1 1 0 11.25 5.625 5.625 0 0 1 0-11.25Z M7.33 9.166a.562.562 0 0 1-.304.735c-2.365.978-3.757 3.343-3.411 5.782.346 2.442 2.35 4.369 4.907 4.704 2.56.335 5.035-1.01 6.05-3.28a.563.563 0 0 1 1.027.458c-1.22 2.73-4.175 4.336-7.223 3.937-3.051-.4-5.457-2.712-5.875-5.661-.418-2.953 1.26-5.808 4.095-6.98a.562.562 0 0 1 .735.305Zm.4 3.871c.28 0 .511.204.555.472l.007.091c0 .177.099.343.261.439l.086.041 1.735.667a1.638 1.638 0 0 1 1.034 1.847A1.674 1.674 0 0 1 9.9 17.917l-.034.001v.654a.562.562 0 0 1-1.117.091l-.007-.091-.001-.649h-.697a.562.562 0 0 1-.09-1.117l.09-.008h1.707a.555.555 0 0 0 .554-.426.506.506 0 0 0-.249-.533l-.087-.042-1.734-.667a1.647 1.647 0 0 1-1.068-1.53c0-.31.252-.563.563-.563Zm7.198-8.35c.28 0 .511.204.555.472l.007.091v.648h.697a.562.562 0 0 1 .092 1.118l-.092.008H14.48a.555.555 0 0 0-.554.426.506.506 0 0 0 .249.532l.087.043 1.734.667a1.638 1.638 0 0 1 1.035 1.846 1.674 1.674 0 0 1-1.508 1.323l-.033.001v.654a.563.563 0 0 1-1.117.092l-.008-.092v-.649h-.697a.563.563 0 0 1-.091-1.117l.091-.007h1.705a.555.555 0 0 0 .555-.426.506.506 0 0 0-.249-.533l-.087-.042-1.735-.668a1.638 1.638 0 0 1-1.034-1.846 1.674 1.674 0 0 1 1.508-1.323l.034-.002V5.25c0-.31.252-.563.563-.563Z"
  })));
};

var _path$E;

function _extends$F() { _extends$F = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$F.apply(this, arguments); }

var SvgCreditCard = function SvgCreditCard(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$F({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_CreditCard"
  }, props), _path$E || (_path$E = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M20.26 4.565H3.74a2.175 2.175 0 0 0-2.175 2.174v10.522c0 1.2.974 2.174 2.174 2.174h16.522c1.2 0 2.174-.974 2.174-2.174V6.739c0-1.2-.974-2.174-2.174-2.174Zm-16.52.87h16.52c.721 0 1.305.584 1.305 1.304v10.522c0 .72-.584 1.304-1.304 1.304H3.739c-.72 0-1.304-.584-1.304-1.304V6.739c0-.72.584-1.304 1.304-1.304ZM22 7.628H2v.862h20Zm-2.609 3.5a.435.435 0 0 1 .078.862l-.078.007h-2.608a.435.435 0 0 1-.079-.862l.079-.007h2.608Zm-7.826 0a.435.435 0 0 1 .078.862l-.078.007H4.61a.435.435 0 0 1-.078-.862l.078-.007h6.956Zm-4.348 2.625a.435.435 0 0 1 .079.862l-.079.007H4.61a.435.435 0 0 1-.078-.862l.078-.007h2.608Z"
  })));
};

var _path$D;

function _extends$E() { _extends$E = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$E.apply(this, arguments); }

var SvgDocumentArrowRight = function SvgDocumentArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$E({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_DocumentArrowRight"
  }, props), _path$D || (_path$D = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M6 9.8h3.8V6H15v4.19h1V6a1 1 0 00-1-1H8.43L5 8.43V18a1 1 0 001 1h9a1 1 0 001-1H6Zm0-.96L8.84 6H9v3H6ZM17.16 11.18a.5.5 0 00-.7.7l1.62 1.62H12a.5.5 0 100 1h6.1l-1.65 1.64a.5.5 0 10.71.7L20 14Z"
  })));
};

var _path$C;

function _extends$D() { _extends$D = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$D.apply(this, arguments); }

var SvgDocumentComplete = function SvgDocumentComplete(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$D({
    viewBox: "0 0 120 120",
    "data-name": "RoadieUI_icon_DocumentComplete"
  }, props), _path$C || (_path$C = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    strokeWidth: 4,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M88 58a30 30 0 1 1 0 60 30 30 0 0 1 0-60zm15 20L83 98 73 88M18 33h22.5M18 48h40M18 63h30M18 78h25m10 30H5.94A2.94 2.94 0 0 1 3 105.06V5.94A2.94 2.94 0 0 1 5.94 3h50.84c.78 0 1.53.31 2.08.86l23.28 23.28c.55.55.86 1.3.86 2.08V48M58 3v25h25"
  })));
};

var _path$B, _path2;

function _extends$C() { _extends$C = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$C.apply(this, arguments); }

var SvgDocumentFinancial = function SvgDocumentFinancial(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$C({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_DocumentFinancial"
  }, props), _path$B || (_path$B = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    d: "M7.385 23H4.462A1.464 1.464 0 0 1 3 21.533V2.467C3 1.657 3.654 1 4.462 1h10.356c.388 0 .76.154 1.034.43l5.72 5.74c.274.276.428.648.428 1.037v13.326c.006.978-.482 1.467-1.462 1.467H7.385ZM6.2 14.783h9.236m-9.269 3.844h5.277 M22 8.422h-5.911a1.481 1.481 0 0 1-1.478-1.485V1"
  })), _path2 || (_path2 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "m8.55 6.778-1.48-.595a.53.53 0 0 1 .196-1.02h1.458a.437.437 0 0 0 0-.876h-.567a.073.073 0 0 1-.073-.073v-.391a.437.437 0 1 0-.87 0v.4c0 .038-.03.07-.067.073a1.4 1.4 0 0 0-.398 2.699l1.48.594a.53.53 0 0 1-.195 1.02H6.573a.437.437 0 0 0 0 .876h.568c.04 0 .072.033.072.073v.39a.437.437 0 1 0 .871 0v-.4c0-.038.03-.07.067-.073A1.4 1.4 0 0 0 9.43 8.081a1.397 1.397 0 0 0-.88-1.303Z"
  })));
};

var _g;

function _extends$B() { _extends$B = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$B.apply(this, arguments); }

var SvgDocumentProcessing = function SvgDocumentProcessing(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$B({
    viewBox: "0 0 120 120",
    "data-name": "RoadieUI_icon_DocumentProcessing"
  }, props), _g || (_g = /*#__PURE__*/React.createElement("g", {
    className: "icon-stroke",
    fill: "none",
    strokeWidth: 4,
    strokeLinecap: "round",
    strokeLinejoin: "round"
  }, /*#__PURE__*/React.createElement("path", {
    d: "M18 33.02h22.52M18 48.02h40.02M18 63.03h30.01M18 78.03h25.01m10.02 30.02H5.94A2.94 2.94 0 0 1 3 105.11V5.94A2.94 2.94 0 0 1 5.94 3h50.87c.78 0 1.53.31 2.08.86l23.29 23.29c.55.55.86 1.3.86 2.08v18.8M58.03 3v25.01h25.01"
  }), /*#__PURE__*/React.createElement("path", {
    className: "rotation",
    d: "m118 89.56-8.84 8.85-8.85-8.85M77.32 66.57c8.36-4 19.6-1.62 26.53 5.3 6.93 6.94 9.3 18.18 5.3 26.54M57.87 86.03l8.84-8.85 8.84 8.85m23 22.99c-8.37 4-19.6 1.62-26.53-5.3-6.93-6.94-9.31-18.17-5.31-26.54"
  }))));
};

var _path$A;

function _extends$A() { _extends$A = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$A.apply(this, arguments); }

var SvgDocumentSmall = function SvgDocumentSmall(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$A({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_DocumentSmall"
  }, props), _path$A || (_path$A = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M7.385 23H4.462A1.464 1.464 0 0 1 3 21.533V2.467C3 1.657 3.654 1 4.462 1h10.356c.388 0 .76.154 1.034.43l5.72 5.74c.274.276.428.648.428 1.037v13.326c.006.978-.482 1.467-1.462 1.467H7.385Zm-1.137-8.311h9.094M6.248 18.6h5.196 M22 8.333h-5.846a1.464 1.464 0 0 1-1.462-1.466V1"
  })));
};

var _path$z;

function _extends$z() { _extends$z = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$z.apply(this, arguments); }

var SvgDocumentSmaller = function SvgDocumentSmaller(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$z({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_DocumentSmaller"
  }, props), _path$z || (_path$z = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeLinecap: "round",
    strokeWidth: 1.2,
    fill: "none",
    d: "M6.08 20C5.48 20 5 19.52 5 18.93V5.07C5 4.47 5.48 4 6.08 4h7.63c.28 0 .56.11.76.31l4.21 4.18c.2.2.32.47.32.75v9.7c0 .7-.35 1.06-1.08 1.06ZM7.39 13.96h6.7M7.39 16.8h3.83M19 9.33h-4.3c-.6 0-1.08-.47-1.08-1.06V4"
  })));
};

var _path$y;

function _extends$y() { _extends$y = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$y.apply(this, arguments); }

var SvgDownload = function SvgDownload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$y({
    viewBox: "0 0 132 130",
    "data-name": "RoadieUI_icon_Download"
  }, props), _path$y || (_path$y = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M113.87 47.12A41.03 41.03 0 0035.35 30.7C15.86 33.02 1.38 49.73 2.02 69.2c.64 19.47 16.2 35.21 35.8 36.23-.9-2.51-.9-5.26 0-7.77A29.82 29.82 0 0110.23 67.8a29.83 29.83 0 0130.64-29.46A33.01 33.01 0 0173.1 14.9c17.77-.4 34.54 13.48 32.78 37.01a23.28 23.28 0 0115.84 27.47A23.46 23.46 0 0198.48 97.66h-5.05c.9 2.51.9 5.26 0 7.77h5.01a31.3 31.3 0 0030.53-23.21 31 31 0 00-15.14-35.1ZM69.58 109.07V61.42a1 1 0 00-7.83 0v47.65L51.68 99.1a1 1 0 00-5.6 5.48L65.66 124l19.58-19.43a1 1 0 00-5.6-5.52Z"
  })));
};

var _path$x;

function _extends$x() { _extends$x = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$x.apply(this, arguments); }

var SvgFilter = function SvgFilter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$x({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Filter"
  }, props), _path$x || (_path$x = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M14.74 20v-7.42l5.97-5.92.1-.27c.13-.26.19-.46.19-.6v-.91a.85.85 0 00-.88-.87H3.94a.88.88 0 00-.94.87v.85c0 .17.03.34.1.5a.8.8 0 00.25.37l5.98 5.98v4.6l1.8.87v-5.88c0-.3-.1-.5-.3-.62L5.05 5.73h13.97l-5.8 5.88a1.11 1.11 0 00-.3.62v6.9Z"
  })));
};

var _path$w;

function _extends$w() { _extends$w = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$w.apply(this, arguments); }

var SvgGasPump = function SvgGasPump(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$w({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_GasPump"
  }, props), _path$w || (_path$w = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "m16.371 5.333-.763.91 1.897 1.752v5.58a.415.415 0 0 1-.12.3.388.388 0 0 1-.292.117c-.217-.005-.388-.199-.388-.422l.037-3.378c.009-.895-.676-1.63-1.532-1.645h-.089v-2.73H8.5l1.009 1.055h4.516v2.72h-1.911l3.017 3.156V9.757h.07a.414.414 0 0 1 .393.42l-.032 3.016 1.888 1.975c.277-.063.536-.209.745-.421.305-.31.472-.726.472-1.172V7.448L16.37 5.333ZM7.833 8.25l7.292 7.074v3.342H7.833z"
  })));
};

var _path$v;

function _extends$v() { _extends$v = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$v.apply(this, arguments); }

var SvgLicense = function SvgLicense(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$v({
    viewBox: "0 0 48 48",
    "data-name": "RoadieUI_icon_License"
  }, props), _path$v || (_path$v = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M41.52 8.4H6.46A6.46 6.46 0 000 14.83v18.34a6.46 6.46 0 006.46 6.43h35.08A6.45 6.45 0 0048 33.17V14.83a6.46 6.46 0 00-6.48-6.43Zm3.65 24.77a3.63 3.63 0 01-3.63 3.61H6.46a3.63 3.63 0 01-3.63-3.61V14.83a3.63 3.63 0 013.63-3.61h35.08a3.63 3.63 0 013.63 3.61v18.34ZM14 25.95c-3.49 0-6.46 1.25-7.55 3.01-.29.48-.45.96-.45 1.49 0 .75.6 1.36 1.34 1.36h13.32c.73 0 1.34-.61 1.34-1.36 0-.51-.16-1.01-.45-1.49-1.07-1.76-4.05-3-7.55-3Zm-2.91-6.33a3.55 3.55 0 007.1 0 3.55 3.55 0 00-7.1 0m29.45-1.48a1.46 1.46 0 010 2.93H27.46a1.46 1.46 0 010-2.93Zm-6 7.81a1.46 1.46 0 010 2.93h-7.08a1.46 1.46 0 010-2.93Z"
  })));
};

var _path$u;

function _extends$u() { _extends$u = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$u.apply(this, arguments); }

var SvgLoader = function SvgLoader(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$u({
    viewBox: "0 0 50 50",
    "data-name": "RoadieUI_icon_Loader"
  }, props), _path$u || (_path$u = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M43.935 25.145c0-10.318-8.364-18.683-18.683-18.683-10.318 0-18.683 8.365-18.683 18.683h4.068c0-8.071 6.543-14.615 14.615-14.615s14.615 6.543 14.615 14.615h4.068z"
  }, /*#__PURE__*/React.createElement("animateTransform", {
    attributeType: "xml",
    attributeName: "transform",
    type: "rotate",
    from: "0 25.252 25.145",
    to: "360 25.252 25.145",
    dur: "0.6s",
    repeatCount: "indefinite"
  }))));
};

var _path$t;

function _extends$t() { _extends$t = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$t.apply(this, arguments); }

var SvgLogo = function SvgLogo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$t({
    viewBox: "0 0 100 100",
    "data-name": "RoadieUI_icon_Logo"
  }, props), _path$t || (_path$t = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M49.66 0C22.6.006.523 21.666 0 48.72h29.52V27.4a.71.71 0 0 1 .72-.72h22.05c20.7 0 19.07 14.42 19.07 14.42 0 8.37-5.32 12.07-12.44 12.07H.11c0 .55.07 1.1.13 1.65h56.64a6.73 6.73 0 0 1 4.94 2.35l.47.65 11.54 16h-14.7a1.44 1.44 0 0 1-1.11-.6l-8.91-13.44a3.62 3.62 0 0 0-.41-.56h-5.41a.74.74 0 0 0-.45.68v13.14a.71.71 0 0 1-.72.72H30.29a.71.71 0 0 1-.72-.72V59.22H.9c5.03 25.708 29.157 43.093 55.136 39.729 25.978-3.365 44.881-26.322 43.197-52.464C97.548 20.345 75.855.002 49.66 0Z M51.67 48.72c4.54 0 7.16-2.2 7.16-5.71v-.14c0-3.78-2.75-5.71-7.22-5.71h-8a.71.71 0 0 0-.72.72V48a.71.71 0 0 0 .72.72h8.06Z"
  })));
};

var _path$s;

function _extends$s() { _extends$s = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$s.apply(this, arguments); }

var SvgMagnifyingGlass = function SvgMagnifyingGlass(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$s({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_MagnifyingGlass"
  }, props), _path$s || (_path$s = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M15.864 14.32h-.813l-.288-.277A6.66 6.66 0 0 0 16.38 9.69a6.69 6.69 0 1 0-6.69 6.69 6.66 6.66 0 0 0 4.354-1.617l.278.288v.813L19.467 21 21 19.467l-5.136-5.146Zm-6.174 0a4.625 4.625 0 0 1-4.632-4.63A4.625 4.625 0 0 1 9.69 5.058a4.625 4.625 0 0 1 4.63 4.632 4.625 4.625 0 0 1-4.63 4.63Z"
  })));
};

var _path$r;

function _extends$r() { _extends$r = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$r.apply(this, arguments); }

var SvgMap = function SvgMap(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$r({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Map"
  }, props), _path$r || (_path$r = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M15.26 5.17 8.74 3.22 3 5.17V21l5.77-1.98 6.53 1.96 5.7-2.15V3ZM8.2 18.03l-4.07 1.4V5.97L8.21 4.6Zm6.51 1.6-5.38-1.62V4.57l5.38 1.61Zm5.16-1.58-4.03 1.52V6.15l4.03-1.53Z"
  })));
};

var _path$q;

function _extends$q() { _extends$q = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$q.apply(this, arguments); }

var SvgMenu = function SvgMenu(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$q({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Menu"
  }, props), _path$q || (_path$q = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeLinecap: "round",
    d: "M21 7H3m18 5H3m18 5H3"
  })));
};

var _path$p;

function _extends$p() { _extends$p = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$p.apply(this, arguments); }

var SvgMenuSquared = function SvgMenuSquared(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$p({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_MenuSquared"
  }, props), _path$p || (_path$p = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M2 19h20v-2.17H2Zm0-5.42h20v-2.16H2ZM2 6v2.17h20V6Z"
  })));
};

var _path$o;

function _extends$o() { _extends$o = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$o.apply(this, arguments); }

var SvgNo = function SvgNo(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$o({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_No"
  }, props), _path$o || (_path$o = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M12 2C6.487 2 2 6.487 2 12s4.487 10 10 10 10-4.487 10-10S17.513 2 12 2ZM3.182 12c0-2.217.823-4.246 2.18-5.801l12.435 12.435a8.778 8.778 0 0 1-5.802 2.18C7.14 20.817 3.182 16.86 3.182 12Zm15.452 5.801L6.199 5.366A8.778 8.778 0 0 1 12 3.186c4.86 0 8.818 3.958 8.818 8.819a8.765 8.765 0 0 1-2.184 5.796Z"
  })));
};

var _path$n;

function _extends$n() { _extends$n = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$n.apply(this, arguments); }

var SvgOpenInNewTab = function SvgOpenInNewTab(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$n({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_OpenInNewTab"
  }, props), _path$n || (_path$n = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M19 19H5V5h7V3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2v-7h-2v7ZM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7Z"
  })));
};

var _path$m;

function _extends$m() { _extends$m = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$m.apply(this, arguments); }

var SvgPencil = function SvgPencil(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$m({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Pencil"
  }, props), _path$m || (_path$m = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M17 4.21c-.4-.4-1.06-.4-1.46 0L5.14 14.6l-.95 4.1a1.03 1.03 0 001 1.25h.21L9.54 19l10.4-10.38c.4-.4.4-1.06 0-1.46ZM9.05 18.1l-3.89.81.88-3.8 7.79-7.76 3 3Zm8.45-8.48-3-3 1.74-1.73 2.95 3Z"
  })));
};

var _path$l;

function _extends$l() { _extends$l = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$l.apply(this, arguments); }

var SvgPerson = function SvgPerson(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$l({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Person"
  }, props), _path$l || (_path$l = /*#__PURE__*/React.createElement("path", {
    d: "M15.23 8.23a1 1 0 00-6.46 0 1 1 0 006.46 0ZM4.5 18.93v-2.78c.03-.25.19-.55.54-.86.36-.32.9-.63 1.58-.9a15.7 15.7 0 015.38-.86c2.16 0 4.1.35 5.47.9.69.28 1.21.6 1.56.92.34.33.47.62.47.88v2.7Z",
    className: "icon-stroke",
    fill: "none"
  })));
};

var _path$k;

function _extends$k() { _extends$k = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$k.apply(this, arguments); }

var SvgPhone = function SvgPhone(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$k({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Phone"
  }, props), _path$k || (_path$k = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M3.635 6.29a1.8 1.8 0 0 1 2.545 0l2.122 2.12a1.8 1.8 0 0 1 .022 2.523l-.066.073a.6.6 0 0 0 .044.799l3.393 3.394c.235.234.615.234.873-.023l.127-.112a1.8 1.8 0 0 1 2.395.135l2.122 2.12a1.8 1.8 0 0 1 0 2.547l-.503.503a4.204 4.204 0 0 1-5.24.565 33.116 33.116 0 0 1-8.906-8.907 4.203 4.203 0 0 1 .569-5.234Zm1.697.849a.6.6 0 0 0-.848 0l-.504.503a2.999 2.999 0 0 0-.414 3.727 31.883 31.883 0 0 0 8.561 8.562 3 3 0 0 0 3.733-.41l.504-.504a.6.6 0 0 0 0-.849l-2.123-2.12a.6.6 0 0 0-.424-.176.591.591 0 0 0-.348.108l-.077.068a1.8 1.8 0 0 1-2.545 0l-3.394-3.395a1.81 1.81 0 0 1-.11-2.424l.11-.121a.6.6 0 0 0 0-.849Zm15.593-4.563a.6.6 0 0 1 0 .848l-7.8 7.8a.6.6 0 0 1-.848-.849l7.8-7.8a.6.6 0 0 1 .848 0ZM20.5 2.4a.6.6 0 0 1 .6.6v4.8a.6.6 0 0 1-1.2 0V3.599l-4.2.001a.6.6 0 0 1-.591-.503L15.1 3a.6.6 0 0 1 .6-.6h4.8Z"
  })));
};

var _path$j;

function _extends$j() { _extends$j = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$j.apply(this, arguments); }

var SvgPinUnderlined = function SvgPinUnderlined(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$j({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_PinUnderlined"
  }, props), _path$j || (_path$j = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M16.5 9a1 1 0 00-9 0c0 3.38 4.5 8.25 4.5 8.25s4.5-4.88 4.5-8.25Zm-6 0a1 1 0 113 0 1 1 0 01-3 0Zm-3.75 9v1.5h10.5V18Z"
  })));
};

var _path$i;

function _extends$i() { _extends$i = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$i.apply(this, arguments); }

var SvgPackage = function SvgPackage(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$i({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Package"
  }, props), _path$i || (_path$i = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M16.683 2.32H7.316a1.462 1.462 0 0 0-1.192.612L2.592 7.878a1.464 1.464 0 0 0-.273.85v11.49c0 .808.655 1.463 1.464 1.463h16.434c.809 0 1.464-.655 1.464-1.464V8.73c0-.305-.095-.602-.272-.85l-3.533-4.947a1.463 1.463 0 0 0-1.193-.613ZM7.315 3.68h9.369c.034 0 .065.017.084.043L20.3 8.67a.102.102 0 0 1 .018.059v11.488a.102.102 0 0 1-.102.102H3.783a.102.102 0 0 1-.102-.102V8.73a.1.1 0 0 1 .019-.06l3.532-4.945a.102.102 0 0 1 .083-.043Zm5.358-.78h-1.354v5.478h1.354Zm5.583 12.724h-5.467c-.595 0-1.078.482-1.078 1.077v1.554c0 .595.483 1.078 1.078 1.078h5.467c.595 0 1.078-.483 1.078-1.078v-1.554c0-.595-.483-1.077-1.078-1.077Zm-.284 1.361v.986h-4.9v-.986h4.9Zm2.987-9.187H2.94v1.354h18.019Z"
  })));
};

var _path$h;

function _extends$h() { _extends$h = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$h.apply(this, arguments); }

var SvgPackageDelivery = function SvgPackageDelivery(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$h({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_PackageDelivery"
  }, props), _path$h || (_path$h = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeLinecap: "round",
    strokeWidth: 1.5,
    fill: "none",
    d: "M11.25 17.25a6 6 0 1112 0 6 6 0 01-12 0m6 3v-6m0 6.25L15 18.25m2.25 2.25 2.25-2.25M8.25 18.75h-6a1.5 1.5 0 01-1.5-1.5V6.75c0-.83.67-1.5 1.5-1.5h15c.83 0 1.5.67 1.5 1.5v1.5M18.27 5.65l-2.82-4.23A1.5 1.5 0 0014.2.75H5.3c-.5 0-.97.25-1.25.67L1.23 5.65M9.75.75v4.5"
  })));
};

var _path$g;

function _extends$g() { _extends$g = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$g.apply(this, arguments); }

var SvgPackagePickup = function SvgPackagePickup(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$g({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_PackagePickup"
  }, props), _path$g || (_path$g = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeLinecap: "round",
    strokeWidth: 1.5,
    fill: "none",
    d: "M11.25 17.25a1 1 0 0112 0 1 1 0 01-12 0m6 3v-6m0 0L15 16.5m2.25-2.25 2.25 2.25M8.25 18.75h-6a1.5 1.5 0 01-1.5-1.5V6.75c0-.83.67-1.5 1.5-1.5h15c.83 0 1.5.67 1.5 1.5v1.5M18.27 5.65l-2.82-4.23A1.5 1.5 0 0014.2.75H5.3c-.5 0-.97.25-1.25.67L1.23 5.65M9.75.75v4.5"
  })));
};

var _path$f;

function _extends$f() { _extends$f = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$f.apply(this, arguments); }

var SvgPackageStack = function SvgPackageStack(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$f({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_PackageStack"
  }, props), _path$f || (_path$f = /*#__PURE__*/React.createElement("path", {
    d: "M16.78 10.66V5.51a.56.56 0 00-.34-.51l-4.22-1.95a.56.56 0 00-.47 0L7.53 5a.56.56 0 00-.32.51v5.16l-3.88 1.79a.56.56 0 00-.33.51v5.52a.56.56 0 00.33.51l4.21 1.95a.56.56 0 00.48 0L12 19.1l3.98 1.84a.56.56 0 00.47 0L20.67 19a.56.56 0 00.33-.51v-5.52a.56.56 0 00-.32-.51Zm2.32 2.31-2.88 1.32-2.87-1.32 2.87-1.32ZM12 4.18l2.87 1.33L12 6.83 9.13 5.51ZM7.78 11.64l2.88 1.33-2.88 1.32L4.91 12.97Zm0 8.18-3.66-1.7v-4.27l3.42 1.56a.56.56 0 00.47 0l3.42-1.57v4.28Zm.56-9.16V6.4l3.42 1.58a.56.56 0 00.48 0l3.42-1.58v4.28L12 12.36Zm11.54 7.47-3.66 1.69-3.66-1.7v-4.27L16 15.4a.56.56 0 00.45 0l3.42-1.57Z",
    className: "icon-fill"
  })));
};

var _path$e;

function _extends$e() { _extends$e = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$e.apply(this, arguments); }

var SvgParkingMeter = function SvgParkingMeter(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$e({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_ParkingMeter"
  }, props), _path$e || (_path$e = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M11.998 5.366c-1.938 0-3.547 1.489-3.806 3.416a3.822 3.822 0 0 0 0 1.072H15.8a3.82 3.82 0 0 0 .034-.536c0-.18-.013-.36-.034-.536-.25-1.927-1.859-3.416-3.801-3.416Zm.854 3.416.5-1.039-.933-.476-.73 1.515H9.251c.246-1.335 1.384-2.343 2.747-2.343s2.505 1.012 2.75 2.343h-1.896Zm-.854 3.48c-.821 0-1.492.686-1.492 1.536s.667 1.536 1.492 1.536 1.492-.686 1.492-1.536-.667-1.536-1.492-1.536Zm0 1.995a.458.458 0 0 1-.45-.463c0-.254.2-.464.45-.464s.45.206.45.464c0 .257-.2.463-.45.463Z M11.998 3.375C8.693 3.375 6 6.147 6 9.55c0 .189.008.373.025.553l.413 1.764 1.338 5.698h3.7v3.06h1.043v-3.06h3.701l1.338-5.698.417-1.764c.017-.184.025-.369.025-.553 0-3.403-2.693-6.175-6.002-6.175Zm4.947 6.48-.404 1.733-.613 2.622-.533 2.283H8.6l-.534-2.283-.612-2.622-.405-1.734a7.228 7.228 0 0 1-.008-.304c-.004-2.815 2.217-5.102 4.956-5.102 2.738 0 4.956 2.287 4.956 5.102.004.103 0 .201-.009.304Z"
  })));
};

var _path$d;

function _extends$d() { _extends$d = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$d.apply(this, arguments); }

var SvgPlus = function SvgPlus(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$d({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Plus"
  }, props), _path$d || (_path$d = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M10.71 3v7.71H3v2.58h7.71V21h2.58v-7.71H21v-2.58h-7.71V3z"
  })));
};

var _path$c;

function _extends$c() { _extends$c = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$c.apply(this, arguments); }

var SvgRoadieUpsCompany = function SvgRoadieUpsCompany(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$c({
    viewBox: "0 0 375.04 103.05",
    "data-name": "RoadieUI_icon_RoadieUpsCompany"
  }, props), _path$c || (_path$c = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M127.53 44.86c0 2.86-2.13 4.65-5.83 4.65h-6.58a.57.57 0 0 1-.58-.58v-8.24a.57.57 0 0 1 .58-.58h6.53c3.64 0 5.88 1.56 5.88 4.64Zm12.21 25-9.35-13.66c4.87-2.07 8.06-6 8.06-12v-.11A12.19 12.19 0 0 0 135 35c-2.63-2.68-6.78-4.25-12.77-4.25h-17.97a.57.57 0 0 0-.58.58v38a.57.57 0 0 0 .58.58H114a.57.57 0 0 0 .59-.58V58.61a.57.57 0 0 1 .58-.59h4.12s.06 0 .38.49l7.25 10.9a1.16 1.16 0 0 0 .91.48Zm42.38-19.48c0 5.65-4 10.47-10 10.47s-10-4.93-10-10.58v-.12c0-5.65 4-10.47 9.91-10.47s10.07 4.93 10.07 10.59Zm11.09-.11v-.12c0-11.25-9-20.27-21.05-20.27S151 39 151 50.3v.11c0 11.25 9 20.27 21.06 20.27s21.16-9.13 21.16-20.38m34.38 3.59s.22.55-.37.55h-7.61s-.59 0-.38-.55l4-10s.21-.54.43 0Zm6 15.46a.91.91 0 0 0 .81.54h10.47s.58 0 .36-.54L229 31a.92.92 0 0 0-.81-.54h-9.31a.92.92 0 0 0-.81.54l-16.24 38.35s-.22.54.36.54h10.25a.9.9 0 0 0 .8-.54l2.37-5.91a.9.9 0 0 1 .8-.54h14a.92.92 0 0 1 .81.54Zm50.17-19c0 6.33-4.31 9.91-10.81 9.91h-3.86a.57.57 0 0 1-.58-.58V40.91a.57.57 0 0 1 .58-.58h3.84c6.5 0 10.81 3.64 10.81 9.91Zm11.14-.17v-.11c0-11.31-8.12-19.37-22.12-19.37h-14.55a.57.57 0 0 0-.58.58v38a.57.57 0 0 0 .58.58h14.31c14.11 0 22.34-8.4 22.34-19.71m14.23 19.13a.57.57 0 0 0 .58.58h9.75a.57.57 0 0 0 .58-.58v-38a.57.57 0 0 0-.58-.58h-9.75a.57.57 0 0 0-.58.58Zm37.82-8.66a.57.57 0 0 1-.59-.58V55a.57.57 0 0 1 .59-.58h17.64a.58.58 0 0 0 .59-.59v-7.37a.58.58 0 0 0-.59-.59h-17.64a.57.57 0 0 1-.59-.58v-4.77a.57.57 0 0 1 .59-.58h19.6a.58.58 0 0 0 .59-.59v-8.07a.57.57 0 0 0-.59-.58h-30.35a.57.57 0 0 0-.59.58v38a.57.57 0 0 0 .59.58h30.63a.57.57 0 0 0 .59-.58v-8.04a.58.58 0 0 0-.59-.59ZM49.23 7.82A42.46 42.46 0 0 0 6.78 49.47H32V31.23a.6.6 0 0 1 .61-.62h18.87c17.7 0 16.31 12.33 16.31 12.33 0 7.15-4.56 10.31-10.64 10.31H6.87c0 .48.06 1 .11 1.42h48.43a5.73 5.73 0 0 1 4.22 2l.4.55 9.86 13.64H57.33a1.23 1.23 0 0 1-.95-.51l-7.61-11.43a3.21 3.21 0 0 0-.36-.47h-4.6a.63.63 0 0 0-.38.58v11.24a.6.6 0 0 1-.62.61H32.63a.6.6 0 0 1-.61-.61V58.45H7.55A42.48 42.48 0 1 0 49.23 7.82ZM51 49.47c3.89 0 6.12-1.88 6.12-4.88v-.12c0-3.23-2.35-4.88-6.17-4.88H44a.6.6 0 0 0-.61.62v8.65a.6.6 0 0 0 .61.61Zm146.91 28.19h2.38l6.45 14.76H204l-1.49-3.54h-6.91l-1.5 3.54h-2.64Zm3.68 8.94-2.53-5.86-2.51 5.86Zm14.33-.4v-8.43h2.58v8.33c0 2.72 1.4 4.18 3.7 4.18s3.68-1.38 3.68-4.08v-8.43h2.58v8.31c0 4.37-2.47 6.57-6.3 6.57s-6.24-2.2-6.24-6.45Zm16.66-8.43h5.77c3.42 0 5.57 1.94 5.57 4.9 0 3.28-2.64 5-5.86 5h-2.91v4.71h-2.57Zm5.56 7.62c2 0 3.17-1.09 3.17-2.62 0-1.72-1.24-2.62-3.17-2.62h-3v5.28Zm7.86 4.89 1.55-1.84a6.71 6.71 0 0 0 4.62 1.91c1.59 0 2.6-.74 2.6-1.85 0-1-.59-1.61-3.31-2.24-3.12-.75-4.88-1.67-4.88-4.37 0-2.51 2.1-4.25 5-4.25a8.12 8.12 0 0 1 5.31 1.84l-1.38 1.95a6.63 6.63 0 0 0-4-1.51c-1.5 0-2.38.77-2.38 1.74 0 1.13.67 1.63 3.47 2.3 3.1.75 4.71 1.86 4.71 4.29 0 2.74-2.15 4.38-5.23 4.38a9 9 0 0 1-6.08-2.35Zm20.61-5.15a7.41 7.41 0 0 1 7.56-7.57 7.62 7.62 0 0 1 5.73 2.28l-1.65 1.9a5.82 5.82 0 0 0-4.1-1.82 4.92 4.92 0 0 0-4.84 5.17 5 5 0 0 0 4.84 5.21 5.72 5.72 0 0 0 4.2-1.9l1.65 1.67a7.58 7.58 0 0 1-5.95 2.6 7.37 7.37 0 0 1-7.44-7.54ZM282 87a5.89 5.89 0 0 1 11.78 0 5.78 5.78 0 0 1-5.92 5.76A5.72 5.72 0 0 1 282 87Zm9.25 0a3.41 3.41 0 0 0-3.39-3.55 3.32 3.32 0 0 0-3.33 3.51 3.43 3.43 0 0 0 3.37 3.56 3.33 3.33 0 0 0 3.3-3.52Zm5.37-5.63h2.53V83a4 4 0 0 1 3.37-1.9 3.48 3.48 0 0 1 3.29 1.94 4.51 4.51 0 0 1 3.74-1.94c2.41 0 3.88 1.53 3.88 4.23v7h-2.54v-6.19c0-1.76-.81-2.7-2.24-2.7s-2.36 1-2.36 2.74v6.24h-2.53v-6.3c0-1.72-.84-2.68-2.24-2.68s-2.37 1-2.37 2.76v6.22h-2.53Zm20.28 0h2.53v1.84a4.43 4.43 0 0 1 3.75-2.07c2.61 0 5.15 2.07 5.15 5.73 0 3.66-2.51 5.74-5.15 5.74a4.51 4.51 0 0 1-3.75-2v5.07h-2.53Zm8.85 5.54a3.27 3.27 0 0 0-3.16-3.53 3.33 3.33 0 0 0-3.22 3.53 3.32 3.32 0 0 0 3.22 3.54 3.23 3.23 0 0 0 3.16-3.54ZM338 92.42v-1.36a4.45 4.45 0 0 1-3.58 1.59c-2.09 0-3.93-1.2-3.93-3.41 0-2.44 1.9-3.62 4.48-3.62A9.33 9.33 0 0 1 338 86v-.21c0-1.52-1-2.36-2.72-2.36a7.67 7.67 0 0 0-3.16.69l-.69-2a9.54 9.54 0 0 1 4.21-.92c3.26 0 4.85 1.72 4.85 4.67v6.55Zm.06-4.63a7.27 7.27 0 0 0-2.53-.44c-1.59 0-2.53.65-2.53 1.72 0 1 .94 1.63 2.13 1.63 1.68 0 2.93-.94 2.93-2.32Zm5.83-6.42h2.53v1.71a4 4 0 0 1 3.45-1.94c2.49 0 3.94 1.67 3.94 4.25v7h-2.54v-6.25c0-1.72-.85-2.7-2.36-2.7a2.45 2.45 0 0 0-2.49 2.74v6.24h-2.53Zm12.4 13.79.85-1.88a3.18 3.18 0 0 0 1.49.41c.71 0 1.15-.31 1.57-1.27l-4.6-11h2.7l3.13 8.2 2.87-8.2h2.64l-4.4 11.36c-.88 2.26-1.86 3.1-3.64 3.1a5.1 5.1 0 0 1-2.61-.72Z"
  })));
};

var _path$b;

function _extends$b() { _extends$b = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$b.apply(this, arguments); }

var SvgShippingBox = function SvgShippingBox(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$b({
    viewBox: "0 0 40 40",
    "data-name": "RoadieUI_icon_ShippingBox"
  }, props), _path$b || (_path$b = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    strokeLinejoin: "round",
    strokeWidth: 1.583,
    d: "M39.167 10.233c0-.663-.393-1.263-1-1.528l-17.5-7.61a1.677 1.677 0 0 0-1.334 0l-17.5 7.61c-.607.265-1 .865-1 1.528v19.462c0 .673.405 1.28 1.025 1.538l18.309 7.629c.428.178.91.17 1.333-.022l16.69-7.59c.595-.27.977-.863.977-1.517v-19.5zm-18.334 7.24L1.142 9.268m19.691 8.205 18.032-8.196M11.978 4.298l18.502 8.797M15.833 21.56a.833.833 0 0 0-.5-.763L7 17.15a.833.833 0 0 0-1.167.765v6.513c0 .332.197.631.5.764l8.334 3.646a.833.833 0 0 0 1.166-.763V21.56zm5 17.483v-21.57"
  })));
};

var _path$a;

function _extends$a() { _extends$a = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$a.apply(this, arguments); }

var SvgSort = function SvgSort(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$a({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Sort"
  }, props), _path$a || (_path$a = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M2 18h6.667v-2.167H2V18ZM2 5v2.167h20V5H2Zm0 7.583h13.333v-2.166H2v2.166Z"
  })));
};

var _path$9;

function _extends$9() { _extends$9 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$9.apply(this, arguments); }

var SvgSteeringWheel = function SvgSteeringWheel(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$9({
    viewBox: "0 0 40 40",
    "data-name": "RoadieUI_icon_SteeringWheel"
  }, props), _path$9 || (_path$9 = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    strokeWidth: 1.583,
    d: "M.833 20a19.167 19.167 0 1 0 38.334 0A19.167 19.167 0 1 0 .833 20m5.537-3.773a46.122 46.122 0 0 1 27.26 0M5.833 20a14.167 14.167 0 1 0 28.334 0 14.167 14.167 0 1 0-28.334 0M17.5 20a2.5 2.5 0 1 0 5 0 2.5 2.5 0 1 0-5 0m-11.613.862c.258-.019.518-.029.78-.029 5.983 0 10.833 4.85 10.833 10.834 0 .752-.08 1.501-.238 2.236m5.47 0a9.603 9.603 0 0 1-.232-2.236c0-5.983 4.85-10.834 10.833-10.834.3 0 .512.045.804.07"
  })));
};

var _path$8;

function _extends$8() { _extends$8 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$8.apply(this, arguments); }

var SvgToll = function SvgToll(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$8({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_Toll"
  }, props), _path$8 || (_path$8 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M15.716 10.208 6.326 3.75l-.607.86 9.301 6.399V21h5.23V10.208h-4.534Zm3.474 1.048v1.718h-3.11v-1.718h3.11Zm-3.11 8.696v-5.93h3.11v5.93h-3.11Zm-4.292-6.957-2.197-3.78-6.587-.034L3 10.23l5.972.034 1.582 2.711H3v1.048h8.47c.76 0 1.379.612 1.379 1.362v2.46H3v1.048h6.528v.532c0 .868.716 1.576 1.594 1.576.883 0 1.595-.708 1.595-1.58v-.528h1.192v-3.508c0-1.22-.925-2.234-2.12-2.389Zm-.131 6.425a.532.532 0 0 1-.535.528.532.532 0 0 1-.534-.528v-.528h1.069v.528Zm-1.535-3.487a1 1 0 1 0 2 0 1 1 0 1 0-2 0"
  })));
};

var _path$7;

function _extends$7() { _extends$7 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$7.apply(this, arguments); }

var SvgTooltipNotch = function SvgTooltipNotch(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$7({
    viewBox: "0 0 30 30",
    "data-name": "RoadieUI_icon_TooltipNotch"
  }, props), _path$7 || (_path$7 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M0 0c15 25 15 25 30 0Z"
  })));
};

var _path$6;

function _extends$6() { _extends$6 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$6.apply(this, arguments); }

var SvgTriangleDown = function SvgTriangleDown(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$6({
    viewBox: "0 0 531.74 531.74",
    "data-name": "RoadieUI_icon_TriangleDown"
  }, props), _path$6 || (_path$6 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "m530.874.5-265.004 459L.866.5z"
  })));
};

var _path$5;

function _extends$5() { _extends$5 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$5.apply(this, arguments); }

var SvgUpload = function SvgUpload(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$5({
    viewBox: "0 0 132 130",
    "data-name": "RoadieUI_icon_Upload"
  }, props), _path$5 || (_path$5 = /*#__PURE__*/React.createElement("path", {
    className: "icon-fill",
    d: "M113.9 47.14A39.95 39.95 0 0073.19 7 40.82 40.82 0 0035.77 30.73 37.91 37.91 0 002 68.14a37.55 37.55 0 0037.61 37.34h14.62v-7.79H39.61a29.78 29.78 0 01-28.2-29.21 29.78 29.78 0 0129.84-30.13A32.86 32.86 0 0173.33 14.9a32.16 32.16 0 0132.63 37.02l2.53 1.17a23.32 23.32 0 01-9.9 44.6H77.62v7.78h20.97a31.17 31.17 0 0030.39-23.22 31.08 31.08 0 00-15.07-35.11ZM65.92 55.46 46.44 74.91a1 1 0 005.5 5.48l10.09-9.92v47.64a1 1 0 007.8 0V70.47l10.01 10a1 1 0 005.58-5.56Z"
  })));
};

var _path$4;

function _extends$4() { _extends$4 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$4.apply(this, arguments); }

var SvgVehicleCar = function SvgVehicleCar(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$4({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_VehicleCar"
  }, props), _path$4 || (_path$4 = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeWidth: 1.47,
    fill: "none",
    d: "M15.66 18a1 1.02 0 002.94 0 1 1.02 0 00-2.94 0M3.93 18a1 1.02 0 002.94 0 1 1.02 0 00-2.94 0m11.74 0h-8.8m-2.94 0H2.47C1.66 18 1 17.33 1 16.5V15c0-1.66 1.31-3 2.93-3l1.8-3.67c.25-.5.75-.83 1.31-.83h7.72c.56 0 1.07.32 1.32.83L17.87 12h2.2A2.97 2.97 0 0123 15v1.5c0 .83-.66 1.5-1.47 1.5H18.6m-.73-6H3.93"
  })));
};

var _path$3;

function _extends$3() { _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$3.apply(this, arguments); }

var SvgVehiclePickup = function SvgVehiclePickup(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$3({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_VehiclePickup"
  }, props), _path$3 || (_path$3 = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    fill: "none",
    strokeWidth: 1.47,
    d: "M17.13 18.06a1.02 1 0 002.94 0 1.02 1 0 00-2.94 0m-11.73 0a1.02 1 0 002.94 0 1.02 1 0 00-2.94 0m14.67 0h1.46c.81 0 1.47-.65 1.47-1.45v-1.44c0-1.6-1.31-2.9-2.93-2.9H18.6L16.07 7.3c-.25-.5-.75-.8-1.31-.8h-3.5c-.79 0-1.46.65-1.46 1.44v4.34H2.47c-.81 0-1.47.64-1.47 1.44v2.9c0 .79.66 1.44 1.47 1.44H5.4m11.73 0h-8.8m1.47-5.78h8.8"
  })));
};

var _path$2;

function _extends$2() { _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$2.apply(this, arguments); }

var SvgVehicleSuv = function SvgVehicleSuv(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$2({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_VehicleSuv"
  }, props), _path$2 || (_path$2 = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeWidth: 1.47,
    fill: "none",
    strokeLinecap: "round",
    d: "M15.66 18.03a1 1 0 002.94 0 1 1 0 00-2.94 0m-11.73 0a1 1 0 002.94 0 1 1 0 00-2.94 0m11.74 0h-8.8m-2.94 0H2.47c-.81 0-1.47-.66-1.47-1.47v-3.53c0-.58.17-1.15.49-1.63l2.44-4.41h7.3c.94 0 1.83.46 2.38 1.23l2.79 3.92h3.38a2.93 2.93 0 012.9 2.46l.32 1.96c0 .8-.66 1.47-1.47 1.47H18.6m-2.2-5.89H1.14M6.87 7v5.15M3.93 7h-2.2"
  })));
};

var _path$1;

function _extends$1() { _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends$1.apply(this, arguments); }

var SvgVehicleTruck = function SvgVehicleTruck(props) {
  return /*#__PURE__*/React.createElement("svg", _extends$1({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_VehicleTruck"
  }, props), _path$1 || (_path$1 = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeWidth: 1.47,
    fill: "none",
    strokeLinecap: "round",
    d: "M14.93 18V7.5h5.38a2.95 2.95 0 012.68 3.15v6.3a.98.98 0 01-.9 1.05h-2.03m0 0a1 1.02 0 01-2.94 0 1 1.02 0 012.94 0M8.33 18a1 1.02 0 01-2.94 0 1 1.02 0 012.94 0M23 12h-4.28a.86.86 0 01-.86-.85V7.5M17.13 18h-8.8M23 15h-1.9M1 13.5h13.93V6c0-.83-.66-1.5-1.47-1.5h-11C1.66 4.5 1 5.17 1 6v7.5zM5.4 18H2.47C1.66 18 1 17.33 1 16.5v-3"
  })));
};

var _path;

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

var SvgVehicleVan = function SvgVehicleVan(props) {
  return /*#__PURE__*/React.createElement("svg", _extends({
    viewBox: "0 0 24 24",
    "data-name": "RoadieUI_icon_VehicleVan"
  }, props), _path || (_path = /*#__PURE__*/React.createElement("path", {
    className: "icon-stroke",
    strokeWidth: 1.44,
    fill: "none",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    d: "M18.7 18.09a1.02 1 0 01-2.88 0 1.02 1 0 012.88 0m-11.47 0a1.02 1 0 01-2.88 0 1.02 1 0 012.88 0m8.6 0H7.22M1.48 7.5H12.9c.93 0 1.8.44 2.34 1.18l2.73 3.76h1.87c1.4 0 2.6 1 2.83 2.36l.33 1.88c0 .78-.64 1.4-1.43 1.4H18.7m-14.35.01H1.48v-5.65m16.5 0H1.48M11.52 7.5v10.59M6.5 12.44V7.5m-5.02 4.94V7.5"
  })));
};

const base$1 = block(rule('width'), rule('height'), rule('maxWidth', '100%'), rule('maxHeight', '100%'), rule('color', 'inherit', { parser: color }), rule('cursor'));

const colorize = (c) => {
    return styled(c) `
    .icon-stroke {
      stroke: currentColor;
    }
    .icon-fill {
      fill: currentColor;
    }
  `;
};
function makeIcon(svg) {
    return guitar(colorize(svg), base$1);
}
const allIcons = {
    AlertCircle: makeIcon(SvgAlertCircle),
    ArrowRight: makeIcon(SvgArrowRight),
    ArrowToBarLeft: makeIcon(SvgArrowToBarLeft),
    ArrowToBarRight: makeIcon(SvgArrowToBarRight),
    Bank: makeIcon(SvgBank),
    BlankProfile: makeIcon(SvgBlankProfile),
    Calendar: makeIcon(SvgCalendar),
    Checkmark: makeIcon(SvgCheckmark),
    CheckmarkCircle: makeIcon(SvgCheckmarkCircle),
    ChevronDoubleRight: makeIcon(SvgChevronDoubleRight),
    ChevronDown: makeIcon(SvgChevronDown),
    ChevronLeft: makeIcon(SvgChevronLeft),
    ChevronRight: makeIcon(SvgChevronRight),
    ChevronUp: makeIcon(SvgChevronUp),
    Clock: makeIcon(SvgClock),
    CloseCircle: makeIcon(SvgCloseCircle),
    Cog: makeIcon(SvgCog),
    Close: makeIcon(SvgClose),
    Coins: makeIcon(SvgCoins),
    CreditCard: makeIcon(SvgCreditCard),
    DocumentArrowRight: makeIcon(SvgDocumentArrowRight),
    DocumentComplete: makeIcon(SvgDocumentComplete),
    DocumentFinancial: makeIcon(SvgDocumentFinancial),
    DocumentProcessing: makeIcon(SvgDocumentProcessing),
    DocumentSmall: makeIcon(SvgDocumentSmall),
    DocumentSmaller: makeIcon(SvgDocumentSmaller),
    Download: makeIcon(SvgDownload),
    Filter: makeIcon(SvgFilter),
    GasPump: makeIcon(SvgGasPump),
    License: makeIcon(SvgLicense),
    Loader: makeIcon(SvgLoader),
    Logo: makeIcon(SvgLogo),
    MagnifyingGlass: makeIcon(SvgMagnifyingGlass),
    Map: makeIcon(SvgMap),
    Menu: makeIcon(SvgMenu),
    MenuSquared: makeIcon(SvgMenuSquared),
    No: makeIcon(SvgNo),
    OpenInNewTab: makeIcon(SvgOpenInNewTab),
    Pencil: makeIcon(SvgPencil),
    Person: makeIcon(SvgPerson),
    Phone: makeIcon(SvgPhone),
    PinUnderlined: makeIcon(SvgPinUnderlined),
    Package: makeIcon(SvgPackage),
    PackageDelivery: makeIcon(SvgPackageDelivery),
    PackagePickup: makeIcon(SvgPackagePickup),
    PackageStack: makeIcon(SvgPackageStack),
    ParkingMeter: makeIcon(SvgParkingMeter),
    Plus: makeIcon(SvgPlus),
    RoadieUpsCompany: makeIcon(SvgRoadieUpsCompany),
    ShippingBox: makeIcon(SvgShippingBox),
    Sort: makeIcon(SvgSort),
    SteeringWheel: makeIcon(SvgSteeringWheel),
    Toll: makeIcon(SvgToll),
    TooltipNotch: makeIcon(SvgTooltipNotch),
    TriangleDown: makeIcon(SvgTriangleDown),
    Upload: makeIcon(SvgUpload),
    VehicleCar: makeIcon(SvgVehicleCar),
    VehiclePickup: makeIcon(SvgVehiclePickup),
    VehicleSuv: makeIcon(SvgVehicleSuv),
    VehicleTruck: makeIcon(SvgVehicleTruck),
    VehicleVan: makeIcon(SvgVehicleVan),
};
const Icon = props => {
    const { svg } = props, $props = __rest(props, ["svg"]);
    const SvgIcon = allIcons[svg];
    return jsxRuntime.exports.jsx(SvgIcon, Object.assign({}, $props), void 0);
};
Object.assign(Icon, allIcons);

const image = block(rule('width'), rule('height', 'auto'), rule('maxWidth', '100%'), rule('maxHeight'), rule('borderRadius'), rule('objectFit', 'fill', {
    parser: (value) => () => value
}));

const Image = guitar('img', image);

const base = block(rule('width', '0'), rule('height', '0'));
const inline = block(base, rule('display', 'inline-block'));

const Spacer = guitar('div', base);
const Inline = guitar('span', inline);
Object.assign(Spacer, {
    Inline,
});

function textThemesOnElement(element) {
    return {
        Button: guitar(element, button),
        Caption: guitar(element, caption),
        H1: guitar(element, h1),
        H2: guitar(element, h2),
        H3: guitar(element, h3),
        H4: guitar(element, h4),
        H5: guitar(element, h5),
        H6: guitar(element, h6),
        Caps: guitar(element, caps),
        Subtitle1: guitar(element, subtitle1),
        Subtitle2: guitar(element, subtitle2),
        Body1: guitar(element, body1),
        Body2: guitar(element, body2),
        Overline: guitar(element, overline),
    };
}
const Text = textThemesOnElement('div');
const Span = textThemesOnElement('span');

const primary = Color.define('primary', '#00BBBB', {
    lighter: '#E6F8F8',
    light: '#80DDDD',
    dark: '#008383',
    darker: '#003838',
});
const secondary = Color.define('secondary', '#5680DF', {
    lighter: '#E7EDFA',
    light: '#B7C8F1',
    dark: '#2F4880',
    darker: '#04153D',
});
const grey = Color.define('grey', '#A8A8A8', {
    lighter: '#EAEAEA',
    light: '#D2D2D2',
    dark: '#7A7A7A',
    darker: '#4C4C4C',
});
const info = Color.define('info', '#258afb', {
    lighter: '#d3e8fe',
    light: '#92c5fd',
    dark: '#1a61b0',
    darker: '#0b294b',
});
const success = Color.define('success', '#72bf44', {
    lighter: '#e3f2da',
    light: '#b9dfa2',
    dark: '#508630',
    darker: '#223914',
});
const alert = Color.define('alert', '#fdb913', {
    lighter: '#fff1d0',
    light: '#fedc89',
    dark: '#b1820d',
    darker: '#4c3706',
});
const error = Color.define('error', '#e34025', {
    lighter: '#f9d9d3',
    light: '#f1a092',
    dark: '#9f2d1a',
    darker: '#44130b',
});
const white = Color.define('white', 'white');
const black = Color.define('black', 'black');
const clear$1 = Color.define('clear', 'rgba(0,0,0,0)');
const star = Color.define('star', '#efcd14');
const facebookBlue = Color.define('facebookBlue', '#3B5998');
const background = Color.define('background', '#f7f7f7');

var colors = /*#__PURE__*/Object.freeze({
    __proto__: null,
    primary: primary,
    secondary: secondary,
    grey: grey,
    info: info,
    success: success,
    alert: alert,
    error: error,
    white: white,
    black: black,
    clear: clear$1,
    star: star,
    facebookBlue: facebookBlue,
    background: background
});

const theme = {
    colors,
    buttons: {
        borderRadius: '5px',
    },
    panel: {
        padding: '3.2rem 4rem',
        borderRadius: '5px',
        mobile: {
            padding: '2.4rem',
        }
    },
};

const dark = {
    parent: Object.assign(Object.assign({}, theme), { colorConverter: darkMode }),
};

const parsers = {
    checkTheme,
    color,
    dropShadow: dropShadow$1,
    linearGradient: linearGradient$1
};

const AutoGridContext = createContext({ columnCount: 0, columnWidth: 0, gridGap: [0, 0] });
const useClientRect = (ref) => {
    const [rect, setRect] = useState(ref.current ? ref.current.getBoundingClientRect() : null);
    const prevRect = useRef(rect);
    useLayoutEffect(() => {
        const { current } = ref;
        if (!current) {
            return;
        }
        const observer = new ResizeObserver(entries => {
            const element = entries[0];
            const newRect = element.target.getBoundingClientRect();
            if (prevRect.current && prevRect.current.width === newRect.width) {
                // We don't want to cause a re-render unless the width has changed.
                return;
            }
            prevRect.current = newRect;
            setRect(newRect);
        });
        observer.observe(current);
        return () => {
            observer.unobserve(current);
        };
    }, [ref]);
    return rect;
};
const AutoGrid$1 = props => {
    const { minColumnWidth = 250, maxColumns = 3, children } = props;
    const gridGap = Array.isArray(props.gridGap) ? props.gridGap : [props.gridGap || 0, props.gridGap || 0];
    const [gridGapVertical, gridGapHorizontal] = gridGap;
    const ref = useRef(null);
    const rect = useClientRect(ref);
    const getColumns = useCallback((wanted) => {
        let result = [wanted, 0];
        if (wanted === 1 || !rect) {
            return result;
        }
        else if (rect) {
            const columnGap = 30 * (wanted - 1); // FIXME what is this magic 30? should it be gridGap?
            const columnWidth = (rect.width - columnGap) / wanted;
            if (columnWidth >= minColumnWidth) {
                result = [wanted, columnWidth];
            }
            else {
                result = getColumns(wanted - 1);
            }
        }
        return result;
    }, [minColumnWidth, rect]);
    const [columnCount, columnWidth] = getColumns(maxColumns);
    const cols = Array.from({ length: columnCount }).map(() => '1fr');
    cols[cols.length - 1] = '1fr';
    const template = '[start] ' + cols.join(' ') + ' [end]';
    return (jsxRuntime.exports.jsx(AutoGridContext.Provider, Object.assign({ value: { columnCount, columnWidth, gridGap } }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ ref: ref, "$gridTemplateColumns": template, "$gridGap": `${gridGapVertical}px ${gridGapHorizontal}px`, "$alignItems": "end" }, { children: children }), void 0) }), void 0));
};

const AutoGridCell = props => {
    const { minWidth = 0, columnSpan = 0, columnStart, columnEnd, rowStart, rowEnd, children } = props;
    const grid = useContext(AutoGridContext);
    if (columnStart && columnEnd) {
        if (columnSpan || minWidth) {
            throw new Error(`
        Cannot set columnSpan or minWidth when setting columnStart and columnEnd. 
        Must remove either columnStart or remove columnEnd if using minWidth or columnSpan.
      `);
        }
    }
    const getColumns = useCallback((wanted) => {
        // the total width of the columns + the gaps to span the wanted length
        const totalWidth = (wanted * grid.columnWidth) + (grid.gridGap[1] * (wanted - 1));
        if (totalWidth < minWidth && wanted < grid.columnCount) {
            return getColumns(wanted + 1);
        }
        return wanted;
    }, [grid, minWidth]);
    const span = getColumns(Math.min(columnSpan || 1, grid.columnCount));
    let start = columnStart, end = columnEnd;
    if (columnEnd && !columnStart) {
        start = `span ${span}`;
    }
    else if (!columnEnd) {
        end = `span ${span}`;
    }
    return (jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ "$gridColumnStart": start, "$gridColumnEnd": end, "$gridRowStart": rowStart, "$gridRowEnd": rowEnd }, { children: children }), void 0));
};

const FullRow = props => (jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnStart: "start", columnEnd: "end" }, { children: props.children }), void 0));

const AutoSubGrid = props => {
    const { minWidth = 0, columnSpan = 1, columnStart, columnEnd, rowStart, rowEnd, children } = props;
    const grid = useContext(AutoGridContext);
    if (columnStart && columnEnd) {
        throw new Error(`
      Cannot set both columnStart and columnEnd. 
      Set only one and use columnSpan.
    `);
    }
    const getColumns = useCallback((wanted) => {
        // the total width of the columns + the gaps to span the wanted length
        const totalWidth = (wanted * grid.columnWidth) + (grid.gridGap[1] * (wanted - 1));
        if (totalWidth < minWidth && wanted < grid.columnCount) {
            return getColumns(wanted + 1);
        }
        return wanted;
    }, [grid, minWidth]);
    const span = getColumns(Math.min(columnSpan, grid.columnCount));
    let start = columnStart, end = columnEnd;
    if (columnEnd && !columnStart) {
        start = `span ${span}`;
    }
    else if (!columnEnd) {
        end = `span ${span}`;
    }
    return (jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ "$gridColumnStart": start, "$gridColumnEnd": end, "$gridRowStart": rowStart, "$gridRowEnd": rowEnd }, { children: jsxRuntime.exports.jsx(AutoGrid$1, Object.assign({ maxColumns: span, gridGap: grid.gridGap }, { children: children }), void 0) }), void 0));
};

const AutoGrid = {
    Grid: AutoGrid$1,
    Cell: AutoGridCell,
    FullRow,
    SubGrid: AutoSubGrid,
};

const StaticWrapper = props => (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "fixed", "$top": `calc(50% - (${props.size}/2))`, "$left": `calc(50% - (${props.size}/2))` }, { children: props.children }), void 0));
const OverlayWrapper = props => (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "absolute", "$top": "0", "$left": "0", "$right": "0", "$bottom": "0", "$backgroundColor": "white@.75" }, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$zIndex": "999", "$position": "absolute", "$top": `calc(50% - (${props.size}/2))`, "$left": `calc(50% - (${props.size}/2))` }, { children: props.children }), void 0) }), void 0));
const BasicWrapper = props => (jsxRuntime.exports.jsx("div", { children: props.children }, void 0));
const Loader = props => {
    const { color = "primary", size = '6rem', mode = 'basic' } = props;
    const Wrapper = mode === 'fixed' ? StaticWrapper : mode === 'overlay' ? OverlayWrapper : BasicWrapper;
    return (jsxRuntime.exports.jsx(Wrapper, Object.assign({ size: size }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$width": "100%", "$height": "100%", "$alignItems": "center", "$justifyContent": "center" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": size, "$gridTemplateRows": size }, { children: jsxRuntime.exports.jsx(Box.Simple, Object.assign({ "$width": "100%", "$height": "100%", "$borderRadius": "100%", "$backgroundColor": `${color}@.30` }, { children: jsxRuntime.exports.jsx(Icon.Loader, { "$color": color }, void 0) }), void 0) }), void 0) }), void 0) }), void 0));
};

class ValidationError extends Error {
    constructor() {
        super(...arguments);
        this.kind = 'ValidaitonError';
    }
}
function isValidationError(e) {
    return (typeof e === 'object') && (e !== null) && ('kind' in e) && (e.kind === 'ValidaitonError');
}
const none = () => { };
const required = v => {
    if (!v) {
        throw new ValidationError('Required');
    }
};
const email = v => {
    const re = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9-.]+\.[A-Z]{1,}$/i);
    if (v && !v.match(re)) {
        throw new ValidationError('Must be a valid email');
    }
    if (v && v.includes('..')) {
        throw new ValidationError('Must be a valid email');
    }
};
const phone = v => {
    if (v && !v.match(/^[2-9]\d{9}$/)) {
        throw new ValidationError('Must be a valid phone number');
    }
};
/** Only allows whole numbers */
const integer = v => {
    if (v && v.match(/\D/)) {
        throw new ValidationError('Must be a whole number');
    }
};
const decimal = v => {
    if (v) {
        const parts = v.split('.');
        if (parts.length > 2) {
            throw new ValidationError('Too many decimal points');
        }
        parts.forEach(part => check$1(integer, part, 'Invalid Number'));
    }
};
/**
 * Validates that time is after the earliestTime
 */
const after = (earliestTime) => time => {
    if (time && time.isBefore(earliestTime)) {
        throw new ValidationError(`Must be after ${earliestTime.format({ month: '2-digit', day: '2-digit', year: 'numeric' })}`);
    }
};
const minLength = (min) => v => {
    if (v && v.length < min) {
        throw new ValidationError(`Minimum ${min} characters required`);
    }
};
const maxLength = (max) => v => {
    if (v && v.length > max) {
        throw new ValidationError(`Maximum ${max} characters only`);
    }
};
const exactLength = (len) => v => {
    if (v && v.length !== len) {
        throw new ValidationError(`Must be exactly ${len} characters`);
    }
};
const lte = (max) => v => {
    if (v && Number(v) > max) {
        throw new ValidationError(`Cannot exceed ${max}`);
    }
};
const gte = (min) => v => {
    if (v && Number(v) < min) {
        throw new ValidationError(`Must be at least ${min}`);
    }
};
const maxDecimalPlace = (place) => v => {
    const max = Math.floor(place);
    if (v) {
        const parts = v.split('.');
        if (parts[0].length > max) {
            throw new ValidationError('Too many decimal places');
        }
        check$1(decimal, v);
    }
};
const oneOf = (valid) => v => {
    if (v && !valid.includes(v)) {
        throw new ValidationError(`Must be one of "${valid.join(', ')}"`);
    }
};
const exactly = (str) => v => {
    if (v && v !== str) {
        throw new ValidationError(`Must be "${str}"`);
    }
};
const not = (str) => v => {
    if (v && v === str) {
        throw new ValidationError(`Must not be "${str}"`);
    }
};
const matchesRegex = (valid) => v => {
    if (v && !v.match(valid)) {
        throw new ValidationError(`Invalid Input`);
    }
};
const yesOrNo = v => {
    if (v && !['yes', 'no'].includes(v.toLowerCase())) {
        throw new ValidationError(`Must be either Yes or No`);
    }
};
const password = [
    matchesRegex(/[a-z]/),
    matchesRegex(/[A-Z]/),
    matchesRegex(/[0-9]/),
    matchesRegex(/[-+_!@#$%^&*., ?]/),
    minLength(8),
    maxLength(50),
];
const currencyOf = (vs) => v => {
    const value = (v || '').replace(/[^\d.]/g, '');
    vs.forEach(validation => {
        check$1(validation, value);
    });
};
const ORIGINAL_ERROR = '%original-error%';
function check$1(validations, value, message) {
    if (typeof validations === 'function') {
        try {
            validations(value);
        }
        catch (e) {
            if (isValidationError(e)) {
                if (message) {
                    throw new ValidationError(message.replace(ORIGINAL_ERROR, e.message));
                }
                else {
                    throw e;
                }
            }
            else {
                throw e;
            }
        }
    }
    else {
        validations.forEach(inner => check$1(inner, value, message));
    }
}

var validations = /*#__PURE__*/Object.freeze({
    __proto__: null,
    ValidationError: ValidationError,
    isValidationError: isValidationError,
    none: none,
    required: required,
    email: email,
    phone: phone,
    integer: integer,
    decimal: decimal,
    after: after,
    minLength: minLength,
    maxLength: maxLength,
    exactLength: exactLength,
    lte: lte,
    gte: gte,
    maxDecimalPlace: maxDecimalPlace,
    oneOf: oneOf,
    exactly: exactly,
    not: not,
    matchesRegex: matchesRegex,
    yesOrNo: yesOrNo,
    password: password,
    currencyOf: currencyOf,
    ORIGINAL_ERROR: ORIGINAL_ERROR,
    check: check$1
});

// const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
// FOR IE11
if (!String.prototype.padStart) {
    // eslint-disable-next-line
    Object.defineProperty(String.prototype, 'padStart', {
        configurable: true,
        writable: true,
        value: function (targetLength, padString) {
            targetLength = targetLength >> 0; //floor if number or convert non-number to 0;
            padString = String(typeof padString !== 'undefined' ? padString : ' ');
            if (this.length > targetLength) {
                return String(this);
            }
            else {
                targetLength = targetLength - this.length;
                if (targetLength > padString.length) {
                    padString += padString.repeat(targetLength / padString.length); //append to original to ensure we are longer than needed
                }
                return padString.slice(0, targetLength) + String(this);
            }
        },
    });
}
const USERS_TIME_ZONE_NAME = Intl.DateTimeFormat().resolvedOptions().timeZone;
const DEFAULT_LOCALE = 'en-US';
function _now() {
    return new Date(Date.now());
}
function isValideDate(date) {
    const d = new Date(date);
    return !isNaN(d.getTime());
}
// function isUsersTZ(tz) {
//   return tz.name === USERS_TIME_ZONE_NAME && tz.offset === USERS_TIME_ZONE_OFFSET
// }
function _same(t1, t2, options) {
    const a = t1.format(options);
    const b = t2.format(options);
    return a === b;
}
function _removeIEHiddenChars(str) {
    return str.replace(/[^\x20-\x7E]/g, '');
}
// eslint-disable-next-line no-irregular-whitespace
// Chrome version 110.x.x.x starting returning responses like: `11:00 AM EST`
// NOTE the first "space" in that string between the "00" and "AM" IS NOT a regular
// space char, but the second space between "AM" and "EST" IS a regular space char.
// I think this a chrome bug. This function is to convert that irregular
// space char into a regular space char.
function _fixIrregularWhiteSpace(str) {
    return str.replace(/[^\x20-\x7E]/g, ' ');
}
function _format(locale, zone, date, options) {
    return _fixIrregularWhiteSpace(new Intl.DateTimeFormat(locale, Object.assign({ timeZone: zone }, options)).format(date));
}
function _stampWithoutOffset(t) {
    const { year, month, day, hour, minute, second } = _parts(t).string;
    const stampWithoutMS = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    const offset = calculateOffset(t);
    const tempStamp = `${stampWithoutMS}${offset}`;
    const tempDate = Time.fromDate(new Date(tempStamp), t.zone);
    const millisecond = t.difference(tempDate).milliseconds;
    const stamp = `${stampWithoutMS}.${millisecond.toString().padStart(3, '0')}`;
    return stamp;
}
function _iso(t) {
    const { year, month, day, hour, minute, second } = _parts(t).string;
    const stampWithoutMS = `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    const offset = calculateOffset(t);
    const stamp = `${stampWithoutMS}${offset}`;
    const tempDate = Time.fromDate(new Date(stamp), t.zone);
    const millisecond = t.difference(tempDate).milliseconds;
    const iso = `${stampWithoutMS}.${millisecond.toString().padStart(3, '0')}${offset}`;
    return iso;
}
function _partsIE11(t) {
    const str = _format("en-US", t.zone, t.date, {
        hour12: false,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
    });
    // const str = t.date.toLocaleString("en-US", { hour12: false, timeZone: t.zone }).replace(/[^\x20-\x7E]/g, '')
    const rx = /^(\d+)\/(\d+)\/(\d+),? (\d+):(\d+):(\d+)/;
    const parts = str.match(rx);
    const [, month, day, year, hour, minute, second] = parts;
    const fixedHour = hour === "24" ? "0" : hour;
    const string = {};
    const number = {};
    number.month = Number(month);
    number.day = Number(day);
    number.year = Number(year);
    number.hour = Number(fixedHour);
    number.minute = Number(minute);
    number.second = Number(second);
    number.millisecond = 0;
    string.month = month.padStart(2, '0');
    string.day = day.padStart(2, '0');
    string.year = year;
    string.hour = fixedHour.padStart(2, '0');
    string.minute = minute.padStart(2, '0');
    string.second = second.padStart(2, '0');
    string.millisecond = '000';
    return { string, number };
}
function _parts(t) {
    return _partsIE11(t);
    // if (isIE11 || true) {
    //   return _partsIE11(t)
    // }
    // let hour    = String(parseInt(t.format({ hourCycle: 'h23', hour: 'numeric', hour12: false })))
    // let minute  = t.format({ minute: 'numeric' })
    // let second  = t.format({ second: 'numeric' })
    // let day     = t.format({ day: 'numeric' })
    // let month   = t.format({ month: 'numeric' })
    // let year    = t.format({ year: 'numeric' })
    // if (month.length === 1) {
    //   month = `0${month}`
    // }
    // if (day.length === 1) {
    //   day = `0${day}`
    // }
    // if (hour.length === 1) {
    //   hour = `0${hour}`
    // }
    // if (hour === "24") {
    //   hour = `00`
    // }
    // if (minute.length === 1) {
    //   minute = `0${minute}`
    // }
    // if (second.length === 1) {
    //   second = `0${second}`
    // }
    // const stampWithoutMS = `${year}-${month}-${day}T${hour}:${minute}:${second}`
    // const offset = calculateOffset(t)
    // const stamp = `${stampWithoutMS}${offset}`
    // const tempDate = new Time(new Date(stamp), t.zone)
    // const millisecond = t.difference(tempDate).milliseconds
    // const iso = `${stampWithoutMS}.${millisecond.toString().padStart(3, '0')}${offset}`
    // return { year, month, day, hour, second, minute, millisecond, iso }
}
function isToday(t) {
    return _same(t, new Time(), { month: '2-digit', day: '2-digit', year: 'numeric' });
}
function isTomorrow(t) {
    const tomorrow = _now();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return _same(t, Time.fromDate(tomorrow), { month: '2-digit', day: '2-digit', year: 'numeric' });
}
function isYesterday(t) {
    const yesterday = _now();
    yesterday.setDate(yesterday.getDate() - 1);
    return _same(t, Time.fromDate(yesterday), { month: '2-digit', day: '2-digit', year: 'numeric' });
}
function isCurrentYear(t) {
    return _same(t, new Time(), { year: 'numeric' });
}
function offsetTimeString(totalMins) {
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    const isNegative = totalMins < 0;
    const offsetStr = (isNegative ? '-' : '+') + String(Math.abs(hours)).padStart(2, '0') + ":" + String(Math.abs(mins)).padStart(2, '0');
    return offsetStr;
}
function getOffsetMinutes(t) {
    const { year, month, day, hour, minute } = _parts(t).number;
    let utcHours = t.date.getUTCHours();
    let ownHours = hour; //parseInt(t.format({ hourCycle: 'h23', hour: 'numeric', hour12: false }))
    if (utcHours === 24) {
        utcHours = 0;
    }
    if (ownHours === 24) {
        ownHours = 0;
    }
    const utcMinutes = t.date.getUTCMinutes();
    const ownMinutes = minute; //t.format({ minute: 'numeric' })
    const utcDay = t.date.getUTCDate();
    const utcMonth = t.date.getUTCMonth() + 1; // Zero based function
    const utcYear = t.date.getUTCFullYear();
    const ownDay = day; //t.format({ day: 'numeric' })
    const ownMonth = month; //t.format({ month: 'numeric' })
    const ownYear = year; //t.format({ year: 'numeric' })
    if (utcYear > ownYear) {
        utcHours += 24;
    }
    else if (utcYear < ownYear) {
        utcHours -= 24;
    }
    else if (utcMonth > ownMonth) {
        utcHours += 24;
    }
    else if (utcMonth < ownMonth) {
        utcHours -= 24;
    }
    else if (utcDay > ownDay) {
        utcHours += 24;
    }
    else if (utcDay < ownDay) {
        utcHours -= 24;
    }
    const hours = ownHours - utcHours;
    const minutes = ownMinutes - utcMinutes;
    return ((hours * 60) + minutes);
}
function calculateOffset(t) {
    return offsetTimeString(getOffsetMinutes(t));
}
function createTime(str, z = USERS_TIME_ZONE_NAME, locale = DEFAULT_LOCALE) {
    const capstr = _removeIEHiddenChars(str).toUpperCase().trim();
    const errmsg = `format is not allowed for creating time from string: "${str}"`;
    const isoUTCRx = /Z$/;
    const isoOffsetRx = /[+-]\d\d:\d\d$/;
    if (capstr.match(isoUTCRx) || capstr.match(isoOffsetRx)) {
        throw new Error(errmsg);
    }
    /** The ISO style timestamp */
    const isoRx = /^(\d\d\d\d)-?(\d\d)-?(\d\d)T(\d\d):?(\d\d):?(\d\d\.?\d{0,3})$/;
    /** The full timestamp including date and time. */
    const fullRx = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:\s+|\s+@\s*)(\d{1,2}):(\d{2})(?::\d{2})? ?([AP]M)?$/;
    /** The full timestamp including date and time. But with only 2 digit year */
    const shortYearRx = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2})(?:\s+|\s+@\s*)(\d{1,2}):(\d{2})(?::\d{2})? ?([AP]M)?$/;
    /** The time only specified, using today as the date. */
    const timeRx = /^(\d{1,2}):(\d{2}) ?([AP]M)?$/;
    let year, month, day, hour, minute, second, millisecond;
    const isoMatch = capstr.match(isoRx);
    const fullMatch = capstr.match(fullRx);
    const shortYearMatch = capstr.match(shortYearRx);
    const timeMatch = capstr.match(timeRx);
    if (isoMatch) {
        const [, _year, _month, _day, _hour, _minute, secondDecimal] = isoMatch;
        const [_second, _millisecond] = secondDecimal.split('.');
        if (Number(_millisecond)) {
            millisecond = _millisecond.padEnd(3, '0');
        }
        if (_second) {
            second = _second;
        }
        year = _year;
        month = _month;
        day = _day;
        hour = _hour;
        minute = _minute;
    }
    else if (fullMatch) {
        const [, _month, _day, _year, _hour, _minute, _meridian] = fullMatch;
        month = _month.padStart(2, '0');
        day = _day.padStart(2, '0');
        year = _year;
        minute = _minute.padStart(2, '0');
        if (_meridian && _meridian === 'PM' && Number(_hour) < 12) {
            hour = String(Number(_hour) + 12).padStart(2, '0');
        }
        else if (_meridian && _meridian === 'AM' && Number(_hour) === 12) {
            hour = '00';
        }
        else {
            hour = _hour.padStart(2, '0');
        }
    }
    else if (shortYearMatch) {
        const [, _month, _day, _year, _hour, _minute, _meridian] = shortYearMatch;
        month = _month.padStart(2, '0');
        day = _day.padStart(2, '0');
        year = `20${_year}`;
        minute = _minute.padStart(2, '0');
        if (_meridian && _meridian === 'PM' && Number(_hour) < 12) {
            hour = String(Number(_hour) + 12).padStart(2, '0');
        }
        else if (_meridian && _meridian === 'AM' && Number(_hour) === 12) {
            hour = '00';
        }
        else {
            hour = _hour.padStart(2, '0');
        }
    }
    else if (timeMatch) {
        const [, _hour, _minute, _meridian] = timeMatch;
        year = _format(locale, z, _now(), { year: 'numeric' });
        month = _format(locale, z, _now(), { month: '2-digit' });
        day = _format(locale, z, _now(), { day: '2-digit' });
        minute = _minute.padStart(2, '0');
        if (_meridian && _meridian === 'PM' && Number(_hour) < 12) {
            hour = String(Number(_hour) + 12).padStart(2, '0');
        }
        else if (_meridian && _meridian === 'AM' && Number(_hour) === 12) {
            hour = '00';
        }
        else {
            hour = _hour.padStart(2, '0');
        }
    }
    else {
        throw new Error(errmsg);
    }
    const rawstamp = `${year}-${month}-${day}T${hour}:${minute}:${second || '00'}${millisecond ? `.${millisecond}` : ''}`;
    const utcstamp = `${rawstamp}Z`;
    // console.log({ rawstamp, utcstamp })
    const utcdate = new Date(utcstamp);
    // console.log({ utcdate })
    if (isValideDate(utcdate)) {
        const offsetString = Time.fromDate(utcdate, z, locale).getOffsetString();
        const stamp = `${rawstamp}${offsetString}`;
        // console.log({stamp}, new Date(stamp))
        const date = new Date(stamp);
        if (!isValideDate(utcdate)) {
            throw new Error(`"${str}" is not a valid date`);
        }
        return Time.fromDate(date, z, locale);
    }
    else {
        throw new Error(`"${str}" is not a valid date`);
    }
}
const msSeconds = 1000;
const msMinutes = 60 * msSeconds;
const msHours = 60 * msMinutes;
function addTimeToDate(str, from) {
    if (typeof str === 'number') {
        str = String(str);
    }
    str = str.replace(/\s/g, '');
    let multipler = 1;
    if (str.match(/^[+-]/)) {
        const sign = str[0];
        str = str.substr(1);
        if (sign === '-') {
            multipler = -1;
        }
    }
    const [hours, minutes, seconds] = str.split(':').map(Number);
    let ms = new Date(from).getTime();
    if (hours && !isNaN(hours)) {
        ms += (hours * msHours) * multipler;
    }
    if (minutes && !isNaN(minutes)) {
        ms += (minutes * msMinutes) * multipler;
    }
    if (seconds && !isNaN(seconds)) {
        ms += (seconds * msSeconds) * multipler;
    }
    return new Date(ms);
}
function difference(t1, t2) {
    const ms1 = t1.date.getTime();
    const ms2 = t2.date.getTime();
    const isNegative = (ms2 > ms1);
    const multipler = isNegative ? -1 : 1;
    const milliseconds = Math.abs(ms1 - ms2);
    const seconds = milliseconds / msSeconds;
    const minutes = milliseconds / msMinutes;
    const hours = milliseconds / msHours;
    let string = '';
    const modHours = Math.floor(hours || 0);
    const modMinutes = Math.floor(hours ? ((hours % 1) * msHours) / msMinutes : minutes || 0);
    const modSeconds = Math.floor(minutes ? ((minutes % 1) * msMinutes) / msSeconds : seconds || 0);
    const modMilliseconds = Math.ceil(seconds ? ((seconds % 1) * msSeconds) : milliseconds || 0);
    if (modMilliseconds) {
        string = `.${String(modMilliseconds).padStart(3, '0')}`;
    }
    if (modSeconds || string) {
        string = `:${String(modSeconds).padStart(2, '0')}${string}`;
    }
    if (modMinutes || string) {
        string = `:${String(modMinutes).padStart(2, '0')}${string}`;
    }
    string = `${isNegative ? '-' : '+'}${String(modHours).padStart(2, '0')}${string}`;
    return {
        milliseconds: (milliseconds * multipler),
        seconds: (seconds * multipler),
        minutes: (minutes * multipler),
        hours: (hours * multipler),
        string,
    };
}
function _getDayName(t) {
    if (isToday(t)) {
        return 'Today';
    }
    else if (isTomorrow(t)) {
        return 'Tomorrow';
    }
    else if (isYesterday(t)) {
        return 'Yesterday';
    }
    else {
        return t.format({ weekday: 'long' });
    }
}
function _getDateString(t, dynamic = true) {
    const format = Time.OMIT_LEADING_ZEROS_IN_DATES
        ? { month: 'numeric', day: 'numeric' }
        : { month: '2-digit', day: '2-digit' };
    if (isToday(t) && dynamic) {
        return 'Today';
    }
    else if (isTomorrow(t) && dynamic) {
        return 'Tomorrow';
    }
    else if (isYesterday(t) && dynamic) {
        return 'Yesterday';
    }
    else if (isCurrentYear(t)) {
        return t.format(format);
    }
    else {
        return t.format(Object.assign(Object.assign({}, format), { year: 'numeric' }));
    }
}
function getDateString(t, opts = {}) {
    const { includeDayName } = opts;
    if (includeDayName) {
        return `${_getDayName(t)}, ${_getDateString(t, false)}`;
    }
    return _getDateString(t);
}
function getTimeString(t, opts = {}) {
    const options = {
        hour: 'numeric',
        timeZone: t.zone,
        // timeZoneName:'short',
        // hourCycle: 'h23',
    };
    if (!opts.omitTimezone) {
        options.timeZoneName = 'short';
    }
    const hasMins = !!Number(t.format({ minute: 'numeric' }));
    if (hasMins || Time.ALWAYS_SHOW_MINUTES) {
        options.minute = '2-digit';
    }
    return t.format(options);
}
function toString$3(time, divider) {
    const dateStr = getDateString(time);
    const timeStr = getTimeString(time);
    return `${dateStr} ${divider} ${timeStr}`;
}
class Time {
    constructor(timestamp = Date.now(), zone = USERS_TIME_ZONE_NAME, locale = DEFAULT_LOCALE) {
        this._isASAP = false;
        if (typeof zone === 'object') {
            zone = zone.timeZoneId;
        }
        this._date = new Date(timestamp);
        this.zone = zone;
        this.locale = locale;
    }
    static isValidUserString(s) {
        try {
            const t = Time.fromUserString(s);
            return isValideDate(t.date);
        }
        catch (e) {
            return false;
        }
    }
    static now(z, l) {
        return new Time(Date.now(), z, l);
    }
    static fromUserString(s, z, l) {
        return createTime(s, z, l);
    }
    static asap(z, l) {
        const time = new Time(undefined, z, l);
        time._isASAP = true;
        return time;
    }
    /**
     * Create a Time instance from a date string without having to instantiate a Date object first.
     * @param date string representation of a date. What you would pass to Date.parse
     * @param z optional time zone
     * @param l optional local
     */
    static fromDate(date, z, l) {
        return new Time(date.getTime(), z, l);
    }
    get isRange() {
        return false;
    }
    get isASAP() {
        return this._isASAP;
    }
    get date() {
        if (this.isASAP) {
            return new Date(Date.now());
        }
        else {
            return this._date;
        }
    }
    set date(date) {
        this._date = date;
        this._isASAP = false;
    }
    toString() {
        if (this.isASAP) {
            return 'ASAP';
        }
        return toString$3(this, Time.AT_TEXT);
    }
    toDeadlineString() {
        if (this.isASAP) {
            return 'ASAP';
        }
        return toString$3(this, Time.BY_TEXT);
    }
    toCommencementString() {
        if (this.isASAP) {
            return 'ASAP';
        }
        return toString$3(this, Time.AFTER_TEXT);
    }
    toRangeString(end) {
        return new TimeRange(this, end).toString();
    }
    toOffsetISOString() {
        return _iso(this);
    }
    toZonelessISOString() {
        return _stampWithoutOffset(this);
    }
    isToday() {
        return isToday(this);
    }
    isTomorrow() {
        return isTomorrow(this);
    }
    isYesterday() {
        return isYesterday(this);
    }
    isSameDayAs(other) {
        return _same(this, other, { month: '2-digit', day: '2-digit', year: 'numeric' });
    }
    isEqualTo(other) {
        return this.date.getTime() === other.date.getTime();
    }
    isAfter(other) {
        return this.date.getTime() > other.date.getTime();
    }
    isBefore(other) {
        return this.date.getTime() < other.date.getTime();
    }
    format(options) {
        return _format(this.locale, this.zone, this.date, options);
    }
    getDateString(options) {
        return getDateString(this, options);
    }
    getTimeString() {
        return getTimeString(this);
    }
    getOffsetString() {
        return calculateOffset(this);
    }
    getOffset() {
        return getOffsetMinutes(this);
    }
    addTime(str) {
        const d = addTimeToDate(str, this.date);
        this.date = d;
        return this;
    }
    startOfDay() {
        const { year, month, day } = _parts(this).string;
        this.date = new Date(`${year}-${month}-${day}T00:00:00.000${this.getOffsetString()}`);
        return this;
    }
    endOfDay() {
        const { year, month, day } = _parts(this).string;
        this.date = new Date(`${year}-${month}-${day}T23:59:59.999${this.getOffsetString()}`);
        return this;
    }
    yesterday() {
        this.date = new Date(this.startOfDay().date.getTime() - 1001);
        return this;
    }
    tomorrow() {
        this.date = new Date(this.endOfDay().date.getTime() + 1001);
        return this;
    }
    firstOfMonth() {
        const { year, month } = _parts(this).string;
        this.date = new Date(`${year}-${month}-01T00:00:00.000${this.getOffsetString()}`);
        return this;
    }
    // alias so keep API consisten:
    //  time.startOfDay, time.endOfDay, time.startOfMonth, time.endOfMonth
    startOfMonth() {
        return this.firstOfMonth();
    }
    endOfMonth() {
        const month = this.date.getMonth();
        const year = this.date.getFullYear();
        const date = new Date(year, month + 1, 0, 23, 59, 59, 999);
        const time = Time.fromDate(date, this.zone);
        time.endOfDay();
        this.date = time.date;
        return this;
    }
    prevMonth() {
        let { year, month } = _parts(this).string;
        if (month === '01') {
            year = String(Number(year) - 1);
            month = '12';
        }
        else {
            month = `${Number(month) - 1}`.padStart(2, '0');
        }
        const currentOffset = this.getOffset();
        this.date = new Date(`${year}-${month}-01T00:00:00.000${this.getOffsetString()}`);
        const newOffset = this.getOffset();
        const offsetDiff = (currentOffset - newOffset) * (1000 * 60);
        this.date = new Date(this.date.getTime() + offsetDiff);
        return this;
    }
    nextMonth() {
        let { year, month } = _parts(this).string;
        if (month === '12') {
            year = String(Number(year) + 1);
            month = '01';
        }
        else {
            month = `${Number(month) + 1}`.padStart(2, '0');
        }
        const currentOffset = this.getOffset();
        this.date = new Date(`${year}-${month}-01T00:00:00.000${this.getOffsetString()}`);
        const newOffset = this.getOffset();
        const offsetDiff = (currentOffset - newOffset) * (1000 * 60);
        this.date = new Date(this.date.getTime() + offsetDiff);
        return this;
    }
    /** Create a time range spanning the entire day */
    toRange() {
        return new TimeRange(this.clone().startOfDay(), this.clone().endOfDay());
    }
    difference(t) {
        return difference(this, t);
    }
    clone() {
        return Time.fromDate(new Date(this.date), this.zone, this.locale);
    }
    cloneIn(newZone) {
        const str = this.toOffsetISOString().slice(0, -6);
        return Time.fromUserString(str, newZone, this.locale);
    }
    equals(other) {
        if (other instanceof Time) {
            return other.date.getTime() === this.date.getTime() && other.zone === this.zone && other.locale === this.locale;
        }
        return false;
    }
}
Time.ALWAYS_SHOW_MINUTES = true;
Time.OMIT_LEADING_ZEROS_IN_DATES = false;
Time.AT_TEXT = 'at';
Time.BY_TEXT = 'by';
Time.AFTER_TEXT = 'after';
function sort(a, b) {
    return [a, b].sort((x, y) => {
        return x.date.getTime() - y.date.getTime();
    });
}
function daysBetween(start, end) {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.round((end.endOfDay().date.getTime() - start.startOfDay().date.getTime()) / millisecondsPerDay);
}
class TimeRange {
    constructor(start, end) {
        this.listeners = [];
        if (start) {
            this.setRangeStart(start);
        }
        if (end) {
            this.setRangeEnd(end);
        }
    }
    static fromJSON(json) {
        const { from, to } = json;
        const start = from ? Time.fromDate(new Date(from)) : undefined;
        const end = to ? Time.fromDate(new Date(to)) : undefined;
        return new TimeRange(start, end);
    }
    get isRange() {
        return true;
    }
    clone() {
        return new TimeRange(this.getStart(), this.getEnd());
    }
    /** Determins if the TimeRange has no values */
    isEmpty() {
        return !this.getEnd() && !this.getStart();
    }
    /** Determines if the TimeRange has one value but not the other */
    isOpen() {
        return (!this.getEnd() && this.getStart()) || (this.getEnd() && !this.getStart());
    }
    /** Determines if the TimeRange has both a start and end */
    isClosed() {
        return this.getEnd() && this.getStart();
    }
    /** Closes the TimeRange if it is open
     *
     * Does nothing if the TimeRange is already closed
     * Throw if the TimeRange is empty
     *
     * If range is open (contains a single time) then it will return a range
     * spanning the start and end of that day. Similar to calling `toRange`
     * on the given Time instance.
     */
    close() {
        if (this.isEmpty()) {
            throw new Error('Cannot close an empty TimeRange');
        }
        if (this.isClosed()) {
            return this;
        }
        const start = (this.getStart() || this.getEnd()).clone().startOfDay();
        const end = (this.getEnd() || this.getStart()).clone().endOfDay();
        this.setRangeStart(start);
        this.setRangeEnd(end);
        return this;
    }
    listen(listener) {
        this.listeners.push(listener);
    }
    unlisten(listener) {
        if (this.listeners.includes(listener)) {
            this.listeners.splice(this.listeners.indexOf(listener), 1);
        }
    }
    broadcastToListeners() {
        this.listeners.forEach(l => l(this));
    }
    setRangeStart(start) {
        if (this.end) {
            const [s, e] = sort(start, this.end);
            this.start = s;
            this.end = e;
        }
        else {
            this.start = start;
        }
        this.broadcastToListeners();
    }
    setRangeEnd(end) {
        if (this.start) {
            const [s, e] = sort(this.start, end);
            this.start = s;
            this.end = e;
        }
        else {
            this.end = end;
        }
        this.broadcastToListeners();
    }
    clear() {
        this.start = undefined;
        this.end = undefined;
        this.broadcastToListeners();
    }
    getStart() {
        return this.start;
    }
    getEnd() {
        return this.end;
    }
    isStart(t) {
        var _a;
        return ((_a = this.start) === null || _a === void 0 ? void 0 : _a.isEqualTo(t)) || false;
    }
    isStartDay(t) {
        var _a;
        return ((_a = this.start) === null || _a === void 0 ? void 0 : _a.isSameDayAs(t)) || false;
    }
    isEndDay(t) {
        var _a;
        return ((_a = this.end) === null || _a === void 0 ? void 0 : _a.isSameDayAs(t)) || false;
    }
    daySpan() {
        if (this.start && this.end) {
            return daysBetween(this.start, this.end);
        }
        else {
            return 0;
        }
    }
    contains(t) {
        if (this.start && this.end) {
            return t.isEqualTo(this.start) ||
                t.isEqualTo(this.end) || (t.isAfter(this.start) &&
                t.isBefore(this.end));
        }
        else if (this.start) {
            return t.isEqualTo(this.start);
        }
        else if (this.end) {
            return t.isEqualTo(this.end);
        }
        else {
            return false;
        }
    }
    toString() {
        if (this.start && this.end) {
            const sameDay = _same(this.start, this.end, { month: '2-digit', day: '2-digit', year: 'numeric' });
            if (sameDay) {
                return `${this.start.getDateString()} from ${getTimeString(this.start, { omitTimezone: true })} - ${this.end.getTimeString()}`;
            }
            else {
                return `From ${this.start.toString()} - ${this.end.toString()}`;
            }
        }
        else if (this.start) {
            return `From ${this.start.toString()} - ???`;
        }
        else if (this.end) {
            return `From ??? - ${this.end.toString()}`;
        }
        else {
            return 'From ??? - ???';
        }
    }
    toDateString() {
        if (this.start && this.end) {
            const sameDay = _same(this.start, this.end, { month: '2-digit', day: '2-digit', year: 'numeric' });
            if (sameDay) {
                return `${this.start.getDateString()}`;
            }
            else {
                return `${this.start.getDateString()} - ${this.end.getDateString()}`;
            }
        }
        else if (this.start) {
            return `${this.start.getDateString()} - ???`;
        }
        else if (this.end) {
            return `??? - ${this.end.getDateString()}`;
        }
        else {
            return '??? - ???';
        }
    }
    toJSON() {
        var _a, _b;
        return {
            from: (_a = this.getStart()) === null || _a === void 0 ? void 0 : _a.toOffsetISOString(),
            to: (_b = this.getEnd()) === null || _b === void 0 ? void 0 : _b.toOffsetISOString(),
        };
    }
}

const MAX_INT = 9999;
const defaultTemplate = [MAX_INT];
const defaultAlledChars = /./;
const textFormat = {};
const nameFormat = {
    allowedChars: /[-a-zA-Z. ']/,
};
const emailFormat = {
    allowedChars: /[^\s]/,
    template: [250]
};
const trackingFormat = {
    allowedChars: /[\d\w]/,
    preprocess: str => str.toUpperCase(),
    template: [4, ' - ', 4, ' - ', 4, ' - ', 4]
};
const phoneFormat = {
    allowedChars: /\d/,
    preprocess: str => str.replace(/^1/, ''),
    template: ['(', 3, ') ', 3, '-', 4]
};
const integerFormat = {
    allowedChars: /\d/,
};
const monthYearFormat = {
    allowedChars: /\d/,
    prevalidate: (value) => {
        if (!value) {
            return;
        }
        const month = value.substring(0, 2);
        const year = value.substring(2);
        if (!month || !year) {
            throw new ValidationError('Must be a valid date of MM / YYYY');
        }
        return Time.fromDate(new Date(`${month}/01/${year}`));
    },
    template: ({ allowedChars }) => {
        const month = allowedChars.substring(0, 2);
        const year = allowedChars.substring(2);
        if (!year && month && Number(month[0]) > 1) {
            return ['0', 1, ' / ', 4];
        }
        return [2, ' / ', 4];
    }
};
const monthDayYearFormat = {
    allowedChars: /\d/,
    prevalidate: (value) => {
        if (!value) {
            return;
        }
        const month = value.substring(0, 2);
        const day = value.substring(2, 4);
        const year = value.substring(4);
        if (!month || !day || !year) {
            throw new ValidationError('Must be a valid date of MM / DD / YYYY');
        }
        return Time.fromDate(new Date(`${month}/${day}/${year}`));
    },
    template: ({ allowedChars }) => {
        const month = allowedChars.substring(0, 2);
        const day = allowedChars.substring(2, 4);
        const year = allowedChars.substring(4);
        if (!year && !day && month && Number(month[0]) > 1) {
            return ['0', 1, ' / ', 2, ' / ', 4];
        }
        if (!year && day && Number(day[0]) > 3) {
            return [2, ' / 0', 1, ' / ', 4];
        }
        return [2, ' / ', 2, ' / ', 4];
    }
};
const decimalFormat = {
    allowedChars: /[\d.]/,
    preprocess: str => str.replace(/^([^.]*\.)|\./g, '$1'),
    preformat: arr => {
        const dotPos = arr.indexOf('.');
        if (dotPos > -1 && arr.length - dotPos > 3) {
            return arr.splice(0, dotPos + 3);
        }
        return arr;
    },
};
const getAllowedChars = (format, userText) => {
    if (format.preprocess) {
        userText = format.preprocess(userText);
    }
    if (!userText.length) {
        return [];
    }
    let allowedChars = userText.match(new RegExp((format.allowedChars || defaultAlledChars).source, 'g')) || [];
    if (format.preformat) {
        allowedChars = format.preformat(allowedChars);
    }
    return allowedChars;
};
function getFormatTemplate(format, userText) {
    const allowedChars = getAllowedChars(format, userText);
    if (!allowedChars.length) {
        return [];
    }
    const _template = (format.template || defaultTemplate);
    return typeof _template === 'function' ? _template({ userText, allowedChars: allowedChars.join('') }) : _template;
}
const applyFormat = (format, userText) => {
    const allowedChars = getAllowedChars(format, userText);
    if (!allowedChars.length) {
        return { formatted: '', raw: '' };
    }
    const template = getFormatTemplate(format, userText);
    let formatted = '';
    let raw = '';
    template.forEach(f => {
        if (typeof f === 'string') {
            if (allowedChars.length) {
                formatted += f;
            }
        }
        else {
            const next = allowedChars.splice(0, f).join('');
            formatted += next;
            raw += next;
        }
    });
    return { formatted, raw };
};

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$4 = freeGlobal || freeSelf || Function('return this')();

var _root = root$4;

var root$3 = _root;

/** Built-in value references. */
var Symbol$4 = root$3.Symbol;

var _Symbol = Symbol$4;

var Symbol$3 = _Symbol;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$5.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$3 ? Symbol$3.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty$4.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto$4 = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$4.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$2 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$2 ? Symbol$2.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$2(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$2;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$5(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$5;

var baseGetTag$1 = _baseGetTag,
    isObject$4 = isObject_1;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction$1(value) {
  if (!isObject$4(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag$1(value);
  return tag == funcTag || tag == genTag || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction$1;

var root$2 = _root;

/** Used to detect overreaching core-js shims. */
var coreJsData$1 = root$2['__core-js_shared__'];

var _coreJsData = coreJsData$1;

var coreJsData = _coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked$1(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked$1;

/** Used for built-in method references. */

var funcProto$1 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource$1(func) {
  if (func != null) {
    try {
      return funcToString$1.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource$1;

var isFunction = isFunction_1,
    isMasked = _isMasked,
    isObject$3 = isObject_1,
    toSource = _toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto$3 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$3.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString.call(hasOwnProperty$3).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative$1(value) {
  if (!isObject$3(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

var _baseIsNative = baseIsNative$1;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function getValue$1(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue$1;

var baseIsNative = _baseIsNative,
    getValue = _getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative$3(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

var _getNative = getNative$3;

var getNative$2 = _getNative;

/* Built-in method references that are verified to be native. */
var nativeCreate$4 = getNative$2(Object, 'create');

var _nativeCreate = nativeCreate$4;

var nativeCreate$3 = _nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear$1() {
  this.__data__ = nativeCreate$3 ? nativeCreate$3(null) : {};
  this.size = 0;
}

var _hashClear = hashClear$1;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function hashDelete$1(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete$1;

var nativeCreate$2 = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet$1(key) {
  var data = this.__data__;
  if (nativeCreate$2) {
    var result = data[key];
    return result === HASH_UNDEFINED$1 ? undefined : result;
  }
  return hasOwnProperty$2.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet$1;

var nativeCreate$1 = _nativeCreate;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas$1(key) {
  var data = this.__data__;
  return nativeCreate$1 ? (data[key] !== undefined) : hasOwnProperty$1.call(data, key);
}

var _hashHas = hashHas$1;

var nativeCreate = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet$1(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED : value;
  return this;
}

var _hashSet = hashSet$1;

var hashClear = _hashClear,
    hashDelete = _hashDelete,
    hashGet = _hashGet,
    hashHas = _hashHas,
    hashSet = _hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash$1.prototype.clear = hashClear;
Hash$1.prototype['delete'] = hashDelete;
Hash$1.prototype.get = hashGet;
Hash$1.prototype.has = hashHas;
Hash$1.prototype.set = hashSet;

var _Hash = Hash$1;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */

function listCacheClear$1() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear$1;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */

function eq$2(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq$2;

var eq$1 = eq_1;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf$4(array, key) {
  var length = array.length;
  while (length--) {
    if (eq$1(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf$4;

var assocIndexOf$3 = _assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete$1(key) {
  var data = this.__data__,
      index = assocIndexOf$3(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete$1;

var assocIndexOf$2 = _assocIndexOf;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet$1(key) {
  var data = this.__data__,
      index = assocIndexOf$2(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet$1;

var assocIndexOf$1 = _assocIndexOf;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas$1(key) {
  return assocIndexOf$1(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas$1;

var assocIndexOf = _assocIndexOf;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet$1(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet$1;

var listCacheClear = _listCacheClear,
    listCacheDelete = _listCacheDelete,
    listCacheGet = _listCacheGet,
    listCacheHas = _listCacheHas,
    listCacheSet = _listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache$1.prototype.clear = listCacheClear;
ListCache$1.prototype['delete'] = listCacheDelete;
ListCache$1.prototype.get = listCacheGet;
ListCache$1.prototype.has = listCacheHas;
ListCache$1.prototype.set = listCacheSet;

var _ListCache = ListCache$1;

var getNative$1 = _getNative,
    root$1 = _root;

/* Built-in method references that are verified to be native. */
var Map$2 = getNative$1(root$1, 'Map');

var _Map = Map$2;

var Hash = _Hash,
    ListCache = _ListCache,
    Map$1 = _Map;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear$1() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$1 || ListCache),
    'string': new Hash
  };
}

var _mapCacheClear = mapCacheClear$1;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */

function isKeyable$1(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable$1;

var isKeyable = _isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData$4(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData$4;

var getMapData$3 = _getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete$1(key) {
  var result = getMapData$3(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete$1;

var getMapData$2 = _getMapData;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet$1(key) {
  return getMapData$2(this, key).get(key);
}

var _mapCacheGet = mapCacheGet$1;

var getMapData$1 = _getMapData;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas$1(key) {
  return getMapData$1(this, key).has(key);
}

var _mapCacheHas = mapCacheHas$1;

var getMapData = _getMapData;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet$1(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet$1;

var mapCacheClear = _mapCacheClear,
    mapCacheDelete = _mapCacheDelete,
    mapCacheGet = _mapCacheGet,
    mapCacheHas = _mapCacheHas,
    mapCacheSet = _mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache$1.prototype.clear = mapCacheClear;
MapCache$1.prototype['delete'] = mapCacheDelete;
MapCache$1.prototype.get = mapCacheGet;
MapCache$1.prototype.has = mapCacheHas;
MapCache$1.prototype.set = mapCacheSet;

var _MapCache = MapCache$1;

var MapCache = _MapCache;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize$1(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize$1.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize$1.Cache = MapCache;

var memoize_1 = memoize$1;

/////////////////////////////////////////////////////////////////
// memoizeCapped: Stolen from lodash internals

const MAX_MEMOIZE_SIZE$1 = 500;

/**
 * A specialized version of `memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped$2(func) {
  const result = memoize_1(func, (key) => {
    const { cache } = result;
    if (cache.size === MAX_MEMOIZE_SIZE$1) {
      cache.clear();
    }
    return key
  });

  return result
}

/////////////////////////////////////////////////////////////////
// stringToPath: stolen from lodash internals

const charCodeOfDot = '.'.charCodeAt(0);
const reEscapeChar$1 = /\\(\\)?/g;
const rePropName$1 = RegExp(
  // Match anything that isn't a dot or bracket.
  '[^.[\\]]+' + '|' +
  // Or match property names within brackets.
  '\\[(?:' +
    // Match a non-string expression.
    '([^"\'][^[]*)' + '|' +
    // Or match strings (supports escaping characters).
    '(["\'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2' +
  ')\\]'+ '|' +
  // Or match "" as the space between consecutive dots or empty brackets.
  '(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))'
  , 'g');

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
const stringToPath$2 = memoizeCapped$2((string) => {
  const result = [];
  if (string.charCodeAt(0) === charCodeOfDot) {
    result.push('');
  }
  string.replace(rePropName$1, (match, expression, quote, subString) => {
    let key = match;
    if (quote) {
      key = subString.replace(reEscapeChar$1, '$1');
    }
    else if (expression) {
      key = expression.trim();
    }
    result.push(key);
  });
  return result
});

function normalizePath(path) {
    const pieces = stringToPath$2(path);
    return pieces.join('.');
}

const useFormLayoutCellContainer = (columnSpan, minWidth, columnStart, columnEnd) => {
    const layout = useFormLayout();
    const AutoGridCellContainer = useMemo(() => {
        const AutoGridCellContainer = props => (jsxRuntime.exports.jsx(AutoGridCell, Object.assign({ columnSpan: columnSpan, minWidth: minWidth, columnStart: columnStart, columnEnd: columnEnd }, { children: props.children }), void 0));
        return AutoGridCellContainer;
    }, [columnEnd, columnSpan, columnStart, minWidth]);
    const NoLayoutCellContainer = useMemo(() => {
        const NoLayoutCellContainer = props => (jsxRuntime.exports.jsx("div", { children: props.children }, void 0));
        return NoLayoutCellContainer;
    }, []);
    const getContainer = useCallback(() => {
        switch (layout) {
            case FormLayout.None: return NoLayoutCellContainer;
            case FormLayout.AutoGrid: return AutoGridCellContainer;
        }
    }, [AutoGridCellContainer, NoLayoutCellContainer, layout]);
    return getContainer();
};

const FormFieldContext = createContext({
    raw: '',
    formatted: '',
    input: null,
});
const InputTypes = {
    hidden: {
        format: textFormat,
        type: 'hidden',
    },
    text: {
        format: textFormat,
        type: 'text',
    },
    password: {
        format: textFormat,
        type: 'password',
    },
    name: {
        format: nameFormat,
        type: 'text',
    },
    email: {
        format: emailFormat,
        type: 'text',
    },
    phone: {
        format: phoneFormat,
        type: 'tel',
    },
    integer: {
        format: integerFormat,
        type: 'tel',
    },
    decimal: {
        format: decimalFormat,
        type: 'text',
    },
    tracking: {
        format: trackingFormat,
        type: 'text',
    },
    date: {
        format: monthDayYearFormat,
        type: 'tel'
    },
    monthYear: {
        format: monthYearFormat,
        type: 'tel'
    },
};
const FieldLabel = props => {
    const { error, hasSubmitted } = useFormFieldError(props.name);
    const valid = hasSubmitted && !error;
    const label = valid ? `✓ ${props.children}` : props.children;
    return (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$left": "0.5rem", "$top": "1rem" }, { children: jsxRuntime.exports.jsx("label", { children: jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$textAlign": "left", "$color": error ? 'error' : valid ? 'success' : undefined }, { children: error || label }), void 0) }, void 0) }), void 0));
};
function getSelectionInFormat(template, selection) {
    let [start, end] = selection;
    let cursor = 0;
    template.forEach(part => {
        if (typeof part === 'number') {
            cursor += part;
        }
        else {
            const extra = part.length;
            const spanning = end !== null && end > cursor && end <= cursor + extra;
            if (spanning) {
                start = start !== null ? start += extra : null;
                end = end !== null ? end += extra : null;
            }
            cursor += extra;
        }
    });
    return [start, end];
}
function cursorAtEnd(value, selection) {
    const [, selectionEnd] = selection;
    return (selectionEnd !== null && selectionEnd !== void 0 ? selectionEnd : -1) >= value.length;
}
const Field = props => {
    const { id: fieldId, name: _name, type: inputType = "text", label, defaultValue, value: externalValue, disabled, columnSpan, minWidth, columnStart, columnEnd, onChange, children } = props, rest = __rest(props, ["id", "name", "type", "label", "defaultValue", "value", "disabled", "columnSpan", "minWidth", "columnStart", "columnEnd", "onChange", "children"]);
    const name = normalizePath(_name);
    const { format, type } = InputTypes[inputType];
    const ref = useRef(null);
    const formId = useFormId();
    const id = fieldId ? fieldId : (formId && name) ? `${formId}-${name}` : undefined;
    const revalidate = useFormRevalidate();
    const { error, hasSubmitted } = useFormFieldError(name);
    const [validatedValue, setValidatedValue] = useState(defaultValue);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const [selection, setSelection] = useState([null, null]);
    const handleChange = useCallback(event => {
        event.persist();
        if (ref.current) {
            setSelection([ref.current.selectionStart, ref.current.selectionEnd]);
        }
        if (onChange) {
            onChange(event);
        }
        setInternalValue(event.target.value);
    }, [onChange]);
    const value = externalValue || internalValue || '';
    const { raw, formatted } = applyFormat(format, value);
    const getNewSelection = React__default.useCallback((previousValue = '', newValue = '') => {
        let newSelection;
        const [selectionStart, selectionEnd] = selection;
        if (newValue.length < previousValue.length) {
            // deleting
            newSelection = selection;
        }
        else if (newValue.length > previousValue.length && cursorAtEnd(previousValue, selection)) {
            // adding at the end of the string
            newSelection = [
                selectionStart !== null ? newValue.length : null,
                selectionEnd !== null ? newValue.length : null
            ];
        }
        else {
            // inserting text anywhere except at the end (need to account for new formatting chars)
            newSelection = getSelectionInFormat(getFormatTemplate(format, newValue), selection);
        }
        return newSelection;
    }, [format, selection]);
    useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedValue !== formatted) {
            revalidate();
            setValidatedValue(formatted);
            if (ref.current) {
                const newSelection = getNewSelection(validatedValue, formatted);
                ref.current.setSelectionRange(newSelection[0], newSelection[1]);
            }
        }
    }, [revalidate, getNewSelection, validatedValue, formatted]);
    const LayoutContainer = useFormLayoutCellContainer(columnSpan, minWidth, columnStart, columnEnd);
    if (type === 'hidden') {
        return jsxRuntime.exports.jsx("input", { type: "hidden", "data-type": inputType, id: id, name: name, value: raw }, void 0);
    }
    return (jsxRuntime.exports.jsx(LayoutContainer, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(FieldLabel, Object.assign({ name: name }, { children: value ? label : "\u00a0" }), void 0), jsxRuntime.exports.jsx(Input, Object.assign({}, rest, { ref: ref, onChange: handleChange, type: type, value: formatted, disabled: disabled, placeholder: label, "$borderStyle": "none", "$borderWidth": "1px", "$borderBottomStyle": "solid", "$borderBottomColor": error ? 'error' : hasSubmitted ? 'success' : 'grey.light', "$placeholderColor": error ? 'error.light' : 'grey', "$focusOutlineColor": error ? 'error' : hasSubmitted ? 'success' : 'primary' }), void 0), jsxRuntime.exports.jsx("input", { type: "hidden", "data-type": inputType, id: id, name: name, value: raw }, void 0), jsxRuntime.exports.jsx(FormFieldContext.Provider, Object.assign({ value: { raw, formatted, input: ref.current } }, { children: children }), void 0)] }), void 0) }, void 0));
};

var getNative = _getNative;

var defineProperty$1 = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty$1;

var defineProperty = _defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue$1(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue$1;

var baseAssignValue = _baseAssignValue,
    eq = eq_1;

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue$1(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty.call(object, key) && eq(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

var _assignValue = assignValue$1;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray$3 = Array.isArray;

var isArray_1 = isArray$3;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$1(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$1;

var baseGetTag = _baseGetTag,
    isObjectLike = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$4(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

var isSymbol_1 = isSymbol$4;

var isArray$2 = isArray_1,
    isSymbol$3 = isSymbol_1;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey$1(value, object) {
  if (isArray$2(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol$3(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey$1;

var memoize = memoize_1;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped$1(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped$1;

var memoizeCapped = _memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath$1 = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath$1;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

function arrayMap$1(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap$1;

var Symbol$1 = _Symbol,
    arrayMap = _arrayMap,
    isArray$1 = isArray_1,
    isSymbol$2 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol$1 ? Symbol$1.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString$1(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray$1(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap(value, baseToString$1) + '';
  }
  if (isSymbol$2(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _baseToString = baseToString$1;

var baseToString = _baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString$2(value) {
  return value == null ? '' : baseToString(value);
}

var toString_1 = toString$2;

var isArray = isArray_1,
    isKey = _isKey,
    stringToPath = _stringToPath,
    toString$1 = toString_1;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath$1(value, object) {
  if (isArray(value)) {
    return value;
  }
  return isKey(value, object) ? [value] : stringToPath(toString$1(value));
}

var _castPath = castPath$1;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex$1(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex$1;

var isSymbol$1 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey$1(value) {
  if (typeof value == 'string' || isSymbol$1(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY) ? '-0' : result;
}

var _toKey = toKey$1;

var assignValue = _assignValue,
    castPath = _castPath,
    isIndex = _isIndex,
    isObject$2 = isObject_1,
    toKey = _toKey;

/**
 * The base implementation of `_.set`.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @param {Function} [customizer] The function to customize path creation.
 * @returns {Object} Returns `object`.
 */
function baseSet$1(object, path, value, customizer) {
  if (!isObject$2(object)) {
    return object;
  }
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      lastIndex = length - 1,
      nested = object;

  while (nested != null && ++index < length) {
    var key = toKey(path[index]),
        newValue = value;

    if (key === '__proto__' || key === 'constructor' || key === 'prototype') {
      return object;
    }

    if (index != lastIndex) {
      var objValue = nested[key];
      newValue = customizer ? customizer(objValue, key, nested) : undefined;
      if (newValue === undefined) {
        newValue = isObject$2(objValue)
          ? objValue
          : (isIndex(path[index + 1]) ? [] : {});
      }
    }
    assignValue(nested, key, newValue);
    nested = nested[key];
  }
  return object;
}

var _baseSet = baseSet$1;

var baseSet = _baseSet;

/**
 * Sets the value at `path` of `object`. If a portion of `path` doesn't exist,
 * it's created. Arrays are created for missing index properties while objects
 * are created for all other missing properties. Use `_.setWith` to customize
 * `path` creation.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to modify.
 * @param {Array|string} path The path of the property to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.set(object, 'a[0].b.c', 4);
 * console.log(object.a[0].b.c);
 * // => 4
 *
 * _.set(object, ['x', '0', 'y', 'z'], 5);
 * console.log(object.x[0].y.z);
 * // => 5
 */
function set(object, path, value) {
  return object == null ? object : baseSet(object, path, value);
}

var set_1 = set;

function reduceConfigs(configs) {
    const headers = configs.map(c => c.headers || {}).reduce((a, b) => (Object.assign(Object.assign({}, a), b)));
    const config = configs.reduce((a, b) => (Object.assign(Object.assign({}, a), b)));
    return Object.assign(Object.assign({}, config), { headers });
}
function buildUrl(url, config) {
    let finalURL;
    if (url.match(/\w+:\/\//)) {
        finalURL = url;
    }
    else {
        const baseURL = config.baseURL || '';
        finalURL = [baseURL, url].map(part => part.replace(/^\/+|\/+$/g, '')).join('/');
    }
    if (config.params) {
        const qry = new URLSearchParams(config.params).toString();
        const joiner = finalURL.includes('?') ? '&' : '?';
        finalURL = [finalURL, qry].join(joiner);
    }
    return finalURL;
}
function handleJSON(res) {
    // Normally we would return res.json()
    // but this will fail if the body is empty (which our backend likes to do)
    // So this is a work around
    return res.text().then(text => text ? JSON.parse(text) : {});
}
function handleText(res) {
    return res.text();
}
function handleBlob(res) {
    return res.blob();
}
function handleFetchResponse(type) {
    return (res) => __awaiter(this, void 0, void 0, function* () {
        let data, error = '';
        try {
            switch (type) {
                case 'json':
                    data = yield handleJSON(res);
                    break;
                case 'blob':
                    data = yield handleBlob(res);
                    break;
                case 'text':
                    data = yield handleText(res);
                    break;
            }
        }
        catch (e) {
            error = String(e);
        }
        const response = {
            rawResponse: res,
            data,
        };
        if (res.status >= 400 || error) {
            const errorResponse = {
                response,
                error: error || res.statusText || 'Unknown Error',
                isRoadieApiError: true,
            };
            return Promise.reject(errorResponse);
        }
        else {
            return response;
        }
    });
}
function getFetchConfig(method, config) {
    const init = Object.assign({}, config);
    delete init.baseURL;
    delete init.responseType;
    return Object.assign(Object.assign({}, init), { method });
}
const DEFAULT_CONFIG$1 = {
    responseType: 'json',
    mode: 'cors',
    cache: 'no-cache',
    // credentials: 'same-origin', // include, *same-origin, omit
    headers: {
        'Accept': 'application/json, text/plain, */*',
    },
    redirect: 'follow',
    referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
};
class InternalAPI {
    constructor(defaultConfig = {}) {
        this.defaultConfig = reduceConfigs([DEFAULT_CONFIG$1, defaultConfig]);
    }
    updateConfig(configChanges = {}) {
        const finalConfig = reduceConfigs([this.defaultConfig, configChanges]);
        this.defaultConfig = finalConfig;
    }
    create(configChanges = {}) {
        const finalConfig = reduceConfigs([this.defaultConfig, configChanges]);
        return new InternalAPI(finalConfig);
    }
    get(url, config = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalConfig = reduceConfigs([this.defaultConfig, config]);
            const finalUrl = buildUrl(url, finalConfig);
            const fetchConfig = getFetchConfig('GET', finalConfig);
            const responseType = finalConfig.responseType || 'json';
            return fetch(finalUrl, fetchConfig).then(handleFetchResponse(responseType));
        });
    }
    post(url, body = {}, config = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const ourConfig = (body instanceof FormData) ? { body } : {
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const finalConfig = reduceConfigs([this.defaultConfig, ourConfig, config]);
            const finalUrl = buildUrl(url, finalConfig);
            const fetchConfig = getFetchConfig('POST', finalConfig);
            const responseType = finalConfig.responseType || 'json';
            return fetch(finalUrl, fetchConfig).then(handleFetchResponse(responseType));
        });
    }
    put(url, body = {}, config = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const ourConfig = (body instanceof FormData) ? { body } : {
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const finalConfig = reduceConfigs([this.defaultConfig, ourConfig, config]);
            const finalUrl = buildUrl(url, finalConfig);
            const fetchConfig = getFetchConfig('PUT', finalConfig);
            const responseType = finalConfig.responseType || 'json';
            return fetch(finalUrl, fetchConfig).then(handleFetchResponse(responseType));
        });
    }
    patch(url, body = {}, config = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const ourConfig = (body instanceof FormData) ? { body } : {
                body: JSON.stringify(body),
                headers: {
                    'Content-Type': 'application/json',
                }
            };
            const finalConfig = reduceConfigs([this.defaultConfig, ourConfig, config]);
            const finalUrl = buildUrl(url, finalConfig);
            const fetchConfig = getFetchConfig('PATCH', finalConfig);
            const responseType = finalConfig.responseType || 'json';
            return fetch(finalUrl, fetchConfig).then(handleFetchResponse(responseType));
        });
    }
    delete(url, config = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const finalConfig = reduceConfigs([this.defaultConfig, config]);
            const finalUrl = buildUrl(url, finalConfig);
            const fetchConfig = getFetchConfig('DELETE', finalConfig);
            const responseType = finalConfig.responseType || 'json';
            return fetch(finalUrl, fetchConfig).then(handleFetchResponse(responseType));
        });
    }
}

const initialApi = new InternalAPI();
function preCheck(config) {
    return __awaiter(this, void 0, void 0, function* () {
        const tokenData = yield check();
        if (tokenData) {
            set_1(config, 'headers.Access-Token', tokenData.access_token);
        }
    });
}
function create$1(internalApi) {
    const api = {};
    api.get = (url, config = {}) => __awaiter(this, void 0, void 0, function* () {
        yield preCheck(config);
        return internalApi.get(url, config);
    });
    api.post = (url, data, config = {}) => __awaiter(this, void 0, void 0, function* () {
        yield preCheck(config);
        return internalApi.post(url, data, config);
    });
    api.put = (url, data, config = {}) => __awaiter(this, void 0, void 0, function* () {
        yield preCheck(config);
        return internalApi.put(url, data, config);
    });
    api.patch = (url, data, config = {}) => __awaiter(this, void 0, void 0, function* () {
        yield preCheck(config);
        return internalApi.patch(url, data, config);
    });
    api.delete = (url, config = {}) => __awaiter(this, void 0, void 0, function* () {
        yield preCheck(config);
        return internalApi.delete(url, config);
    });
    api.version = version => create$1(internalApi.create({
        headers: {
            Accept: `application/json, text/plain, */*; version=${version}`
        }
    }));
    api.raw = () => internalApi.create();
    api.external = () => initialApi.create();
    api.setAccessToken = token => {
        console.log('new access token', token.substring(0, 9));
        internalApi.updateConfig({
            headers: {
                'Access-Token': token,
            }
        });
    };
    api.setProfileIdentityGUID = guid => {
        console.log('new profile identity guid', guid);
        internalApi.updateConfig({
            headers: {
                'Current-Profile-Guid': guid,
            }
        });
    };
    api.setApiBaseUrl = url => {
        internalApi.updateConfig({
            baseURL: `https://${url}`,
        });
    };
    api.resetApiBaseUrl = () => {
        sessionStorage.removeItem("api-base");
        window.location.reload();
    };
    api.clearAccessToken = () => {
        const config = internalApi.defaultConfig;
        if (config.headers && 'Access-Token' in config.headers) {
            delete config.headers['Access-Token'];
        }
    };
    api.downloadFile = ({ path, name, version = 3 }) => {
        return api.version(version).get(path, { responseType: 'blob' }).then(resp => {
            const data = resp.data;
            // @ts-expect-error Property 'msSaveBlob' does not exist on type 'Navigator'
            if (navigator.msSaveBlob) {
                // @ts-expect-error Property 'msSaveBlob' does not exist on type 'Navigator'
                navigator.msSaveBlob(data, name);
            }
            else {
                const url = window.URL.createObjectURL(data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        });
    };
    return api;
}
const api = create$1(initialApi.create({
    baseURL: process.env.REACT_APP_ROADIE_API_URL,
}));

let accessToken;
let accessExpire;
let refreshToken;
let refreshExpire;
/**
 * GUID that pertains to the currently logged in User
 */
let userIdentityGUID;
let refreshQueueId;
let logoutQueueId;
let ephemeral = false;
function isSet() {
    return !!(accessToken && accessExpire && refreshToken && refreshExpire);
}
function accessExpired() {
    return isSet() && (Date.now() > accessExpire.getTime());
}
function refreshExpired() {
    return isSet() && (Date.now() > refreshExpire.getTime());
}
function isLive() {
    return isSet() && (!accessExpired() || !refreshExpired());
}
let isChecking = false;
function __check() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!isSet()) {
            // console.log('no session set')
            return; // No session set.
        }
        else if (!accessExpired()) {
            // console.log('access has not expired', accessExpire, new Date())
            return; // Active
        }
        else if (!refreshExpired()) {
            // Access has expired but refresh has not.
            // console.log('access expired, but refresh not, so refreshing', refreshExpire, new Date())
            return yield refresh();
        }
        else {
            // Set but both expired.
            // console.log('Both expired.')
            clear();
            window.location.reload();
        }
    });
}
function check() {
    return __awaiter(this, void 0, void 0, function* () {
        // console.log('checking... ?')
        if (!isChecking) {
            // console.log('.. new check')
            isChecking = __check();
        }
        // console.log('awaiting isChecking')
        const resp = yield isChecking;
        // console.log('done awaiting isChecking', { resp })
        isChecking = false;
        // console.log('isChecking reset to false')
        return resp;
    });
}
function extractData(data, profileIdentityGUID) {
    accessToken = data.access_token;
    refreshToken = data.refresh_token;
    accessExpire = new Date(data.access_token_valid_until);
    refreshExpire = new Date(data.refresh_token_valid_until);
    if (data.user_identity_guid) {
        userIdentityGUID = data.user_identity_guid;
    }
    console.log('new access exires', accessExpire);
    if (!ephemeral && data.access_token) {
        localStorage.setItem('access-token', JSON.stringify(Object.assign(Object.assign({}, data), { user_identity_guid: userIdentityGUID })));
    }
    api.setAccessToken(accessToken);
    if (profileIdentityGUID) {
        api.setProfileIdentityGUID(profileIdentityGUID);
    }
    // console.log('set token', { accessExpire, refreshExpire, api })
    enqueueRefresh();
    enqueueLogout();
}
function isRoadieApiError(error) {
    return error.isRoadieApiError;
}
function refresh() {
    return __awaiter(this, void 0, void 0, function* () {
        if (document.hidden) {
            return; // Do not refresh when window is not active
        }
        // console.log('refreshing...')
        try {
            const resp = yield api.version(3).raw()
                .post('session/access_token', { refresh_token: refreshToken });
            extractData(resp.data);
            // console.log('.....REFRESHED....')
            return resp.data;
        }
        catch (error) {
            // console.log('got error refreshing', error)
            if (isRoadieApiError(error) && !error.response) {
                // There was no response, so network failed.
                // console.log('refresh network request failed, will try again in 5 secs')
                return new Promise(res => {
                    setTimeout(res, 1000 * 5);
                }).then(refresh);
            }
            else {
                // console.log('Could not refresh token.')
                clear();
                window.location.reload();
            }
        }
    });
}
function enqueueRefresh() {
    clearTimeout(refreshQueueId);
    if (!isSet()) {
        return;
    }
    const msUntilExpire = Math.abs(Date.now() - accessExpire.getTime());
    const minuteBefore = msUntilExpire - 60000;
    refreshQueueId = window.setTimeout(refresh, minuteBefore);
    // console.log('refresh enqueued')
}
function enqueueLogout() {
    clearTimeout(logoutQueueId);
    if (!isSet()) {
        return;
    }
    const msUntilExpire = Math.abs(Date.now() - refreshExpire.getTime());
    const minuteBefore = msUntilExpire - 60000;
    logoutQueueId = window.setTimeout(clear, minuteBefore);
    // console.log('logout enqueued')
}
function handleVisibilityChange() {
    // console.log('vis change')
    if (document.hidden) {
        // console.log('doc hidden')
        return;
    }
    check();
}
function watchVisibility() {
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
}
function unwatchVisibility() {
    document.removeEventListener('visibilitychange', handleVisibilityChange, false);
}
function create(data, config) {
    console.log('creating session', { data });
    ephemeral = (config === null || config === void 0 ? void 0 : config.ephemeral) || false;
    extractData(data, config === null || config === void 0 ? void 0 : config.identity);
    watchVisibility();
}
function getUserIdentityGUID() {
    return userIdentityGUID;
}
function setProfileIdentity(guid) {
    if (!ephemeral && accessToken) {
        localStorage.setItem('default-identity', guid);
        // We need to refresh the page to clear all data for current identity
        // When the page is reloaded, the new identity will be detected and used
        window.location.reload();
    }
}
function destroy() {
    return __awaiter(this, void 0, void 0, function* () {
        yield api.version(3).raw().delete('session', {
            body: JSON.stringify({ refresh_token: refreshToken }),
            headers: {
                'Content-Type': 'application/json',
            }
        }).catch(() => { });
    });
}
function clear() {
    unwatchVisibility();
    api.clearAccessToken();
    clearTimeout(refreshQueueId);
    clearTimeout(logoutQueueId);
    accessToken = null;
    refreshToken = null;
    accessExpire = null;
    refreshExpire = null;
    userIdentityGUID = undefined;
    localStorage.removeItem('default-identity');
    localStorage.removeItem('access-token');
}

var session = /*#__PURE__*/Object.freeze({
    __proto__: null,
    isSet: isSet,
    accessExpired: accessExpired,
    refreshExpired: refreshExpired,
    isLive: isLive,
    check: check,
    isRoadieApiError: isRoadieApiError,
    create: create,
    getUserIdentityGUID: getUserIdentityGUID,
    setProfileIdentity: setProfileIdentity,
    destroy: destroy,
    clear: clear
});

var FormLayout;
(function (FormLayout) {
    FormLayout[FormLayout["None"] = 0] = "None";
    FormLayout[FormLayout["AutoGrid"] = 1] = "AutoGrid";
})(FormLayout || (FormLayout = {}));
const FormContext = createContext({
    layout: FormLayout.AutoGrid,
    hasSubmitted: false,
    errors: {},
    checkValidation() {
        return undefined;
    }
});
const useFormRevalidate = () => {
    const form = useContext(FormContext);
    const revalidate = useCallback(() => {
        if (form.hasSubmitted) {
            return form.checkValidation();
        }
    }, [form]);
    return revalidate;
};
const useFormFieldError = (name) => {
    name = normalizePath(name);
    const form = useContext(FormContext);
    const error = form.errors[name];
    return {
        error: (error && error.length && error[0].message) || undefined,
        hasSubmitted: form.hasSubmitted,
    };
};
const useFormBaseError = () => {
    const form = useContext(FormContext);
    const error = form.baseError;
    return error;
};
const useFormId = () => {
    const form = useContext(FormContext);
    const id = form.id;
    return id;
};
const useFormErrorCount = () => {
    const form = useContext(FormContext);
    return Object.keys(form.errors).length;
};
const useFormLayout = () => {
    const form = useContext(FormContext);
    return form.layout;
};
const isInput = (e) => {
    return e.tagName.toLowerCase() === 'input';
};
const isTextArea = (e) => {
    return e.tagName.toLowerCase() === 'textarea';
};
const isSelect = (e) => {
    return e.tagName.toLowerCase() === 'select';
};
const isFormField = (e) => {
    return isInput(e) || isTextArea(e) || isSelect(e);
};
const useIsMounted = () => {
    const mounted = useRef(true);
    useEffect(() => {
        return () => {
            mounted.current = false;
        };
    }, []);
    return mounted.current;
};
function reduceError(e) {
    if (!e) {
        return {};
    }
    else if (Array.isArray(e)) {
        return { base: e[0] };
    }
    else if (typeof e === 'string') {
        return { base: e };
    }
    else {
        const results = {};
        Object.keys(e).forEach(key => {
            const value = e[key];
            const result = Array.isArray(value) ? value[0] : value;
            results[key] = result;
        });
        return results;
    }
}
function getErrorsFromApi(e) {
    if (e.response && e.response.data) {
        if ('error' in e.response.data) {
            return reduceError(e.response.data.error);
        }
        else if ('errors' in e.response.data) {
            return reduceError(e.response.data.errors);
        }
    }
}
const Form$1 = props => {
    const { layout = FormLayout.AutoGrid, id, validations, onSubmit, children } = props;
    const renderChildren = useCallback(() => {
        if (layout === FormLayout.AutoGrid) {
            const { minColumnWidth = 250, maxColumns = 3, gridGap = [0, 30] } = props;
            return (jsxRuntime.exports.jsx(AutoGrid.Grid, Object.assign({ minColumnWidth: minColumnWidth, maxColumns: maxColumns, gridGap: gridGap }, { children: children }), void 0));
        }
        else if (layout === FormLayout.None) {
            return children;
        }
    }, [children, layout, props]);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [submitting, setSubitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [baseError, setBaseError] = useState();
    const ref = useRef(null);
    const mounted = useIsMounted();
    const checkValidation = useCallback(() => {
        const fields = Array.from(ref.current.elements).filter(isFormField);
        const data = fields.reduce((data, field) => {
            if (field.name) {
                set_1(data, field.name, field.value);
            }
            return data;
        }, {});
        const errors = (validations || []).reduce((errors, validation) => {
            const [_name, rules, message] = validation;
            const name = normalizePath(_name);
            const element = fields.find(e => e.name === name);
            const dataset = element === null || element === void 0 ? void 0 : element.dataset;
            let prevalidate;
            if (dataset && dataset.type) {
                const inputType = InputTypes[dataset.type];
                prevalidate = inputType.format.prevalidate;
            }
            try {
                let valueToValidate = element === null || element === void 0 ? void 0 : element.value;
                if (prevalidate) {
                    valueToValidate = prevalidate(valueToValidate);
                }
                check$1(rules, valueToValidate, message);
            }
            catch (err) {
                if (isValidationError(err)) {
                    if (!errors[name]) {
                        errors[name] = [];
                        errors[name].push(err);
                    }
                }
            }
            return errors;
        }, {});
        setErrors(errors);
        const errorCount = Object.keys(errors).length;
        return [errorCount, data];
    }, [validations]);
    const handleUnknownError = useCallback(() => {
        setBaseError('An error occured, please try again or contact support.');
    }, []);
    const handleRoadieApiError = useCallback((ae) => {
        const errors = getErrorsFromApi(ae);
        if (errors) {
            const fields = Array.from(ref.current.elements).filter(isFormField);
            const fieldNames = fields.map(field => field.name).filter(name => !!name && errors[name]);
            if (!fieldNames.length && !errors.base) {
                handleUnknownError();
            }
            if (errors.base) {
                setBaseError(errors.base);
            }
            else if (errors.route && !fieldNames.includes('route')) {
                setBaseError(errors.route);
            }
            if (fieldNames.length) {
                const fieldErrors = fieldNames.reduce((memo, next) => {
                    const message = errors[next];
                    memo[next] = [new Error(Array.isArray(message) ? message[0] : message)];
                    return memo;
                }, {});
                setErrors(fieldErrors);
            }
        }
        else {
            handleUnknownError();
        }
    }, [handleUnknownError]);
    const handleSubmit = useCallback((event) => __awaiter(void 0, void 0, void 0, function* () {
        event.preventDefault();
        setBaseError(undefined);
        const [errorCount, data] = checkValidation();
        if (errorCount) {
            setHasSubmitted(true);
        }
        else {
            if (onSubmit) {
                setSubitting(true);
                try {
                    yield onSubmit(data);
                }
                catch (e) {
                    setHasSubmitted(true);
                    if (isRoadieApiError(e)) {
                        handleRoadieApiError(e);
                    }
                    else {
                        handleUnknownError();
                    }
                }
                if (mounted) {
                    setSubitting(false);
                }
            }
        }
    }), [checkValidation, onSubmit, mounted, handleRoadieApiError, handleUnknownError]);
    return (jsxRuntime.exports.jsx(FormContext.Provider, Object.assign({ value: { layout, id, hasSubmitted, errors, baseError, checkValidation } }, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "relative" }, { children: jsxRuntime.exports.jsxs("form", Object.assign({ id: id, ref: ref, onSubmit: handleSubmit }, { children: [renderChildren(), submitting ? jsxRuntime.exports.jsx(Loader, { mode: "overlay" }, void 0) : null] }), void 0) }), void 0) }), void 0));
};
const BasicForm = props => (jsxRuntime.exports.jsx(Form$1, Object.assign({}, props, { layout: FormLayout.None }), void 0));
const AutoGridForm = props => (jsxRuntime.exports.jsx(Form$1, Object.assign({}, props, { layout: FormLayout.AutoGrid }), void 0));

const RadialButton = props => {
    const { checked, disabled } = props;
    const checkedColor = (() => {
        if (disabled) {
            return checked ? 'grey.light' : 'clear';
        }
        else {
            return checked ? 'primary' : 'clear';
        }
    })();
    return (jsxRuntime.exports.jsx(Box.Model, Object.assign({ "$cursor": disabled ? 'default' : 'pointer', "$borderColor": disabled ? 'grey.lighter' : 'grey.light', "$borderWidth": "1px", "$borderStyle": "solid", "$borderRadius": "100%", "$width": "3rem", "$height": "3rem" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$alignItems": "center", "$justifyContent": "center", "$width": "100%", "$height": "100%" }, { children: jsxRuntime.exports.jsx(Box.Model, { "$backgroundColor": checkedColor, "$borderRadius": "100%", "$width": "1.6rem", "$height": "1.6rem" }, void 0) }), void 0) }), void 0));
};

const RadioContext = createContext({ value: '', disabled: false, onChange: () => undefined });
const RadioField = props => {
    const { label, value, disabled: selfDisabled } = props;
    const group = useContext(RadioContext);
    const checked = value === group.value;
    const disabled = group.disabled || selfDisabled;
    const { onChange } = group;
    const activatedHandler = useCallback(() => {
        if (value && onChange && !disabled) {
            onChange(value);
        }
    }, [disabled, onChange, value]);
    const handleKeyPress = useCallback(event => {
        if (event.key === ' ') {
            activatedHandler();
        }
    }, [activatedHandler]);
    return (jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "3rem 1fr", "$gridGap": "1.5rem", "$alignItems": "center", "$focusOutlineColor": "primary", "$cursor": disabled ? 'default' : 'pointer', tabIndex: 0, onKeyPress: handleKeyPress, onClick: activatedHandler }, { children: [jsxRuntime.exports.jsx(RadialButton, { checked: checked }, void 0), jsxRuntime.exports.jsx(Text.Body1, Object.assign({ "$textAlign": "left" }, { children: label }), void 0)] }), void 0));
};
const RadioGroup = props => {
    const { id: fieldId, name: _name, children, disabled = false, defaultValue, value: externalValue, onChange } = props;
    const name = _name && normalizePath(_name);
    const formId = useFormId();
    const id = fieldId ? fieldId : (formId && name) ? `${formId}-${name}` : undefined;
    const [internalValue, setInternalValue] = useState(defaultValue);
    const value = externalValue || internalValue || '';
    const handleChange = useCallback((newValue) => {
        setInternalValue(newValue === value ? '' : newValue);
        if (onChange) {
            onChange(name, newValue);
        }
    }, [name, value, onChange]);
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$display": "contents" }, { children: [jsxRuntime.exports.jsx(RadioContext.Provider, Object.assign({ value: { value, disabled, onChange: handleChange } }, { children: children }), void 0), jsxRuntime.exports.jsx("input", { type: "hidden", id: id, name: name, value: value }, void 0)] }), void 0));
};

const CheckboxFace = props => {
    const { checked, disabled, hasError, onClick } = props;
    return (jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": "2rem", "$gridTemplateRows": "2rem", "$width": "2rem", "$height": "2rem", "$alignItems": "center", "$justifyContent": "stretch", "$borderRadius": "3px", "$borderStyle": "solid", "$borderWidth": "1px", "$color": "white", "$borderColor": hasError ? 'error' : (disabled ? 'grey.light' : 'primary'), "$backgroundColor": checked ? (hasError ? 'error' : 'primary') : 'white', onClick: onClick }, { children: jsxRuntime.exports.jsx(Icon.Checkmark, { "$width": "1.8rem", "$height": "1.8rem" }, void 0) }), void 0));
};
const CheckLabel = props => {
    const { error, hasSubmitted } = useFormFieldError(props.name);
    const valid = hasSubmitted && !error;
    const label = valid ? jsxRuntime.exports.jsxs(jsxRuntime.exports.Fragment, { children: ["\u2713 ", props.children] }, void 0) : props.children;
    return (jsxRuntime.exports.jsx("label", Object.assign({ onClick: props.onClick }, { children: jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$textAlign": "left", "$color": error ? 'error' : valid ? 'success' : undefined }, { children: error || label }), void 0) }), void 0));
};
const Checkbox = props => {
    const { id: fieldId, name: _name, label, value, defaultChecked, checked: externalChecked, disabled = false, columnSpan, minWidth, columnStart, columnEnd, onChange, children } = props;
    const name = normalizePath(_name);
    const formId = useFormId();
    const id = fieldId ? fieldId : (formId && name) ? `${formId}-${name}` : undefined;
    const revalidate = useFormRevalidate();
    const { error } = useFormFieldError(name);
    const [validatedChecked, setValidatedChecked] = useState(null);
    const [internalValue, setInternalValue] = useState(defaultChecked);
    const checked = externalChecked || internalValue || false;
    useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedChecked !== checked) {
            revalidate();
            setValidatedChecked(checked);
        }
    }, [revalidate, validatedChecked, checked]);
    const checkHandler = useCallback(() => {
        if (disabled) {
            return; // don't handle
        }
        const newChecked = !checked;
        setInternalValue(newChecked);
        if (onChange) {
            onChange(newChecked);
        }
    }, [checked, disabled, onChange]);
    const LayoutContainer = useFormLayoutCellContainer(columnSpan, minWidth, columnStart, columnEnd);
    return (jsxRuntime.exports.jsx(LayoutContainer, { children: jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "2rem auto", "$gridGap": "1rem", "$alignItems": "start" }, { children: [jsxRuntime.exports.jsxs(Container.GridItem, Object.assign({ "$justifySelf": "center" }, { children: [jsxRuntime.exports.jsx(CheckboxFace, { checked: checked, disabled: disabled, hasError: !!error, onClick: checkHandler }, void 0), jsxRuntime.exports.jsx("input", { type: "hidden", id: id, name: name, value: checked ? value : '' }, void 0)] }), void 0), jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ "$padding": ".2rem 0 0" }, { children: label ? (jsxRuntime.exports.jsx(CheckLabel, Object.assign({ onClick: checkHandler, name: name }, { children: label }), void 0)) : null }), void 0), jsxRuntime.exports.jsx("div", {}, void 0), jsxRuntime.exports.jsx("div", { children: children ? (children(checked, disabled, error)) : null }, void 0)] }), void 0) }, void 0));
};

const Select = guitar('select', select);
const SelectField = props => {
    var _a;
    const { id: fieldId, name: _name, label, defaultValue, value: externalValue, disabled, columnSpan, minWidth, columnStart, columnEnd, onChange } = props;
    const name = normalizePath(_name);
    const formId = useFormId();
    const id = fieldId ? fieldId : (formId && name) ? `${formId}-${name}` : undefined;
    const revalidate = useFormRevalidate();
    const { error, hasSubmitted } = useFormFieldError(name);
    const [validatedValue, setValidatedValue] = useState(null);
    const [internalValue, setInternalValue] = useState(defaultValue);
    const handleChange = useCallback(event => {
        event.persist();
        if (onChange) {
            onChange(event);
        }
        setInternalValue(event.target.value);
    }, [onChange]);
    const value = (_a = externalValue !== null && externalValue !== void 0 ? externalValue : internalValue) !== null && _a !== void 0 ? _a : '';
    useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedValue !== value) {
            revalidate();
            setValidatedValue(value);
        }
    }, [revalidate, validatedValue, value]);
    const LayoutContainer = useFormLayoutCellContainer(columnSpan, minWidth, columnStart, columnEnd);
    return (jsxRuntime.exports.jsx(LayoutContainer, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(FieldLabel, Object.assign({ name: name }, { children: value ? label : "\u00a0" }), void 0), jsxRuntime.exports.jsxs(Select, Object.assign({ id: id, name: name, "$color": error ? 'error.light' : value ? undefined : 'grey', onChange: handleChange, value: value, disabled: disabled, "$borderStyle": "none", "$borderWidth": "1px", "$borderBottomStyle": "solid", "$borderBottomColor": error ? 'error' : hasSubmitted ? 'success' : 'grey.light', "$placeholderColor": error ? 'error.light' : 'grey', "$focusOutlineColor": error ? 'error' : hasSubmitted ? 'success' : 'primary' }, { children: [label && jsxRuntime.exports.jsx("option", Object.assign({ disabled: !!value, value: "" }, { children: label }), void 0), props.children] }), void 0), jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$right": "0.5rem", "$bottom": "1rem", "$width": "1rem" }, { children: jsxRuntime.exports.jsx(Icon.TriangleDown, { "$color": error ? 'error' : hasSubmitted ? 'success' : 'primary' }, void 0) }), void 0)] }), void 0) }, void 0));
};

// FIXME: Replace with RoadieUI
const Visible = styled.div `
  visibility: ${props => props.$visible === false ? 'hidden' : 'visible'}
`;
function convertRemToPixels(rem) {
    // Stolen from https://stackoverflow.com/questions/36532307/rem-px-in-javascript  
    return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}
function computeWidthString(value) {
    var _a;
    // Get exact pixel number from strings like "20px" or "20rem"
    const [, num, unit] = (_a = String(value).match(/(\d+)([^\d]*)/)) !== null && _a !== void 0 ? _a : [];
    switch (unit) {
        case 'px': return Number(num);
        case '': return Number(num);
        case 'rem': return convertRemToPixels(Number(num));
        default: throw new Error(`Don't know how to convert ${unit}`);
    }
}
const Hovercard = props => {
    const { dockBelow, dockRight, width, offsetY, color, onClickAway } = props;
    const [fullWidth, setFullWidth] = useState();
    const containerRef = useRef(null);
    const handleRootClick = useCallback((event) => {
        if (!dockBelow || dockBelow.contains(event.target)) {
            return;
        }
        if (onClickAway) {
            event.preventDefault();
            onClickAway(event.target);
        }
    }, [dockBelow, onClickAway]);
    useEffect(() => {
        const root = document.getElementById('root');
        if (!root) {
            return;
        }
        root.addEventListener('click', handleRootClick, true);
        return () => {
            root.removeEventListener('click', handleRootClick, true);
        };
    }, [handleRootClick]);
    useEffect(() => {
        if (dockBelow && width === 'auto' && containerRef.current) {
            const b = containerRef.current.getBoundingClientRect();
            setFullWidth(b.width);
        }
    }, [width, dockBelow]);
    if (!dockBelow) {
        return null;
    }
    if (width === 'auto' && !fullWidth) {
        // We need to calculated the fullWidth
        return (jsxRuntime.exports.jsx(Visible, Object.assign({ "$visible": false }, { children: jsxRuntime.exports.jsx(Filter.DropShadow, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ ref: containerRef, "$position": "absolute", "$left": "0", "$top": "0", "$width": "auto", "$borderRadius": "4px", "$backgroundColor": color, "$overflow": "hidden" }, { children: props.children }), void 0) }, void 0) }), void 0));
    }
    const rect = dockBelow.getBoundingClientRect();
    const x = (fullWidth && dockRight) ? `${(rect.left + rect.width) - fullWidth}px` : (width && dockRight && width !== 'auto') ? `${(rect.left + rect.width) - computeWidthString(width)}px` : `${rect.left}px`;
    const y = `${rect.top + rect.height + (offsetY || 0) + window.pageYOffset}px`;
    const w = width || `${fullWidth || rect.width}px`;
    const hovercardPortal = document.getElementById('hovercard');
    if (!hovercardPortal) {
        console.warn('Cannot find div#hovercard element. It is required to render into.');
        return jsxRuntime.exports.jsx(jsxRuntime.exports.Fragment, {}, void 0);
    }
    return createPortal(jsxRuntime.exports.jsx(Visible, { children: jsxRuntime.exports.jsx(Filter.DropShadow, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$left": x, "$top": y, "$width": w, "$borderRadius": "4px", "$backgroundColor": color, "$overflow": "hidden" }, { children: props.children }), void 0) }, void 0) }, void 0), hovercardPortal);
};

function getMediaSize() {
    const width = window.innerWidth;
    const size = Object.keys(sizes).find(key => {
        const { min, max } = sizes[key];
        let isMin = true, isMax = true;
        if (min && min > width) {
            isMin = false;
        }
        if (max && max < width) {
            isMax = false;
        }
        return isMin && isMax;
    });
    return (size || 'desktop');
}
const useMediaSize = () => {
    const [mediaSize, setMediaSize] = useState(getMediaSize);
    const handleResize = useCallback(() => {
        const nextMediaSize = getMediaSize();
        if (nextMediaSize !== mediaSize) {
            setMediaSize(nextMediaSize);
        }
    }, [mediaSize]);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    });
    return mediaSize;
};
const useIsMediaSize = (...sizes) => {
    const mediaSize = useMediaSize();
    return sizes.includes(mediaSize);
};

/**
 * A breakpoint helper for mobile, tablet or desktop
 * - Uses {@link useMediaSize} under the hood.
 *
 * @example
 *
 * useBreakPoints({ mobile: '100px', tablet: '200px', desktop: '300px'
 * // returns '100px' for mobile, 200px for tablet, and 300px for desktop.
 *
 * // Each size is optional and will return undefined if not speficied
 * useBreakPoints({ mobile: '2rem' })
 * // returns `undefined` for desktop and tablet sizes.
 */
const useBreakPoints = (params) => {
    const mediaSize = useMediaSize();
    return params[mediaSize];
};

const PREFIX = 'rui';
let idStore = 0;
/**
 * @param prefix String preppended to ID (*In dev environment only*)
 * @returns Unique ID
 */
const useId = (prefix) => {
    let _prefix = '';
    const [id] = React__default.useState(() => PREFIX + String(idStore++));
    return _prefix + id;
};

/** Create a custom css class on the fly
 *
 * @example
 * const positioned = useCreateCssClass(`
 *   position: absolute;
 *   top: 100px;
 * `)
 * return (
 *   <div className={positioned} />
 * )
 */
const useCreateCssClass = (definition) => {
    const className = useId();
    useInsertionEffect(() => {
        const head = document.querySelector('head');
        if (!head) {
            return;
        }
        const style = document.createElement('style');
        style.setAttribute('data-className', className);
        style.innerHTML = `.${className} { ${definition} }`;
        head.appendChild(style);
        return () => {
            style.remove();
        };
    }, [className, definition]);
    return className;
};

var root = _root;

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now$1 = function() {
  return root.Date.now();
};

var now_1 = now$1;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

var baseTrim = _baseTrim,
    isObject$1 = isObject_1,
    isSymbol = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol(value)) {
    return NAN;
  }
  if (isObject$1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber$1;

var isObject = isObject_1,
    now = now_1,
    toNumber = toNumber_1;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  wait = toNumber(wait) || 0;
  if (isObject(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

var debounce_1 = debounce;

const useDebounce = (fn, ms) => {
    const [dfn, setDfn] = useState(() => debounce_1(fn, ms));
    useEffect(() => {
        setDfn(() => debounce_1(fn, ms));
    }, [fn, ms]);
    return dfn;
};

/** Hook to see what a value was on last render cycle */
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}
/** Hook to determine if a value has changed since the last render cycle
 *
 * Useful inside a useEffect when you want to determine which dependencies
 * triggered it to run.
 */
function useHasChanged(value) {
    const prev = usePrevious(value);
    return prev !== value;
}

const useHasFocus = (ref) => {
    const [hasFocus, setHasFocus] = useState(() => ref ? (ref === document.activeElement) : false);
    const handleFocus = useCallback(() => {
        const isTrue = (ref === document.activeElement);
        if (isTrue) {
            setHasFocus(isTrue);
        }
        else {
            // Add slight delay when clicking away,
            // (otherwise the layout shift can cancel clicks on whatever is gaining focus) 
            setTimeout(() => {
                setHasFocus(isTrue);
            }, 200);
        }
    }, [ref]);
    useEffect(() => {
        if (ref) {
            ref.addEventListener('focus', handleFocus);
            ref.addEventListener('blur', handleFocus);
            if (ref === document.activeElement) {
                setHasFocus(true);
            }
        }
        return () => {
            if (ref) {
                ref.removeEventListener('focus', handleFocus);
                ref.removeEventListener('blur', handleFocus);
            }
        };
    }, [ref, handleFocus]);
    return hasFocus;
};

/** React hook to lock the document from scrolling
 *
 * Useful when preventing user interaction such as showing a modal.
 *
 * @param on whether or not to apply. Defaults to `true`.
 */
const useLockHtmlScroll = (on = true) => {
    const className = useCreateCssClass('overflow: hidden;');
    useEffect(() => {
        const html = document.querySelector('html');
        if (html && on) {
            html.classList.add(className);
        }
        return () => {
            if (html) {
                html.classList.remove(className);
            }
        };
    }, [on, className]);
};

const useModalShowing = () => {
    const [modalShowing, setModalShowing] = useState(false);
    const [modalRef] = useState(() => document.getElementById('modal'));
    const handleModalChildren = useCallback(() => {
        setModalShowing((modalRef === null || modalRef === void 0 ? void 0 : modalRef.hasChildNodes()) || false);
    }, [modalRef]);
    useEffect(() => {
        if (modalRef) {
            const observer = new MutationObserver(handleModalChildren);
            observer.observe(modalRef, { childList: true });
            return () => {
                observer.disconnect();
            };
        }
    }, [modalRef, handleModalChildren]);
    return modalShowing;
};

const usePreferredColorScheme = () => {
    const [darkMode] = useState(() => window.matchMedia('(prefers-color-scheme: dark)'));
    const getCurrentTheme = useCallback(() => {
        const defaultTheme = 'light';
        try {
            const lsThemeValue = localStorage.getItem('app-theme');
            switch (lsThemeValue) {
                case 'light':
                case 'dark':
                    return lsThemeValue;
                case 'system':
                    return darkMode.matches ? 'dark' : 'light';
                default:
                    return defaultTheme;
            }
        }
        catch (e) {
            return defaultTheme;
        }
    }, [darkMode.matches]);
    const [theme, setTheme] = useState(getCurrentTheme);
    const handleMode = useCallback(() => {
        const currentTheme = getCurrentTheme();
        setTheme(currentTheme);
    }, [getCurrentTheme]);
    const handleStorage = useCallback((event) => {
        if (event.key === 'app-theme') {
            handleMode();
        }
    }, [handleMode]);
    useEffect(() => {
        if ('addEventListener' in darkMode) {
            darkMode.addEventListener('change', handleMode);
        }
        window.addEventListener('storage', handleStorage);
        return () => {
            if ('addEventListener' in darkMode) {
                darkMode.removeEventListener('change', handleMode);
            }
            window.removeEventListener('storage', handleStorage);
        };
    }, [darkMode, handleMode, handleStorage]);
    return theme;
};

const useQueryParam = (name) => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const value = query.get(name);
    return value;
};

var toString = toString_1;

/** Used to generate unique IDs. */
var idCounter = 0;

/**
 * Generates a unique ID. If `prefix` is given, the ID is appended to it.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {string} [prefix=''] The value to prefix the ID with.
 * @returns {string} Returns the unique ID.
 * @example
 *
 * _.uniqueId('contact_');
 * // => 'contact_104'
 *
 * _.uniqueId();
 * // => '105'
 */
function uniqueId(prefix) {
  var id = ++idCounter;
  return toString(prefix) + id;
}

var uniqueId_1 = uniqueId;

const RawBanner = props => {
    const { children } = props;
    const bannerPortal = document.getElementById('banner');
    if (!bannerPortal) {
        console.warn('Expecting element div.#banner to exist on page.');
        return jsxRuntime.exports.jsx(jsxRuntime.exports.Fragment, {}, void 0);
    }
    return createPortal((jsxRuntime.exports.jsx("div", Object.assign({ className: "roadie-ui-banner" }, { children: children }), void 0)), bannerPortal);
};
const InfoBanner = props => {
    return (jsxRuntime.exports.jsx(RawBanner, { children: jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "2rem 1fr 2rem", "$gridGap": "1rem", "$textAlign": "center", "$padding": "1rem", "$backgroundColor": props.type || 'alert', "$color": "white" }, { children: [jsxRuntime.exports.jsx("span", {}, void 0), jsxRuntime.exports.jsx("div", Object.assign({ className: "info-banner-contents" }, { children: props.children }), void 0), props.onRequestClose ? (jsxRuntime.exports.jsx(Icon.CloseCircle, { "$cursor": "pointer", onClick: props.onRequestClose }, void 0)) : null] }), void 0) }, void 0));
};
const QuickBanner = props => {
    const { onClose } = props;
    const open = useToggle(true);
    const handleClose = useCallback(() => {
        open.off();
        if (onClose) {
            onClose();
        }
    }, [open, onClose]);
    if (open.value) {
        return (jsxRuntime.exports.jsx(InfoBanner, Object.assign({ type: props.type, onRequestClose: handleClose }, { children: jsxRuntime.exports.jsx(Text.Subtitle1, { children: props.children }, void 0) }), void 0));
    }
    return null;
};
const QuickBannerContext = createContext(() => {
    return (() => null);
});
const QuickBannerProvider = props => {
    const [stack, setStack] = useState([]);
    const remove = useCallback((id) => {
        setStack(stack => stack.filter(details => details.id !== id));
    }, []);
    const push = useCallback((details) => {
        const id = uniqueId_1();
        const pop = () => remove(id);
        setStack(stack => [...stack, Object.assign({ id, pop }, details)]);
        return pop;
    }, [remove]);
    console.log("stack count", stack.length);
    return (jsxRuntime.exports.jsxs("div", { children: [jsxRuntime.exports.jsx(QuickBannerContext.Provider, Object.assign({ value: push }, { children: props.children }), void 0), jsxRuntime.exports.jsx("div", { children: stack.map(details => (jsxRuntime.exports.jsx(QuickBanner, Object.assign({ type: details.type, onClose: details.pop }, { children: details.message }), details.id))) }, void 0)] }, void 0));
};

/** Hook to create a banner in a callback function.
 *
 * @requires QuickBannerProvider
 *
 * Must have a quick banner provider somewhere up the ancestry tree.
 *
 * @returns a function to remove the banner
 *
 * @example
 * const Example = () => {
 *   const quickBanner = useQuickBanner()
 *   const handler = useCallback(() => {
 *     const closeBanner = quickBanner('success', 'Hello, World') // opens the banner
 *     closeBanner() // closes the banner
 *   }, [quickBanner])
 * }
 */
const useQuickBanner = () => {
    const push = useContext(QuickBannerContext);
    const wrapper = useCallback((type, message) => push({ type, message }), [push]);
    return wrapper;
};

const useRect = () => {
    const [elementRef, setElementRef] = useState();
    const [elementRect, setElementRect] = useState(new DOMRect());
    const handleElementSize = useCallback((element) => {
        setElementRect(element.getBoundingClientRect());
    }, []);
    const handleElementResize = useCallback(() => {
        if (elementRef) {
            handleElementSize(elementRef);
        }
    }, [handleElementSize, elementRef]);
    useEffect(() => {
        if (elementRef) {
            const observer = new ResizeObserver(handleElementResize);
            observer.observe(elementRef);
            handleElementSize(elementRef);
            return () => {
                observer.disconnect();
            };
        }
    }, [handleElementResize, handleElementSize, elementRef]);
    const handleElementRef = useCallback((ref) => {
        setElementRef(ref);
    }, []);
    return [elementRect, handleElementRef];
};

const useThemeColor = (color$1) => {
    const theme = useTheme();
    return color(color$1)({ theme });
};

/** Generate a TimeRange and get updates when it changes
 *
 * @param {function} getInitial - A function that returns an initial `TimeRange`
 * @description This allows you to generate a `TimeRange` and pass it around to any other
 * component and when it gets updated (no matter who updates it) it will trigger a state
 * change to re-render with the new values.
 */
const useTimeRange = (getInitial, changeHandler) => {
    const [range] = useState(getInitial);
    const [, updateState] = useState({});
    const handleChange = useCallback((range) => {
        updateState({});
        if (changeHandler) {
            changeHandler(range);
        }
    }, [changeHandler]);
    useEffect(() => {
        range.listen(handleChange);
        return () => range.unlisten(handleChange);
    });
    return range;
};

function useToggle(defaultValue) {
    const initialValue = React__default.useRef(defaultValue !== null && defaultValue !== void 0 ? defaultValue : false).current;
    const [value, setValue] = React__default.useState(initialValue);
    return {
        initialValue,
        value: value,
        toggle: () => {
            setValue(!value);
        },
        on: () => {
            setValue(true);
        },
        off: () => {
            setValue(false);
        },
        reset: () => {
            setValue(initialValue);
        }
    };
}

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    useBreakPoints: useBreakPoints,
    useCreateClass: useCreateCssClass,
    useDebounce: useDebounce,
    useHasChanged: useHasChanged,
    useHasFocus: useHasFocus,
    useId: useId,
    useLockHtmlScroll: useLockHtmlScroll,
    useMediaSize: useMediaSize,
    useIsMediaSize: useIsMediaSize,
    useModalShowing: useModalShowing,
    usePreferredColorScheme: usePreferredColorScheme,
    useQueryParam: useQueryParam,
    useQuickBanner: useQuickBanner,
    useRect: useRect,
    useThemeColor: useThemeColor,
    useTimeRange: useTimeRange,
    useToggle: useToggle
});

/**
 * ## Dropdown
 *
 * General purpose dropdown component that just handles rendering a UI in a Hovercard and showing some related text inside of the Dropdown box. This is to allow many types of dropdowns to be supported since it only imposes retrictions on default and initial properties.
 *
 * The Hovercard UI has to control selected, disabled, and hovered states and sets the Dropdown value via the `setValue` callback in the `children` render function.
 *
 * The dropdown can be a controlled or independenct component depending on the usage requirements and the provided props (i.e. `label` and `value`).
 */
const Dropdown$1 = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const { defaultLabel, initialLabel, initialValue, children, onChange } = props;
    const padding = (_a = props.padding) !== null && _a !== void 0 ? _a : '0.4rem';
    const state = (_b = props.state) !== null && _b !== void 0 ? _b : 'normal';
    const uniqueId = useId();
    const id = (_c = props.id) !== null && _c !== void 0 ? _c : uniqueId;
    const dropdownRef = React__default.useRef(null);
    const [hovercardOpen, setHovercardOpen] = React__default.useState(false);
    const defaultValue = (_d = props.defaultValue) !== null && _d !== void 0 ? _d : '';
    const [internalValue, setInternalValue] = React__default.useState((_e = initialValue !== null && initialValue !== void 0 ? initialValue : defaultValue) !== null && _e !== void 0 ? _e : '');
    const value = (_g = (_f = props.value) !== null && _f !== void 0 ? _f : internalValue) !== null && _g !== void 0 ? _g : '';
    const [internalLabel, setInternalLabel] = React__default.useState((_h = (value === defaultValue ? defaultLabel : initialLabel)) !== null && _h !== void 0 ? _h : '');
    const label = (_k = (_j = props.label) !== null && _j !== void 0 ? _j : internalLabel) !== null && _k !== void 0 ? _k : '';
    const handleClick = React__default.useCallback(() => {
        if (state === 'disabled') {
            return;
        }
        setHovercardOpen(open => !open);
    }, [state]);
    const handleChange = React__default.useCallback((value, label) => () => {
        var _a;
        onChange === null || onChange === void 0 ? void 0 : onChange(value);
        setInternalValue(value);
        setInternalLabel((_a = (value === defaultValue ? defaultLabel : label)) !== null && _a !== void 0 ? _a : '');
        setHovercardOpen(false);
    }, [defaultLabel, defaultValue, onChange]);
    return (jsxRuntime.exports.jsxs(jsxRuntime.exports.Fragment, { children: [jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ id: id, ref: dropdownRef, tabIndex: 0, onClick: handleClick, "$gridTemplateColumns": "1fr 1rem", "$gridGap": "0.4rem", "$padding": padding, "$cursor": "default", "$borderStyle": "solid", "$borderWidth": "0 0 1px", "$borderColor": state === 'error' ?
                    'error' :
                    (state === 'success' ?
                        'success' :
                        (state === 'disabled' ? 'grey.light@0.7' : 'grey.light')), "$focusOutlineColor": state === 'error' ? 'error' : (state === 'success' ? 'success' : 'primary') }, { children: [jsxRuntime.exports.jsx(Box.Model, Object.assign({ "$color": state === 'error' ?
                            'error.light' :
                            (value ?
                                (state === 'disabled' ? 'grey@0.7' : 'black') :
                                (state === 'disabled' ? 'grey.light@0.7' : 'grey')) }, { children: label }), void 0), jsxRuntime.exports.jsx(Box.Simple, Object.assign({ "$width": "1rem" }, { children: jsxRuntime.exports.jsx(Icon.TriangleDown, { "$color": state === 'error' ? 'error' : (state === 'success' ? 'success' : 'primary') }, void 0) }), void 0)] }), void 0), hovercardOpen ? (jsxRuntime.exports.jsx(Hovercard, Object.assign({ color: "white", dockBelow: dropdownRef.current, onClickAway: () => setHovercardOpen(false) }, { children: children({
                    selectedValue: value,
                    setValue: handleChange,
                    keyPrefix: `${id}-`
                }) }), void 0)) : null] }, void 0));
};

function getDropdownState(disabled, errored, submitted) {
    if (disabled) {
        return 'disabled';
    }
    if (errored) {
        return 'error';
    }
    if (submitted) {
        return 'success';
    }
    return 'normal';
}
/**
 * ## Form Dropdown
 *
 * Light wrapper of the related UI `Dropdown` component that handles setting the `state` prop correctly and storing the chosen value in a hidden `Field`.
 *
 * Tries to mimick the behavior of a Select box as much as possible when it can.
 */
const Dropdown = props => {
    var _a, _b;
    const { id: fieldId, name: _name, label, defaultLabel, defaultValue, value: externalValue, dropdownText, disabled, columnSpan, minWidth, columnStart, columnEnd, children, onChange } = props;
    const padding = (_a = props.padding) !== null && _a !== void 0 ? _a : '2.5rem .4rem 1rem';
    const name = normalizePath(_name);
    const formId = useFormId();
    const id = fieldId ? fieldId : (formId && name) ? `${formId}-${name}` : undefined;
    const revalidate = useFormRevalidate();
    const { error, hasSubmitted } = useFormFieldError(name);
    const [validatedValue, setValidatedValue] = React__default.useState(null);
    const [internalValue, setInternalValue] = React__default.useState(defaultValue);
    const handleChange = React__default.useCallback((value) => {
        if (onChange) {
            onChange(value);
        }
        setInternalValue(value);
    }, [onChange]);
    const value = (_b = externalValue !== null && externalValue !== void 0 ? externalValue : internalValue) !== null && _b !== void 0 ? _b : '';
    React__default.useEffect(() => {
        // This `if` prevents an endless loop
        if (validatedValue !== value) {
            revalidate();
            setValidatedValue(value);
        }
    }, [revalidate, validatedValue, value]);
    const LayoutContainer = useFormLayoutCellContainer(columnSpan, minWidth, columnStart, columnEnd);
    return (jsxRuntime.exports.jsx(LayoutContainer, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(Field, { type: "hidden", id: id, name: name, value: value }, void 0), jsxRuntime.exports.jsx(FieldLabel, Object.assign({ name: name }, { children: value ? label : "\u00a0" }), void 0), jsxRuntime.exports.jsx(Dropdown$1, Object.assign({ state: getDropdownState(disabled !== null && disabled !== void 0 ? disabled : false, !!error, hasSubmitted), defaultLabel: label, initialLabel: defaultLabel, label: dropdownText, initialValue: defaultValue, value: value, padding: padding, onChange: handleChange }, { children: children }), void 0)] }), void 0) }, void 0));
};

const Submit = props => {
    const { id: fieldId, columnSpan = 1, minWidth } = props;
    const grid = useContext(AutoGridContext);
    const errorCount = useFormErrorCount();
    const formId = useFormId();
    const id = fieldId ? fieldId : formId ? `${formId}-submit` : undefined;
    const LayoutContainer = useFormLayoutCellContainer(columnSpan, minWidth, undefined, 'end');
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$display": "contents" }, { children: [Array.from({ length: grid.columnCount - columnSpan }).map((u, i) => jsxRuntime.exports.jsx("span", {}, i)), jsxRuntime.exports.jsx(LayoutContainer, { children: jsxRuntime.exports.jsx(Button.Primary, Object.assign({ id: id, "$width": "100%", disabled: !!errorCount }, { children: props.children }), void 0) }, void 0)] }), void 0));
};

const Form = {
    /** Form that allows you to specify which layout system to use
     *
     * @example
     * ```
     * <Form.Container layout={FormLayout.AutoGrid}>
     * ```
     *
     * layout defaults to `FormLayout.AutoGrid`
     *
     * Unless your layout needs to be dynamic it's better to use these directly:
     * - Form.AutoGrid (Autogrid layout, explicitly sets layout system to AutoGrid)
     * - Form.Basic (No layout system)
     */
    Container: Form$1,
    /** Form with no layout system applied */
    Basic: BasicForm,
    /** Form with AutoGrid layout system internally applied */
    AutoGrid: AutoGridForm,
    Input: Field,
    Checkbox,
    Radio: {
        Group: RadioGroup,
        Field: RadioField,
    },
    Select: SelectField,
    Dropdown,
    Submit,
    validations,
    InputContext: FormFieldContext,
};

function colorForType(type) {
    switch (type) {
        case 'success': return 'success';
        case 'error': return 'error';
        case 'alert': return 'alert';
        case 'info': return 'info';
    }
}
const WebkitFix = styled.div `
  display: -webkit-box;
  -webkit-line-clamp: 10;
  -webkit-box-orient: vertical;
`;
const FlashDisplay = props => {
    const { overlay = true, minimized, type, onClose, children } = props;
    return (jsxRuntime.exports.jsx(Effect.Transition, Object.assign({ "$property": "all", "$duration": "700ms", "$easing": "ease" }, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": overlay ? 'absolute' : 'initial', "$maxHeight": minimized ? '1px' : 'initial', "$zIndex": "15", "$width": "100%", "$overflow": "hidden" }, { children: jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "2rem 1fr 2rem", "$gridGap": "1rem", "$alignItems": "center", "$padding": "1rem 2rem", "$textAlign": "center", "$color": "white@.8", "$backgroundColor": colorForType(type), onClick: onClose }, { children: [jsxRuntime.exports.jsx("span", {}, void 0), jsxRuntime.exports.jsx(WebkitFix, { children: jsxRuntime.exports.jsx(Text.Subtitle1, Object.assign({ id: "flash-text", "$overflow": "hidden", "$textOverflow": "ellipsis" }, { children: children }), void 0) }, void 0), jsxRuntime.exports.jsx(Icon.CloseCircle, { "$width": "2rem", "$height": "2rem", "$cursor": "pointer" }, void 0)] }), void 0) }), void 0) }), void 0));
};
const FlashController = props => {
    const { flash, placement, onClose, overlay = false, canShow = true } = props;
    const [minimized, setMinimized] = useState(true);
    useEffect(() => {
        if (flash && minimized) {
            setMinimized(false);
        }
        else if (!flash && !minimized) {
            setMinimized(true);
        }
    }, [flash, minimized]);
    if (!canShow || !flash) {
        return null;
    }
    if (flash.placement !== placement) {
        return null;
    }
    return (jsxRuntime.exports.jsx(FlashDisplay, Object.assign({ overlay: overlay, minimized: minimized, type: flash.type, onClose: onClose }, { children: flash.message }), void 0));
};

var FlashMessage = /*#__PURE__*/Object.freeze({
    __proto__: null,
    FlashDisplay: FlashDisplay,
    FlashController: FlashController
});

const CustomBadge = props => (jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$display": "inline-grid", "$padding": ".5rem 1rem", "$alignItems": "center", "$color": props.foregroundColor, "$backgroundColor": props.backgroundColor, "$borderRadius": "2rem", "$width": "min-content" }, { children: jsxRuntime.exports.jsx(Text.Overline, { children: props.children }, void 0) }), void 0));
const ActiveBadge = props => (jsxRuntime.exports.jsx(CustomBadge, Object.assign({ foregroundColor: "white", backgroundColor: "primary" }, { children: props.children }), void 0));
const InactiveBadge = props => (jsxRuntime.exports.jsx(CustomBadge, Object.assign({ foregroundColor: "grey.dark", backgroundColor: "grey.lighter@.5" }, { children: props.children }), void 0));
const HaltBadge = props => (jsxRuntime.exports.jsx(CustomBadge, Object.assign({ foregroundColor: "error.dark", backgroundColor: "error.lighter" }, { children: props.children }), void 0));
const Badge = {
    Custom: CustomBadge,
    Active: ActiveBadge,
    Halt: HaltBadge,
    Inactive: InactiveBadge,
};

const ButtonBarButton = ({ id, option, selected, onClick, bar: { hue, selectedHue = hue, selectedColor, selectedBackgroundColor } }) => (jsxRuntime.exports.jsx(Button.Primary, Object.assign({ id: id, onClick: onClick, "$borderRadius": "0", "$width": "100%", "$hue": selected ? selectedHue : hue, "$backgroundColor": selected ? selectedBackgroundColor : undefined, "$borderColor": selected ? selectedBackgroundColor : undefined, "$color": selected ? selectedColor : undefined }, { children: option.label }), void 0));
const ButtonBar = props => {
    const { buttons, onChange, containerColor, selectedValue, defaultSelectedValue, buttonComp } = props;
    const Button = buttonComp || ButtonBarButton;
    const [internalSelected, setInternalSelected] = useState(defaultSelectedValue);
    const selected = selectedValue || internalSelected;
    const handleClick = useCallback((opt) => () => {
        const { value } = opt;
        setInternalSelected(value);
        if (onChange) {
            onChange(value);
        }
    }, [onChange]);
    return (jsxRuntime.exports.jsx(Box.Model, Object.assign({ "$width": "max-content", "$color": containerColor, "$borderRadius": "buttons.borderRadius", "$borderWidth": "1px", "$borderStyle": "solid", "$backgroundColor": "currentColor", "$overflow": "hidden" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": `repeat(${buttons.length}, 1fr)`, "$width": "max-content", "$gridGap": "1px" }, { children: buttons.map(opt => (jsxRuntime.exports.jsx(Button, { id: opt.id, option: opt, selected: opt.value === selected, onClick: handleClick(opt), bar: props }, opt.value))) }), void 0) }), void 0));
};

function timeKey(t) {
    return t.getDateString();
}
/** A `Map` with `Time` keys
 *
 * Intead of time keys being unique by object reference, they are unique by the day
 * they represent
 *
 * Useful when storing data based on a day, such as passing information to a `Calendar` component
 */
class TimeMap {
    constructor(initial) {
        this._map = initial ? toTimeMap(initial) : new Map();
    }
    set(t, v) {
        this._map.set(timeKey(t), v);
    }
    get(t) {
        return this._map.get(timeKey(t));
    }
    /**
     * @param mapper Converter function to be run for each `TimeMap` value
     *
     * @returns New `TimeMap` with values mapped from this `TimeMap`
     */
    valueMap(mapper) {
        const mappedTimeMap = new TimeMap();
        for (const [key, value] of this._map) {
            mappedTimeMap._map.set(key, mapper(value));
        }
        return mappedTimeMap;
    }
}
function toTimeMap(t) {
    const map = new Map();
    t.forEach(([time, value]) => {
        map.set(timeKey(time), value);
    });
    return map;
}

const CalendarSlotDot = props => (jsxRuntime.exports.jsx(Container.Base, { "$backgroundColor": props.color, "$width": "5px", "$height": "5px", "$borderRadius": "100%" }, void 0));
const CalenderTooltopInfo = props => (jsxRuntime.exports.jsx("div", { children: props.info.map(data => (jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "5px 20rem", "$alignItems": "start", "$gridGap": "5px" }, { children: [jsxRuntime.exports.jsx(Container.Base, Object.assign({ "$padding": "5px 0" }, { children: jsxRuntime.exports.jsx(CalendarSlotDot, { color: data.color }, void 0) }), void 0), jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$color": "grey.lighter" }, { children: data.info }), void 0)] }), `${data.color}:${data.info}`))) }, void 0));
const DayInfoWrapper = props => {
    const { info = [], children } = props;
    if (!info.length) {
        return (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "relative" }, { children: children }), void 0));
    }
    return (jsxRuntime.exports.jsx(Tooltip, Object.assign({ content: jsxRuntime.exports.jsx(CalenderTooltopInfo, { info: info }, void 0) }, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$top": "5px", "$left": "5px" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$gridTemplateColumns": "5px", "$gridAutoRows": "5px", "$gridGap": "3px" }, { children: info.slice(0, 4).map(cdi => (jsxRuntime.exports.jsx(CalendarSlotDot, { color: cdi.color }, `${cdi.color}:${cdi.info}`))) }), void 0) }), void 0), children] }), void 0) }), void 0));
};
const Slot = props => {
    const { time, info, onInRangeClick, onOutRangeClick, disabled, outOfRange, hilightInRange, isToday, isSelectionStart, isSelectionEnd, isSelectionSpan, children } = props;
    const topLeftRadius = isSelectionStart ? `7px` : 0;
    const topRightRadius = isSelectionEnd ? `7px` : 0;
    const bottomRightRadius = isSelectionEnd ? `7px` : 0;
    const bottomLeftRadius = isSelectionStart ? `7px` : 0;
    const backgroundColor = disabled
        ? 'white'
        : (isSelectionStart || isSelectionEnd)
            ? 'info'
            : isSelectionSpan
                ? 'info.lighter'
                : hilightInRange ?
                    outOfRange ? 'white' : 'alert.lighter'
                    : 'white';
    const backgroundColorHover = (!disabled && !isSelectionStart && !isSelectionEnd && !isSelectionSpan)
        ? hilightInRange
            ? outOfRange ? 'background' : 'alert.light'
            : 'background'
        : undefined;
    const textColor = disabled
        ? 'grey.light'
        : (isSelectionStart || isSelectionEnd)
            ? 'white'
            : (isToday || isSelectionSpan)
                ? 'info'
                : 'black';
    const handleClick = useCallback(() => {
        if (!disabled && !outOfRange) {
            onInRangeClick(time);
        }
        else if (!disabled) {
            onOutRangeClick(time);
        }
    }, [disabled, onInRangeClick, onOutRangeClick, time, outOfRange]);
    return (jsxRuntime.exports.jsx(DayInfoWrapper, Object.assign({ info: info }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ onClick: handleClick, "$aspectRatio": "1", "$cursor": disabled ? 'default' : 'pointer', "$alignContent": "center", "$justifyContent": "center", "$backgroundColor": backgroundColor, "$backgroundColorHover": backgroundColorHover, "$borderRadius": `${topLeftRadius} ${topRightRadius} ${bottomRightRadius} ${bottomLeftRadius}` }, { children: jsxRuntime.exports.jsx(Text.Body2, Object.assign({ "$color": textColor, "$fontWeight": (isToday || isSelectionStart || isSelectionEnd) ? 'bold' : 'inherit' }, { children: children }), void 0) }), void 0) }), void 0));
};
const Slots = props => {
    const { month, year, selection, maxDaySpan, maxDay, minDay, info } = props;
    const date = new Date(year, month);
    const totalDays = new Date(year, month + 1, 0).getDate();
    const startDay = date.getDay();
    const slots = [];
    const handleOutRangeClick = useCallback((time) => {
        // Just start a new range if clicked out of range bounds
        selection.clear();
        selection.setRangeStart(time.startOfDay());
    }, [selection]);
    const handleInRangeClick = useCallback((time) => {
        var _a, _b;
        if (
        // At least one is set
        (selection.getStart() || selection.getEnd()) &&
            // start is either empty or same as just selected
            (!selection.getStart() || ((_a = selection.getStart()) === null || _a === void 0 ? void 0 : _a.isSameDayAs(time))) &&
            // end is either empty or same as just selected
            (!selection.getEnd() || ((_b = selection.getEnd()) === null || _b === void 0 ? void 0 : _b.isSameDayAs(time)))) {
            // Just clear it
            // (single day was selected and they selected it again)
            selection.clear();
        }
        else if (selection.getStart() && selection.getEnd()) {
            // Start new selection
            selection.clear();
            selection.setRangeStart(time.startOfDay());
        }
        else if (selection.getStart()) {
            // start set but no end, set end
            selection.setRangeEnd(time.endOfDay());
        }
        else {
            // no start, set start
            selection.setRangeStart(time.startOfDay());
        }
    }, [selection]);
    for (let i = 0; i < startDay; i++) {
        slots.push(jsxRuntime.exports.jsx("span", {}, `span-${i}`));
    }
    for (let i = 1; i <= totalDays; i++) {
        const day = new Date(year, month, i);
        const time = Time.fromDate(day);
        const dayInfo = info ? info.get(time) : undefined;
        const isSelectionStart = selection.isStartDay(time);
        const isSelectionEnd = selection.isEndDay(time) || (isSelectionStart && !selection.getEnd());
        const isSelectionSpan = (!isSelectionStart &&
            !isSelectionEnd &&
            selection.contains(time));
        let disabled = false;
        let outOfRange = false;
        if (minDay && time.isBefore(minDay.startOfDay())) {
            disabled = true;
        }
        else if (maxDay && time.isAfter(maxDay.endOfDay())) {
            disabled = true;
        }
        if (
        // can't be out of range if it is disabled
        !disabled &&
            // can't be out of range if there is no max range
            maxDaySpan &&
            // can't be out of range if there is no starting point
            selection.getStart() &&
            // can't be out of range if the range is already selected
            !selection.getEnd()) {
            const wouldBeRange = new TimeRange(selection.getStart(), time);
            if (wouldBeRange.daySpan() > maxDaySpan) {
                outOfRange = true;
            }
        }
        slots.push(jsxRuntime.exports.jsx(Slot, Object.assign({ onInRangeClick: handleInRangeClick, onOutRangeClick: handleOutRangeClick, time: time, disabled: disabled, outOfRange: outOfRange, hilightInRange: /* temp turn this off per UX meeting */ false  , isToday: time.isToday(), isSelectionStart: isSelectionStart, isSelectionSpan: isSelectionSpan, isSelectionEnd: isSelectionEnd, info: dayInfo }, { children: String(day.getDate()) }), `day-${i}`));
    }
    return (jsxRuntime.exports.jsx(Container.Grid, Object.assign({ id: "calendar-days", "$gridTemplateColumns": `repeat(7, 1fr)`, "$gridAutoRows": "1fr", "$gridGap": "1px", "$backgroundColor": "background" }, { children: slots }), void 0));
};
const MonthPicker = props => {
    const { month, year, onChange, maxDay, minDay } = props;
    const time = useMemo(() => Time.fromDate(new Date(year, month)), [month, year]);
    const monthName = time.format({ month: 'short' });
    const [canGoPrev, setCanGoPrev] = useState(false);
    const [canGoNext, setCanGoNext] = useState(false);
    const handlePrev = useCallback(() => {
        if (canGoPrev) {
            onChange(time.prevMonth());
        }
    }, [canGoPrev, onChange, time]);
    const handleNext = useCallback(() => {
        if (canGoNext) {
            onChange(time.nextMonth());
        }
    }, [canGoNext, onChange, time]);
    useEffect(() => {
        if (minDay) {
            setCanGoPrev(!time.clone().prevMonth().endOfMonth().isBefore(minDay));
        }
        else {
            setCanGoPrev(true);
        }
        if (maxDay) {
            setCanGoNext(!time.clone().nextMonth().startOfMonth().isAfter(maxDay));
        }
        else {
            setCanGoNext(true);
        }
    }, [minDay, maxDay, time]);
    return (jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ id: "calendar-month", "$padding": "1rem", "$gridTemplateColumns": "2rem 1fr 2rem", "$justifyItems": "center", "$backgroundColor": "background", "$color": "black" }, { children: [jsxRuntime.exports.jsx(Icon.ChevronLeft, { onClick: handlePrev, "$width": "100%", "$cursor": canGoPrev ? "pointer" : undefined, "$colorHover": canGoPrev ? "info" : undefined, "$color": canGoPrev ? undefined : 'grey.light' }, void 0), jsxRuntime.exports.jsxs(Text.Body1, Object.assign({ "$fontWeight": "bold" }, { children: [monthName, " ", year] }), void 0), jsxRuntime.exports.jsx(Icon.ChevronRight, { onClick: handleNext, "$width": "100%", "$cursor": canGoNext ? "pointer" : undefined, "$colorHover": canGoNext ? "info" : undefined, "$color": canGoNext ? undefined : 'grey.light' }, void 0)] }), void 0));
};
const CalenderHeading = () => {
    return (jsxRuntime.exports.jsx(Box.Border, Object.assign({ id: "calendar-week", "$borderTopColor": "grey.light", "$borderBottomColor": "grey.lighter", "$borderTopWidth": "1px", "$borderTopStyle": "solid", "$borderBottomWidth": "1px", "$borderBottomStyle": "solid" }, { children: jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "repeat(7, 1fr)", "$justifyItems": "center", "$backgroundColor": "white" }, { children: [jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Sun" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Mon" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Tue" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Wed" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Thu" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Fri" }), void 0), jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$aspectRatio": "1", "$alignItems": "center", "$textAlign": "center", "$height": "100%" }, { children: "Sat" }), void 0)] }), void 0) }), void 0));
};
const Calendar = props => {
    const { defaultSelection, maxDay, maxDaySpan, minDay, onChange, info } = props;
    const infoMap = useMemo(() => (!info || info instanceof TimeMap) ? info : new TimeMap(info), [info]);
    const selection = useTimeRange(() => {
        if (!defaultSelection) {
            return new TimeRange();
        }
        else if (!defaultSelection.isRange) {
            return defaultSelection.toRange();
        }
        else if (defaultSelection.isOpen()) {
            return defaultSelection.clone().close();
        }
        return defaultSelection.clone();
    }, onChange);
    const [pageMonth, setPageMonth] = useState(() => {
        const t = selection.getStart() || Time.now();
        return t.date.getMonth();
    });
    const [pageYear, setPageYear] = useState(() => {
        const t = selection.getStart() || Time.now();
        return t.date.getFullYear();
    });
    const handlePage = useCallback((t) => {
        setPageYear(t.date.getFullYear());
        setPageMonth(t.date.getMonth());
    }, [setPageMonth, setPageYear]);
    console.log('ROOT: selection', selection.toString(), 'spanning', selection.daySpan(), 'days');
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$cursor": "default", "$width": "30rem" }, { children: [jsxRuntime.exports.jsx(MonthPicker, { maxDay: maxDay, minDay: minDay, month: pageMonth, year: pageYear, onChange: handlePage }, void 0), jsxRuntime.exports.jsx(CalenderHeading, {}, void 0), jsxRuntime.exports.jsx(Slots, { month: pageMonth, year: pageYear, selection: selection, maxDay: maxDay, minDay: minDay, maxDaySpan: maxDaySpan, info: infoMap }, void 0)] }), void 0));
};

const Collapse = props => {
    const { children, open } = props;
    const childrenWrapperRef = React__default.useRef(null);
    const height = React__default.useMemo(() => {
        if (open && childrenWrapperRef.current) {
            return String(childrenWrapperRef.current.offsetHeight) + 'px';
        }
        else {
            return '0';
        }
    }, [open]);
    return (jsxRuntime.exports.jsx(Effect.Transition, Object.assign({ "$property": "max-height", "$duration": "500ms" }, { children: jsxRuntime.exports.jsx(Box.Simple, Object.assign({ "$maxHeight": height, "$overflow": "hidden" }, { children: jsxRuntime.exports.jsx("div", Object.assign({ ref: childrenWrapperRef }, { children: children }), void 0) }), void 0) }), void 0));
};

const Faq = props => {
    const { title, className, children, faintNotch, titleTextColor = 'primary', bodyTextColor = 'grey.darker' } = props;
    const collapse = useToggle();
    return (jsxRuntime.exports.jsxs(Box.Simple, Object.assign({ "$padding": ".8rem 0", "$textAlign": "left", className: className }, { children: [jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateColumns": "1.2rem 1fr", "$gridGap": "1.2rem", "$cursor": "pointer", onClick: collapse.toggle }, { children: [jsxRuntime.exports.jsx(Effect.Opacity, Object.assign({ "$opacity": faintNotch ? '.6' : '1' }, { children: jsxRuntime.exports.jsx(Effect.Transition, Object.assign({ "$property": "transform", "$duration": "150ms" }, { children: jsxRuntime.exports.jsx(Container.Base, Object.assign({ "$color": titleTextColor, "$transform": collapse.value ? 'rotate(0deg)' : 'rotate(-90deg)' }, { children: jsxRuntime.exports.jsx(Icon.TriangleDown, {}, void 0) }), void 0) }), void 0) }), void 0), jsxRuntime.exports.jsx(Text.Subtitle1, Object.assign({ "$color": titleTextColor }, { children: title }), void 0)] }), void 0), jsxRuntime.exports.jsx(Collapse, Object.assign({ open: collapse.value }, { children: jsxRuntime.exports.jsx(Box.Padding, Object.assign({ "$paddingTop": ".8rem" }, { children: jsxRuntime.exports.jsx(Text.Subtitle1, Object.assign({ "$color": bodyTextColor, "$fontSize": "1.6rem" }, { children: children }), void 0) }), void 0) }), void 0)] }), void 0));
};

function stringifyProps(props) {
    return JSON.stringify(props, Object.keys(props).filter((key) => {
        return key !== 'children' && !key.startsWith('_');
    }));
}
function key(icon) {
    return `${icon.key}:${stringifyProps(icon.props)}:${icon.type}`;
}
function wrapIcon(icon) {
    return (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$top": "0", "$bottom": "0", "$left": "0", "$right": "0" }, { children: icon }), key(icon)));
}
function wrapIcons(icons) {
    const arr = Array.isArray(icons) ? icons : [icons];
    return arr.map(wrapIcon);
}
const IconStack = (props) => (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "relative", "$display": props.display || 'inline-block', "$width": props.width || props.height || '100%', "$height": props.height || props.width || '100%', "$maxWidth": props.maxWidth || '100%', "$maxHeight": props.maxHeight || '100%' }, { children: wrapIcons(props.children) }), void 0));

var _a;
// When running on the server (such as running tests)
// google.maps does not exist since it is installed
// via a script tag in the document.
// This just prevents it from blowing up.
const googleMapsPolyfill = (_a = window.google) !== null && _a !== void 0 ? _a : {
    maps: {
        LatLngBounds: Date,
        MapTypeId: {}
    },
};
if (!window.google) {
    window.google = googleMapsPolyfill;
}
let renderToStaticMarkup;
function setup(options) {
    renderToStaticMarkup = options.renderToStaticMarkup;
}
const ColorScheme = props => {
    const { light, dark } = props;
    const themes = { light, dark };
    const mode = usePreferredColorScheme();
    const theme = themes[mode];
    return (jsxRuntime.exports.jsx(ThemeProvider, Object.assign({ theme: theme }, { children: props.children }), void 0));
};
const RoadieThemeProvider = props => (jsxRuntime.exports.jsx(ColorScheme, Object.assign({ light: { RoadieUI: theme }, dark: { RoadieUI: dark } }, { children: props.children }), void 0));
const mapContext = createContext({
    bounds: window.google ? new window.google.maps.LatLngBounds() : {}
});
const DEFAULT_CONFIG = {
    zoom: 15,
    minZoom: 3,
    mapTypeId: window.google.maps.MapTypeId.ROADMAP,
    zoomControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    rotateControl: false,
    panControl: false,
    mapTypeControl: false,
    gestureHandling: 'cooperative'
};
function buildMap(el, config) {
    return new window.google.maps.Map(el, config);
}
function latlngsForRoute(route) {
    const decodedPath = window.google.maps.geometry.encoding.decodePath(route);
    return decodedPath.filter(segment => segment && segment.lat() && segment.lng());
}
const RoadieMap = props => {
    const { config } = props;
    const [map, setMap] = useState();
    const [bounds] = useState(() => (new window.google.maps.LatLngBounds()));
    const handleMapRef = useCallback((el) => {
        if (el) {
            const finalConfig = Object.assign(Object.assign({}, DEFAULT_CONFIG), (config || {}));
            const _map = buildMap(el, finalConfig);
            setMap(_map);
        }
    }, [config]);
    return (jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative" }, { children: [jsxRuntime.exports.jsx(Container.Base, { "$width": props.width, "$height": props.height, ref: handleMapRef }, void 0), jsxRuntime.exports.jsx(mapContext.Provider, Object.assign({ value: { map, bounds } }, { children: props.children }), void 0)] }), void 0));
};
const RoadieMapRoute = props => {
    const { map, bounds } = useContext(mapContext);
    const { route, color, fitInMap } = props;
    const themeColor = useThemeColor(color);
    const [routeCoords, setRouteCoords] = useState();
    useEffect(() => {
        setRouteCoords(latlngsForRoute(route));
    }, [route]);
    useEffect(() => {
        if (routeCoords && map) {
            if (fitInMap) {
                routeCoords.forEach(r => bounds.extend(r));
                map.fitBounds(bounds);
            }
            const poly = new window.google.maps.Polyline({
                strokeColor: themeColor,
                strokeOpacity: 1.0,
                strokeWeight: 7.0,
                clickable: false
            });
            poly.setPath(routeCoords);
            poly.setMap(map);
            return () => {
                poly.setMap(null);
            };
        }
    }, [routeCoords, map, bounds, themeColor, fitInMap]);
    return null;
};
const RoadieMapMarker = props => {
    const { map, bounds } = useContext(mapContext);
    const { longitude, latitude, imageUrl, size = 55, fitInMap, centerInMap, children, color: colorName } = props;
    const [marker, setMarker] = useState();
    const color = useThemeColor(colorName || 'primary');
    useEffect(() => {
        if (!map) {
            return;
        }
        const position = new window.google.maps.LatLng(latitude, longitude);
        const icon = imageUrl ? {
            url: imageUrl,
            size: new window.google.maps.Size(size, size),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(size / 2, size),
            scaledSize: new window.google.maps.Size(size, size)
        } : {
            path: "M242.606,0C142.124,0,60.651,81.473,60.651,181.955s151.631,303.257,181.956,303.257c30.326,0,181.955-202.775,181.955-303.257S343.089,0,242.606,0z M242.606,303.257c-66.9,0-121.302-54.433-121.302-121.302S175.706,60.651,242.606,60.651c66.902,0,121.302,54.435,121.302,121.304S309.509,303.257,242.606,303.257z",
            fillColor: color,
            fillOpacity: 0.9,
            strokeWeight: 0,
            rotation: 0,
            scale: .075,
            anchor: new window.google.maps.Point(245, 475),
        };
        const marker = new window.google.maps.Marker({ position, map, icon });
        setMarker(marker);
        if (fitInMap) {
            bounds.extend(position);
            map.fitBounds(bounds);
        }
        else if (centerInMap) {
            map.setCenter(position);
        }
        return () => {
            marker.setMap(null);
        };
    }, [map, longitude, latitude, imageUrl, size, bounds, fitInMap, centerInMap, color]);
    if (children && marker) {
        return (jsxRuntime.exports.jsx(RoadieMapInfoWindow, Object.assign({ anchor: marker }, { children: children }), void 0));
    }
    return null;
};
const RoadieMapInfoWindow = props => {
    const { anchor, children } = props;
    const { map } = useContext(mapContext);
    useEffect(() => {
        if (!renderToStaticMarkup) {
            throw new Error('Must set renderToStaticMarkup method in MapSystem.setup()');
        }
        const infowindow = new window.google.maps.InfoWindow({
            content: renderToStaticMarkup(jsxRuntime.exports.jsx(RoadieThemeProvider, { children: children }, void 0))
        });
        if (!anchor) {
            return;
        }
        infowindow.open({
            anchor,
            map,
            shouldFocus: false,
        });
        return () => {
            infowindow.close();
        };
    }, [map, anchor, children]);
    return null;
};
const RoadieMapSystem = {
    setup,
    Map: RoadieMap,
    Route: RoadieMapRoute,
    Marker: RoadieMapMarker,
};

const ModalUI = props => {
    var _a;
    const initiallyClickedElement = React__default.useRef(document.body);
    // Prevent click events from bubbling outside of the Modal.
    const rootClickHandler = useCallback(event => {
        event.stopPropagation();
    }, []);
    const gridClickHandler = useCallback(event => {
        const _initiallyClickedElement = initiallyClickedElement.current;
        if (props.onRequestClose && _initiallyClickedElement.isSameNode(event.currentTarget)) {
            props.onRequestClose();
        }
    }, [props]);
    // Prevent click events on grid item from bubbling to grid (which will call onRequestClose)
    const gridItemClickHandler = useCallback(event => {
        event.stopPropagation();
    }, []);
    const mouseDownHandler = useCallback(event => {
        event.stopPropagation();
        initiallyClickedElement.current = event.currentTarget;
    }, []);
    useLockHtmlScroll((_a = props.open) !== null && _a !== void 0 ? _a : true);
    if (props.open === false) {
        return jsxRuntime.exports.jsx(jsxRuntime.exports.Fragment, {}, void 0);
    }
    return (jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "fixed", "$top": "0", "$bottom": "0", "$left": "0", "$right": "0", "$zIndex": "1", onClick: rootClickHandler }, { children: [jsxRuntime.exports.jsx(Filter.BackdropBlur, { children: jsxRuntime.exports.jsx(Container.Base, { "$backgroundColor": "!black@.58", "$width": "100vw", "$height": "100vh" }, void 0) }, void 0), jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$top": "0", "$left": "0" }, { children: jsxRuntime.exports.jsx(Container.Grid, Object.assign({ "$alignItems": "center", "$justifyContent": "center", "$width": "100vw", "$height": "100vh", onClick: gridClickHandler, onMouseDown: mouseDownHandler }, { children: jsxRuntime.exports.jsx(Container.GridItem, Object.assign({ onClick: gridItemClickHandler, onMouseDown: mouseDownHandler }, { children: props.children }), void 0) }), void 0) }), void 0)] }), void 0));
};
const Modal = props => {
    const modalPortal = document.getElementById('modal');
    if (!modalPortal) {
        console.warn('Expecting element div.#modal to exist on page.');
        return jsxRuntime.exports.jsx(jsxRuntime.exports.Fragment, {}, void 0);
    }
    return createPortal((jsxRuntime.exports.jsx(ModalUI, Object.assign({}, props, { children: props.children }), void 0)), modalPortal);
};
const BasicModal = props => {
    return (jsxRuntime.exports.jsx(Modal, Object.assign({}, props, { children: jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "relative" }, { children: jsxRuntime.exports.jsx(Container.Base, Object.assign({ "$width": props.width || '46.4rem', "$maxWidth": "100vw" }, { children: jsxRuntime.exports.jsx(Filter.DropShadow, { children: jsxRuntime.exports.jsx(Container.Panel, Object.assign({ "$padding": "4rem", "$borderWidth": "0" }, { children: jsxRuntime.exports.jsxs(Container.Base, Object.assign({ "$textAlign": "center" }, { children: [props.title ? (jsxRuntime.exports.jsxs(Container.Base, { children: [jsxRuntime.exports.jsx(Text.H4, Object.assign({ "$color": "black" }, { children: props.title }), void 0), jsxRuntime.exports.jsx(Spacer, { "$height": "1rem" }, void 0)] }, void 0)) : null, props.children, props.onRequestClose ? (jsxRuntime.exports.jsxs(Container.Base, { children: [jsxRuntime.exports.jsx(Spacer, { "$height": "1rem" }, void 0), jsxRuntime.exports.jsx(Button.Tertiary, Object.assign({ onClick: props.onRequestClose }, { children: props.closeButtonText || 'Dismiss' }), void 0)] }, void 0)) : null, props.onRequestClose ? (jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$position": "absolute", "$top": "1.6rem", "$right": "1.6rem", "$width": "2rem" }, { children: jsxRuntime.exports.jsx(Icon.Close, { "$cursor": "pointer", "$color": "grey", onClick: props.onRequestClose }, void 0) }), void 0)) : null] }), void 0) }), void 0) }, void 0) }), void 0) }), void 0) }), void 0));
};
const Modals = {
    Raw: Modal,
    Basic: BasicModal,
};

const DONT_SHOW_AGAIN_STORAGE_KEY = 'roadie-messages-nevershow';
function getMessages() {
    return __awaiter(this, void 0, void 0, function* () {
        const resp = yield api.raw().get(process.env.REACT_APP_MESSAGES_URL);
        return resp.data;
    });
}
function rememberDontShowAgain(id) {
    const arr = JSON.parse(localStorage.getItem(DONT_SHOW_AGAIN_STORAGE_KEY) || '[]');
    if (!arr.includes(id)) {
        arr.push(id);
    }
    localStorage.setItem(DONT_SHOW_AGAIN_STORAGE_KEY, JSON.stringify(arr));
}
const Messages = props => {
    const { app } = props;
    const [messages, setMessages] = useState([]);
    const [seenIds, setSeenIds] = useState([]);
    const [nextMessage, setNextMessage] = useState();
    const dontShowAgain = useToggle(false);
    useEffect(() => {
        getMessages().then(messages => {
            const neverShowIds = JSON.parse(localStorage.getItem(DONT_SHOW_AGAIN_STORAGE_KEY) || '[]');
            const showableMessages = messages
                .filter(m => !neverShowIds.includes(m.id))
                .filter(m => m.apps.includes(app));
            setMessages(showableMessages);
        });
    }, [app]);
    useEffect(() => {
        const nextMessage = messages.find(m => !seenIds.includes(m.id));
        setNextMessage(nextMessage);
    }, [messages, seenIds]);
    const handleSeen = useCallback(() => {
        console.log('donshow', dontShowAgain.value);
        if (!nextMessage) {
            return;
        }
        setSeenIds(ids => [...ids, nextMessage.id]);
        if (dontShowAgain.value) {
            rememberDontShowAgain(nextMessage.id);
            dontShowAgain.off();
        }
    }, [nextMessage, dontShowAgain]);
    if (!nextMessage) {
        return null;
    }
    return (jsxRuntime.exports.jsx(Modals.Basic, Object.assign({ title: nextMessage.title, onRequestClose: handleSeen }, { children: jsxRuntime.exports.jsxs(Container.Grid, Object.assign({ "$gridTemplateRows": "auto 1rem", "$gridGap": "2rem", "$justifyContent": "center" }, { children: [jsxRuntime.exports.jsx(Text.Body1, { children: nextMessage.message }, void 0), jsxRuntime.exports.jsx(Form.Checkbox, { name: "dontshowagain", label: "Don't show again", checked: dontShowAgain.value, onChange: dontShowAgain.toggle }, void 0), jsxRuntime.exports.jsx(Spacer, { "$height": "1px" }, void 0)] }), void 0) }), void 0));
};

const Tooltip = props => {
    var _a;
    const [showing, setShowing] = useState(null);
    const show = useCallback((e) => setShowing(e.currentTarget), []);
    const hide = useCallback(() => setShowing(null), []);
    const [containerRef, setContainerRef] = useState(null);
    useEffect(() => {
        if (containerRef) {
            containerRef.addEventListener('mouseenter', show);
            containerRef.addEventListener('mouseleave', hide);
        }
        return () => {
            if (containerRef) {
                containerRef.removeEventListener('mouseenter', show);
                containerRef.removeEventListener('mouseleave', hide);
            }
        };
    }, [containerRef, show, hide]);
    const handleRef = useCallback((el) => {
        setContainerRef(el);
    }, []);
    return (jsxRuntime.exports.jsxs(Container.Base, Object.assign({ ref: handleRef, "$display": "contents" }, { children: [props.children, (showing && props.content) ?
                jsxRuntime.exports.jsx(TooltipUI, { on: showing, content: props.content, direction: (_a = props.direction) !== null && _a !== void 0 ? _a : 'top' }, void 0) :
                null] }), void 0));
};
const NOTCH_SIZE_PX = 10;
const NOTCH_SIZE_REM = `${NOTCH_SIZE_PX / 10}rem`;
function nudgeTooltipHorizontally(targetRect, tooltipRect) {
    const bodyRect = document.body.getBoundingClientRect();
    const tooltipCenter = NOTCH_SIZE_PX + (tooltipRect.width / 2);
    if (targetRect.center.x < tooltipCenter) {
        return tooltipCenter - targetRect.center.x;
    }
    else if (targetRect.center.x > bodyRect.width - tooltipCenter) {
        return (bodyRect.width - tooltipCenter) - targetRect.center.x;
    }
    return 0;
}
function nudgeTooltipVertically(targetRect, tooltipRect) {
    const bodyRect = document.body.getBoundingClientRect();
    const tooltipCenter = NOTCH_SIZE_PX + (tooltipRect.height / 2);
    if (targetRect.center.y < tooltipCenter) {
        return tooltipCenter - targetRect.center.y;
    }
    else if (targetRect.center.y > bodyRect.height - tooltipCenter) {
        return (bodyRect.height - tooltipCenter) - targetRect.center.y;
    }
    return 0;
}
function inverseDirection(direction) {
    switch (direction) {
        case 'top': return 'bottom';
        case 'bottom': return 'top';
        case 'left': return 'right';
        case 'right': return 'left';
    }
}
function isOffscreen(direction, targetRect, tooltipRect) {
    switch (direction) {
        case 'top':
            return targetRect.top - NOTCH_SIZE_PX - tooltipRect.height < 0;
        case 'bottom':
            return targetRect.bottom + NOTCH_SIZE_PX + tooltipRect.height > window.innerHeight;
        case 'left':
            return targetRect.left - NOTCH_SIZE_PX - tooltipRect.width < 0;
        case 'right':
            return targetRect.right + NOTCH_SIZE_PX + tooltipRect.width > window.innerWidth;
    }
}
const TooltipUI = props => {
    const { content, direction } = props;
    const targetElement = props.on.children[0];
    const targetRect = useMemo(() => {
        const parentElement = targetElement.closest('.tooltip-container') || targetElement.offsetParent || document.body;
        const targetRect = targetElement.getBoundingClientRect();
        const parentRect = parentElement.getBoundingClientRect();
        const left = Math.max(targetRect.left, parentRect.left);
        const top = Math.max(targetRect.top, parentRect.top);
        const right = Math.min(targetRect.right, parentRect.right);
        const bottom = Math.min(targetRect.bottom, parentRect.bottom);
        return {
            left,
            top,
            right,
            bottom,
            width: right - left,
            height: bottom - top,
            center: {
                x: left + ((right - left) / 2),
                y: top + ((bottom - top) / 2)
            },
        };
    }, [targetElement]);
    const [offset, setOffset] = useState(0);
    const [inverted, setInverted] = useState(false);
    const tooltipRef = useRef(null);
    const [tooltipProps, notchProps] = useMemo(() => {
        switch (inverted ? inverseDirection(direction) : direction) {
            case 'top':
                return [{
                        $transform: `translate(-50%, calc(-100% - ${NOTCH_SIZE_REM}))`,
                        $top: `${targetRect.top}px`,
                        $left: `${targetRect.center.x + offset}px`,
                    }, {
                        $top: '100%',
                        $left: `calc(50% - (${NOTCH_SIZE_REM} / 2) - ${offset}px)`,
                    }];
            case 'bottom':
                return [{
                        $transform: `translate(-50%, ${NOTCH_SIZE_REM})`,
                        $top: `${targetRect.bottom}px`,
                        $left: `${targetRect.center.x + offset}px`,
                    }, {
                        $bottom: '100%',
                        $left: `calc(50% - (${NOTCH_SIZE_REM} / 2) - ${offset}px)`,
                        $transform: 'rotate(180deg)',
                    }];
            case 'left':
                return [{
                        $transform: `translate(calc(-100% - ${NOTCH_SIZE_REM}), -50%)`,
                        $top: `${targetRect.center.y + offset}px`,
                        $left: `${targetRect.left}px`,
                    }, {
                        $left: '100%',
                        $top: `calc(50% - (${NOTCH_SIZE_REM} / 2) - ${offset}px)`,
                        $transform: 'rotate(-90deg)',
                    }];
            case 'right':
                return [{
                        $transform: `translate(${NOTCH_SIZE_REM}, -50%)`,
                        $top: `${targetRect.center.y + offset}px`,
                        $left: `${targetRect.right}px`,
                    }, {
                        $right: '100%',
                        $top: `calc(50% - (${NOTCH_SIZE_REM} / 2) - ${offset}px)`,
                        $transform: 'rotate(90deg)',
                    }];
        }
    }, [direction, inverted, offset, targetRect]);
    useLayoutEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            //Nudge tooltip along perpindicular axis to fit on-screen
            const nudgeAmount = (direction === 'top' || direction === 'bottom') ?
                nudgeTooltipHorizontally(targetRect, tooltipRect) :
                nudgeTooltipVertically(targetRect, tooltipRect);
            setOffset(nudgeAmount);
            //Swap tooltip parallel axis if it goes off-screen
            setInverted(isOffscreen(direction, targetRect, tooltipRect));
        }
    }, [direction, inverted, targetRect]);
    return createPortal(jsxRuntime.exports.jsx(Container.Position, Object.assign({ ref: tooltipRef, "$position": "absolute", "$maxWidth": "20rem", "$minWidth": "5rem", "$width": "max-content" }, tooltipProps, { children: jsxRuntime.exports.jsx(Filter.DropShadow, { children: jsxRuntime.exports.jsxs(Container.Position, Object.assign({ "$position": "relative", "$backgroundColor": "grey.darker", "$borderRadius": "5px", "$padding": "1rem" }, { children: [typeof content === 'string' ? (jsxRuntime.exports.jsx(Text.Caption, Object.assign({ "$textAlign": "center", "$color": "grey.lighter" }, { children: content }), void 0)) : (content), jsxRuntime.exports.jsx(Container.Position, Object.assign({ "$display": "grid", "$position": "absolute", "$height": NOTCH_SIZE_REM, "$width": NOTCH_SIZE_REM }, notchProps, { children: jsxRuntime.exports.jsx(Icon.TooltipNotch, { "$color": "grey.darker" }, void 0) }), void 0)] }), void 0) }, void 0) }), void 0), document.getElementById('tooltip'));
};

export { AutoGrid, Badge, BasicFormControls, Box, Button, ButtonBar, Calendar, Color, Container, dark as DarkTheme, Dropdown$1 as Dropdown, Effect, Faq, Filter, FlashMessage as Flash, Form, Hovercard, Icon, IconStack, Image, InfoBanner, Loader, theme as MainTheme, RoadieMapSystem as MapSystem, Messages, Modals as Modal, QuickBannerProvider, RadialButton, RawBanner, Spacer, Span, Text, Time, TimeMap, TimeRange, Tooltip, api, index as hooks, isColorIndicator, isShadedColorName, parsers, rule, session, useFormBaseError, useFormFieldError, useFormId, useFormLayout };
